// EditModal.js
import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import {
    TextField,
    Autocomplete,
    FormControl,
    Portal,
    Popper,
    Tooltip,
    IconButton,
    Button,
    FormControlLabel,
    Radio,
    Checkbox,
    Box,
    CircularProgress,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Draggable from 'react-draggable';
import SubAccountantService from '../../Services/subAccountant.service';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import he from 'date-fns/locale/he';
import arSA from 'date-fns/locale/ar-SA';
import enUS from 'date-fns/locale/en-US';
import CustomMonthDatePicker from '../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import { parseSmartDateInput } from '../../Utils/parseSmartDateInput';
import CustomDeleteConfirmationModal from '../CustomDeleteConfirmationModal/CustomDeleteConfirmationModal';
import DocumentTransferDialog from '../DocumentTransferDialog/DocumentTransferDialog';
import SaveIcon from '@mui/icons-material/SaveAsRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ClientService from '../../Services/client.service';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import ledger from '../../assests/images/Icons/ledger.svg';
import '../../pages/Document/Document.scss';
import './EditModal.css';
import AddAccountCardModal from '../Widgets/AddAccountCardModal/AddAccountCardModal';
import EnumsService from '../../Services/enums.service';
import AutocompleteCardSelect from '../Widgets/AutocompleteCardSelect/AutocompleteCardSelect'
import FixedSizeVirtualizedListbox from '../Widgets/AutocompleteCardSelect/FixedSizeVirtualizedListbox'
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter'
import journalFieldStyles from '../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss'
import RadioGroup from '@mui/material/RadioGroup';
import dayjs, { Dayjs } from 'dayjs';
import { styled } from '@mui/system';
import { calculateVatAmount, calculateGrossFromNet, getVatValue } from '../../Utils/GetVatValue';
import { ToastContainer, toast } from "react-toastify";
import Constants from '../../assests/Constants/constants';
import useCaseSystemAccountSettings from '../../hooks/useCaseSystemAccountSettings';
import { PermissionsContext } from '../../Contexts/Permissions';
import { beautifyNumber } from '../../Utils/FormatNumber';
import DocumentService from '../../Services/documents.service';
import AuthService from '../../Services/auth.service';
import ClarificationDocumentService from '../../Services/clarificationDocuments.service';
import JournalGroupService from '../../Services/journalGroup.service';
import LockIcon from '@mui/icons-material/Lock';
import Swal from 'sweetalert2';
import BulkEditModal from '../PrimaryBulkActionsToolbar/BulkEditModal';
import SplitDocumentModal from './SplitDocumentModal';
import PdfHighlightViewer from '../PdfHighlightViewer/PdfHighlightViewer';
import ForeignCurrencyService from '../../Services/foreignCurrency.service';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';
import { useDropzone } from 'react-dropzone';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ReceiptLongIcon from '@mui/icons-material/Loupe';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DuplicateJournalEntriesModal from './DuplicateJournalEntriesModal';
import RelatedPaymentAcceptancesModal from './RelatedPaymentAcceptancesModal';
import PaymentAcceptanceDuplicateModal from '../PaymentAcceptance/SubComponents/PaymentAcceptanceDuplicateModal';
import useAccountCardReport from '../../hooks/useAccountCardReport';
import AccountCardReportHTML from '../AccountCardDashboard/AccountCardReportHTML/AccountCardReportHTML';
import ProjectsService from '../../Services/projects.service';
import ClientExpenseAssignmentService from '../../Services/clientExpenseAssignment.service';
import ProjectModal from '../Projects/ProjectModal.jsx';
import { validateDocumentBeforeProcessing, confirmSortCodeDocTypeMismatch, showSortCodeDocTypeMismatchWarning } from '../../Utils/documentValidation';
import { getDescriptionCellClass, getMisfiledDocumentFlag } from '../../Utils/documentDescriptionFlags';
import { lookupSortCodeVatType } from '../../Utils/sortCodeDocTypeMismatch';
import { canProcessDocumentStatus } from '../../Utils/helpers';
import DocumentConversationSideTabs from '../DocumentsRequiringClarification/DocumentConversationSideTabs';
import useFieldPositions from '../DocumentBoxViewer/useFieldPositions';
import FieldBoxDot, { FieldBoxDotProvider } from '../DocumentBoxViewer/FieldBoxDot';
import { canonical, visibleFieldSet } from '../DocumentBoxViewer/fieldBoxes';
import ClarificationConversationPickerModal from '../DocumentsRequiringClarification/ClarificationConversationPickerModal';
import LinkIcon from '@mui/icons-material/Link';

function getAssignedProjectIdFromItem(item) {
    if (!item) return '';
    const p = item?.projects?.[0] ?? item?.segmentations?.[0];
    if (p) {
        const id = p.projectId ?? p.ProjectId ?? p.id ?? p.Id;
        if (id != null && id !== '') return String(id);
    }
    const segId =
        item.segmentationId ??
        item.SegmentationId ??
        item.segmentationProjectId ??
        item.projectId;
    if (segId != null && segId !== '') return String(segId);
    return '';
}

function normalizeSegmentationProjectRow(p) {
    const rawId = p?.id ?? p?.Id ?? p?.projectId ?? p?.ProjectId;
    const id = rawId != null && rawId !== '' ? String(rawId) : '';
    return {
        ...p,
        id,
        code: p?.code ?? p?.Code ?? '',
        name: p?.name ?? p?.Name ?? '',
        incomesIds: p?.incomesIds ?? p?.IncomesIds ?? [],
        expensesIds: p?.expensesIds ?? p?.ExpensesIds ?? [],
        journalEntryIds: p?.journalEntryIds ?? p?.JournalEntryIds ?? [],
        paymentAcceptanceIds: p?.paymentAcceptanceIds ?? p?.PaymentAcceptanceIds ?? [],
    };
}

function segmentationContainsDocument(seg, docType, documentId) {
    if (!seg || documentId == null || documentId === '') return false;
    const docId = String(documentId);
    const has = (arr) => Array.isArray(arr) && arr.some((x) => String(x) === docId);
    const d = docType || '';
    if (d === 'Incomes') return has(seg.incomesIds);
    if (d === 'Expenses') return has(seg.expensesIds);
    if (d === 'JournalEntries') return has(seg.journalEntryIds);
    if (d === 'PaymentAcceptances' || d.includes('PaymentAcceptance')) {
        return has(seg.paymentAcceptanceIds);
    }
    return false;
}

function findSegmentationProjectForItem(segmentationList, item, defaultDocType = '') {
    if (!item || !Array.isArray(segmentationList) || segmentationList.length === 0) {
        return null;
    }

    const assignedId = getAssignedProjectIdFromItem(item);
    if (assignedId) {
        const byId = segmentationList.find((p) => String(p.id) === String(assignedId));
        if (byId) return byId;
    }

    const savedCode =
        item.segmentationCode ??
        item.SegmentationCode ??
        item.projectCode ??
        item.segmentationProjectCode;
    if (savedCode != null && savedCode !== '') {
        const byCode = segmentationList.find((p) => String(p.code) === String(savedCode));
        if (byCode) return byCode;
    }

    const docType = item.docType || item.type || item.type1 || defaultDocType;
    if (item.id && docType) {
        return (
            segmentationList.find((seg) =>
                segmentationContainsDocument(seg, docType, item.id)
            ) ?? null
        );
    }

    return null;
}

function buildProjectFieldsFromSegmentation(seg) {
    const id = String(seg.id);
    return {
        segmentationId: id,
        segmentationCode: seg.code,
        segmentationName: seg.name,
        projects: [{ projectId: id, code: seg.code, name: seg.name }],
    };
}

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${journalFieldStyles.comboPopper} edit-modal-combo-popper`}
        placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600 }}
    />
));
ComboPopper.displayName = 'ComboPopper';

function AddAccountCardOptionContent({ label, searchTerm }) {
    const text = String(label || '').trim();
    return (
        <div className="edit-add-account-card">
            <span className="edit-add-account-card__plus" aria-hidden="true">+</span>
            <span className="edit-add-account-card__label">
                {text}
                {searchTerm ? ` "${searchTerm}"` : ''}
            </span>
        </div>
    );
}

const DragOverlayText = styled('div')({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '10px 20px',
    borderRadius: '5px',
    display: 'none',
});

const DropZoneContainer = styled('div')(({ isDragActive }) => ({
    width: '100%',
    textAlign: 'center',
    cursor: 'pointer',
    background: isDragActive ? '#e0e0e0' : 'transparent',
    transition: 'background 0.3s ease-in-out',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'relative',
}));
function EditModal(props) {
    const {
        editedItem,
        subAccountantOptions,
        handlePrevious,
        handleNext,
        currentIndex,
        filteredAndSortedData,
        NoDocumentsPdf,
        handleAccountChange,
        isRange,
        isClosedReportingMonth,
        accountCardOptions,
        handleChange,
        handleDateChange,
        setReportingMonthDate,
        handleVatRadioChange,
        statusCheck,
        handleRadioChange,
        type,
        editedAddAccountCard,
        handleCloseEditAddNewAccountCardDocument,
        handleEditAddAccountChange,
        sortCodeIdOptions,
        debitAccountOptions,
        creditAccountOptions,
        handleUpdate,
        handleCancel,
        handleDeleteClick,
        client,
        token,
        setAccountCardOptions,
        setLoading,
        setEditedAddAccountCard,
        setEditedItem,
        handleReopenEdit,
        palCodes,
        RefreshPage,
        showIframe = true,
        fetchEnums,
        assets = null,
        assetClicked,
        setItemToAddToAsset,
        accountCards,
        hideStatus = false,
        ViewMode = false,
        showForceProcess: showForceProcessOption = true
    } = props;
    const { settings: caseSystemSettings } = useCaseSystemAccountSettings(client?.caseId, token);
    const navigate = useNavigate();
    const location = useLocation();

    const { i18n, t } = useTranslation();

    const [forceProcess, setForceProcess] = useState(false);

    useEffect(() => {
        setForceProcess(false);
    }, [editedItem?.id]);

    const MANUAL_RATE_DECIMALS = 10;

    const parseNumeric = (v) => {
        if (v == null) return NaN;
        if (typeof v === 'number') return v;
        const s = String(v).replace(/,/g, '').trim();
        if (s === '') return NaN;
        return Number(s);
    };

    // A negative document (refund, reversing entry) converts at exactly the same rate as
    // a positive one, so every currency calculation below works on magnitudes. Without
    // this the `> 0` guards would skip the conversion entirely and leave the foreign
    // amount / rate empty. Both amounts carry the same sign in the UI (a negative shekel
    // amount shows a negative foreign amount) while the rate stays positive, and the
    // payload sends the foreign amount as a positive magnitude so the server's conversion
    // back to shekels keeps the right sign.
    const absNumeric = (v) => {
        const n = parseNumeric(v);
        return isNaN(n) ? NaN : Math.abs(n);
    };

    const signOf = (v) => (parseNumeric(v) < 0 ? -1 : 1);

    const toLocalDateKey = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const dayNum = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${dayNum}`;
    };

    // Build item to save. Foreign currency is always snapshotted as Manual (code + FC amount + rate)
    // so the server does not recalculate ILS via ConvertAsync on update.
    const getItemToSave = () => {
        const fromProjects = Array.isArray(editedItem.projects) ? editedItem.projects : [];
        const segIdRaw = editedItem.segmentationId ?? editedItem.SegmentationId;
        const projectList =
            fromProjects.length > 0
                ? fromProjects
                : showProjectAssignRow && segIdRaw != null && segIdRaw !== ''
                  ? [
                        {
                            projectId: String(segIdRaw),
                            code: editedItem.segmentationCode ?? editedItem.SegmentationCode ?? '',
                            name: editedItem.segmentationName ?? editedItem.SegmentationName ?? '',
                        },
                    ]
                  : [];
        const firstProject = projectList[0];
        const rawPid =
            firstProject?.projectId ??
            firstProject?.ProjectId ??
            firstProject?.id ??
            firstProject?.Id;
        const projectPayload =
            showProjectAssignRow
                ? {
                      syncProject: true,
                      projectId: rawPid != null && rawPid !== '' ? String(rawPid) : null,
                      projects: projectList,
                  }
                : {};

        const isManual = editedItem.exchangeRateType === Constants.ExchangeRateType.Manual;
        const manualRateNum = parseNumeric(manualRateInput);
        const hasValidManualRate = manualRateInput !== '' && !isNaN(manualRateNum) && manualRateNum > 0;
        const amountNum = parseNumeric(editedItem.amount);
        const fcNum = parseNumeric(editedItem.foreignCurrencyAmount);
        const amountAbs = absNumeric(editedItem.amount);
        const fcAbs = absNumeric(editedItem.foreignCurrencyAmount);
        const hasForeignCode = editedItem.foreignCurrencyCode != null && editedItem.foreignCurrencyCode !== '';
        const hasValidAmountAndFc = hasForeignCode && amountAbs > 0 && fcAbs > 0 && !isNaN(amountAbs) && !isNaN(fcAbs);
        const calculatedFromAmounts = hasValidAmountAndFc
            ? (amountAbs / fcAbs).toFixed(MANUAL_RATE_DECIMALS)
            : null;
        const fcRateNum = parseNumeric(FCRate);
        const hasValidFcRate = !isNaN(fcRateNum) && fcRateNum > 0;

        // When there is no foreign currency selected, exchangeRateType / exchangeRate /
        // foreignCurrencyAmount are meaningless. They can otherwise stick around as stale
        // values from a previous edit session (e.g. user toggled to Manual then cleared the
        // currency dropdown without saving), and the server would persist a phantom rate
        // type against an ILS-only document. Force them to null so the payload matches the
        // actual state of the document.
        if (!hasForeignCode) {
            return {
                ...editedItem,
                exchangeRateType: null,
                exchangeRate: null,
                foreignCurrencyAmount: null,
                ...projectPayload,
            };
        }

        let resolvedRate = null;
        if (hasValidManualRate) {
            resolvedRate = manualRateInput;
        } else if (isManual && calculatedFromAmounts) {
            resolvedRate = calculatedFromAmounts;
        } else if (hasValidFcRate) {
            resolvedRate = String(fcRateNum);
        } else if (
            editedItem.exchangeRate !== null &&
            editedItem.exchangeRate !== undefined &&
            editedItem.exchangeRate !== ''
        ) {
            resolvedRate = String(editedItem.exchangeRate);
        } else if (calculatedFromAmounts) {
            resolvedRate = calculatedFromAmounts;
        }

        const rateForCalc = parseNumeric(resolvedRate);
        let resolvedFc = editedItem.foreignCurrencyAmount;
        if ((resolvedFc == null || resolvedFc === '') && amountAbs > 0 && rateForCalc > 0) {
            resolvedFc = (amountAbs / rateForCalc).toFixed(2);
        } else if (resolvedFc != null && resolvedFc !== '' && !isNaN(fcAbs)) {
            // Always hand the API a positive foreign amount: the server converts it back to
            // shekels, so a negative value there would flip the sign of the stored amount.
            resolvedFc = fcAbs.toFixed(2);
        }

        let resolvedAmount = editedItem.amount;
        if ((resolvedAmount == null || resolvedAmount === '') && fcAbs > 0 && rateForCalc > 0) {
            resolvedAmount = Number((fcAbs * rateForCalc).toFixed(2));
        } else if (!isNaN(amountNum)) {
            resolvedAmount = Number(amountNum.toFixed(2));
        }

        return {
            ...editedItem,
            foreignCurrencyCode: editedItem.foreignCurrencyCode,
            foreignCurrencyAmount: resolvedFc,
            exchangeRate: resolvedRate,
            exchangeRateType: Constants.ExchangeRateType.Manual,
            amount: resolvedAmount,
            ...projectPayload,
            ...(forceProcess ? { forceProcess: true } : {}),
        };
    };

    // Handle save with inventory check for Expenses or JournalEntries with sortCodeId 200
    const handleSaveWithInventoryCheck = async () => {
        const itemToSave = getItemToSave();
        // Basic / hideStatus clients should not mark documents processed; drop status so parents skip those APIs.
        const savePayload = canProcessStatus
            ? itemToSave
            : (({ status, forceProcess, ...rest }) => rest)(itemToSave);
        const sortCodeVatTypeForSave = lookupSortCodeVatType(
            itemToSave.sortCodeId,
            sortCodeIdOptions,
            itemToSave
        );
        const sortCodeMatchesDocType = await confirmSortCodeDocTypeMismatch(
            itemToSave.docType || itemToSave.documentType || (type === 'AccountCardDashboard' ? undefined : type),
            sortCodeVatTypeForSave,
            t
        );
        if (!sortCodeMatchesDocType) return;

        // Persist the isolated "שיוך הוצאות" AccountCard assignment (Expenses only, only if changed).
        await persistAssignedAccountCardIfNeeded();

        // Check if it's Expenses or JournalEntries type and sortCodeId is 200
        const isExpensesWithInventory = (type === 'Expenses' || type === 'JournalEntries') && 
            (itemToSave.sortCodeId === '200' || itemToSave.sortCodeId === 200);

        if (isExpensesWithInventory && client?.managesInventory) {
            const result = await Swal.fire({
                title: t('UpdateInventoryQuestion') || 'Do you want to update the inventory?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: t('Yes') || 'Yes',
                cancelButtonText: t('No') || 'No',
            });

            if (result.isConfirmed) {
                // First save the document
                await handleUpdate(savePayload);
                
                // Clean state helper (similar to useClientNavigation)
                const cleanState = (st) => {
                    if (!st) return null;
                    const { previousLocation, ...rest } = st;
                    return rest;
                };
                
                // Then navigate to InventoryManagement with document data (following useClientNavigation pattern)
                navigate('/InventoryManagement', {
                    state: {
                        type: 'InventoryManagement',
                        client,
                        breadcrumbs: ['Sales', 'InventoryManagement'],
                        documentData: {
                            ...itemToSave,
                            docType: type, // Ensure we have the type for later update
                            documentUrl: itemToSave.documentUrl,
                        },
                        showDocumentTypeModal: true,
                        previousLocation: {
                            pathname: location.pathname,
                            state: cleanState(location.state),
                        },
                    }
                });
                return;
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                // User cancelled, just save normally
                await handleUpdate(savePayload);
                return;
            } else {
                // User closed the dialog, abort save
                return;
            }
        }

        // Normal save flow (include current manual rate when in Manual mode)
        await handleUpdate(savePayload);
    };
    // const direction = i18n.dir();
    const [container] = useState(() => document.createElement('div'));
    const nodeRef = useRef(null);
    const formPaneRef = useRef(null);
    const EDIT_MODAL_SIZE_KEY = 'editModalSizeMode';
    const EDIT_MODAL_FREE_SIZE_KEY = 'editModalFreeSize';
    const [modalSizeMode, setModalSizeMode] = useState(() => {
        try {
            const saved = localStorage.getItem(EDIT_MODAL_SIZE_KEY);
            if (saved === 'compact' || saved === 'medium' || saved === 'expanded' || saved === 'free') return saved;
        } catch (_) { /* ignore */ }
        return 'expanded';
    });
    const [sizeBeforeExpand, setSizeBeforeExpand] = useState('medium');
    const [freeSize, setFreeSize] = useState(() => {
        try {
            const raw = localStorage.getItem(EDIT_MODAL_FREE_SIZE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.width && parsed?.height) return parsed;
            }
        } catch (_) { /* ignore */ }
        return {
            width: Math.round(window.innerWidth * 0.9),
            height: Math.round(window.innerHeight * 0.9),
            top: Math.round(window.innerHeight * 0.05),
            left: Math.round(window.innerWidth * 0.05),
        };
    });
    const [fitDensity, setFitDensity] = useState(0);
    const resizeStateRef = useRef(null);

    const persistFreeSize = useCallback((next) => {
        setFreeSize(next);
        try {
            localStorage.setItem(EDIT_MODAL_FREE_SIZE_KEY, JSON.stringify(next));
        } catch (_) { /* ignore */ }
    }, []);

    const captureCurrentAsFreeSize = useCallback(() => {
        const el = nodeRef.current;
        if (!el) return freeSize;
        const rect = el.getBoundingClientRect();
        return {
            width: Math.round(rect.width),
            height: Math.round(rect.height),
            top: Math.round(rect.top),
            left: Math.round(rect.left),
        };
    }, [freeSize]);

    const applyModalSizeMode = useCallback((mode) => {
        if (mode === 'free') {
            const next = captureCurrentAsFreeSize();
            persistFreeSize(next);
            if (nodeRef.current) nodeRef.current.style.transform = '';
            setModalSizeMode('free');
            try {
                localStorage.setItem(EDIT_MODAL_SIZE_KEY, 'free');
            } catch (_) { /* ignore */ }
            return;
        }
        setModalSizeMode(mode);
        try {
            localStorage.setItem(EDIT_MODAL_SIZE_KEY, mode);
        } catch (_) { /* ignore */ }
        if (nodeRef.current) {
            nodeRef.current.style.transform = '';
        }
    }, [captureCurrentAsFreeSize, persistFreeSize]);

    const handleToggleExpandCollapse = useCallback(() => {
        if (modalSizeMode === 'expanded') {
            const restore = sizeBeforeExpand === 'expanded' ? 'medium' : sizeBeforeExpand;
            applyModalSizeMode(restore);
        } else {
            setSizeBeforeExpand(modalSizeMode === 'free' ? 'free' : modalSizeMode);
            applyModalSizeMode('expanded');
        }
    }, [modalSizeMode, sizeBeforeExpand, applyModalSizeMode]);

    const beginResize = useCallback((edge) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        const current = modalSizeMode === 'free' ? freeSize : captureCurrentAsFreeSize();
        if (modalSizeMode !== 'free') {
            persistFreeSize(current);
            setModalSizeMode('free');
            try {
                localStorage.setItem(EDIT_MODAL_SIZE_KEY, 'free');
            } catch (_) { /* ignore */ }
            if (nodeRef.current) nodeRef.current.style.transform = '';
        }
        resizeStateRef.current = {
            edge,
            startX: event.clientX,
            startY: event.clientY,
            start: current,
        };

        const onMove = (ev) => {
            const st = resizeStateRef.current;
            if (!st) return;
            const dx = ev.clientX - st.startX;
            const dy = ev.clientY - st.startY;
            let { width, height, top, left } = st.start;
            if (st.edge.includes('e')) width = st.start.width + dx;
            if (st.edge.includes('s')) height = st.start.height + dy;
            if (st.edge.includes('w')) {
                width = st.start.width - dx;
                left = st.start.left + dx;
            }
            const minW = 560;
            const minH = 420;
            const maxW = window.innerWidth - 8;
            const maxH = window.innerHeight - 8;
            width = Math.max(minW, Math.min(maxW, width));
            height = Math.max(minH, Math.min(maxH, height));
            left = Math.max(0, Math.min(window.innerWidth - minW, left));
            top = Math.max(0, Math.min(window.innerHeight - minH, top));
            if (st.edge.includes('w') && width === minW) {
                left = st.start.left + (st.start.width - minW);
            }
            setFreeSize({ width: Math.round(width), height: Math.round(height), top: Math.round(top), left: Math.round(left) });
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            const latest = resizeStateRef.current;
            resizeStateRef.current = null;
            if (!latest) return;
            // Persist from DOM so we store the committed size after last setState
            const rect = nodeRef.current?.getBoundingClientRect();
            if (rect) {
                persistFreeSize({
                    width: Math.round(rect.width),
                    height: Math.round(rect.height),
                    top: Math.round(rect.top),
                    left: Math.round(rect.left),
                });
            }
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [modalSizeMode, freeSize, captureCurrentAsFreeSize, persistFreeSize]);

    const [showPdfIframe, setShowPdfIframe] = useState(true);

    // Dynamically densify form fields so everything fits without scrolling
    useEffect(() => {
        const shell = nodeRef.current;
        const formPane = formPaneRef.current;
        if (!shell || !formPane) return undefined;

        let raf = 0;
        const measure = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const scrollArea = formPane.querySelector('.edit-modal-form-scroll') || formPane;
                const form = formPane.querySelector('.form-container');
                if (!scrollArea || !form) return;
                // Reset then escalate density until content fits
                shell.classList.remove('fit-d1', 'fit-d2', 'fit-d3');
                let dens = 0;
                const fits = () => form.scrollHeight <= scrollArea.clientHeight + 4;
                if (!fits()) {
                    dens = 1;
                    shell.classList.add('fit-d1');
                }
                if (!fits()) {
                    dens = 2;
                    shell.classList.add('fit-d2');
                }
                if (!fits()) {
                    dens = 3;
                    shell.classList.add('fit-d3');
                }
                setFitDensity(dens);
            });
        };

        const ro = new ResizeObserver(measure);
        ro.observe(shell);
        ro.observe(formPane);
        measure();
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [modalSizeMode, freeSize, showPdfIframe, editedItem?.id]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [creditDebitSearchTerm, setCreditDebitSearchTerm] = useState('');
    const [selectedAccountCard, setSelectedAccountCard] = useState(null);
    const [newAccountCard, setNewAccountCard] = useState({ accountCodeName: '', accountCodeId: "" });
    const [isEditAccountCard, setIsEditAccountCard] = useState(false);
    const [direction, setDirection] = useState('rtl');
    const { userTypeContext, permissionsState } = useContext(PermissionsContext);
    const isClientUser = userTypeContext?.toString() === Constants.UserType.Client;
    const canProcessStatus = canProcessDocumentStatus(userTypeContext, permissionsState, hideStatus);
    const notify = {
        success: (message, options) => toast.success(message, { position: 'top-left', ...options }),
        error: (message, options) => toast.error(message, { position: 'top-left', ...options }),
        info: (message, options) => toast.info(message, { position: 'top-left', ...options }),
        warning: (message, options) => toast.warning(message, { position: 'top-left', ...options }),
    };

    // Chat functionality
    const { handleSendMessageToChat, selectUserAndOpenChat } = useHamburgerMenu();

    const [currentVat, setCurrentVat] = useState(0);
    const [beforeVatAmount, SetBeforeVatAmount] = useState(0);
    const [netAmountInput, setNetAmountInput] = useState('');
    const lastAmountEditRef = useRef('gross');
    const netFocusedRef = useRef(false);
    // VAT the user picked in this document. Sort-code defaults (including 0%) must not replace it.
    const vatOverrideRef = useRef(null);
    const netAmountInputRef = useRef('');
    const [currencyWarn, setCurrencyWarn] = useState(false);
    const currentSortCode = useMemo(
        () => sortCodeIdOptions?.find((option) => option.sortCodeId === editedItem.sortCodeId) ?? null,
        [sortCodeIdOptions, editedItem.sortCodeId]
    );
    const currentSortCodeVatLabel = useMemo(
        () => (currentSortCode && Constants.SortCodeVatType ? Constants.SortCodeVatType[currentSortCode.sortCodeVatType] : null),
        [currentSortCode]
    );
    const isJournalEntryDocument = editedItem.docType === 'JournalEntries';
    const docTypeForProject = editedItem?.docType || type;
    const showProjectAssignRow =
        docTypeForProject === 'JournalEntries' ||
        docTypeForProject === 'Expenses' ||
        docTypeForProject === 'Incomes';
    const assignedProjectIdStr = useMemo(
        () => getAssignedProjectIdFromItem(editedItem),
        [editedItem]
    );
    const [segmentationProjects, setSegmentationProjects] = useState([]);
    const [projectModalOpen, setProjectModalOpen] = useState(false);
    const [projectCreateLoading, setProjectCreateLoading] = useState(false);
    const [projectAssignLoading, setProjectAssignLoading] = useState(false);

    // "שיוך הוצאות" — isolated AccountCard assignment for Expenses only (persists on Save).
    const [selectedAssignedAccountCardId, setSelectedAssignedAccountCardId] = useState(null);
    const initialAssignedAccountCardIdRef = useRef(null);
    const assignedAccountCardSelectOptions = useMemo(
        () =>
            (accountCardOptions || [])
                .filter(
                    (o) => o && o.cardId !== undefined && o.cardId !== null && o.cardId !== ''
                )
                .map((o) => ({
                    value: o.cardId,
                    label: o.accountCaseId ? `${o.name} - ${o.accountCaseId}` : `${o.name}`,
                    name: o.name,
                    accountCaseId: o.accountCaseId || '',
                    cardId: o.cardId,
                })),
        [accountCardOptions]
    );
    const selectedAssignedAccountCardOption = useMemo(
        () =>
            selectedAssignedAccountCardId
                ? assignedAccountCardSelectOptions.find(
                      (opt) => String(opt.value) === String(selectedAssignedAccountCardId)
                  ) || null
                : null,
        [assignedAccountCardSelectOptions, selectedAssignedAccountCardId]
    );
    const projectAutocompleteOptions = useMemo(
        () => [
            { id: '__ADD_NEW__', label: t('AddProject'), kind: 'add' },
            ...segmentationProjects.map((p) => ({
                id: String(p.id ?? p.projectId ?? ''),
                label: `${p.code} — ${p.name}`,
                kind: 'project',
            })),
        ],
        [segmentationProjects, t]
    );
    const selectedProjectAutocompleteValue = useMemo(() => {
        if (!assignedProjectIdStr) {
            return null;
        }
        const matchedOption = projectAutocompleteOptions.find(
            (o) => o.kind === 'project' && String(o.id) === String(assignedProjectIdStr)
        );
        if (matchedOption) return matchedOption;

        const code = editedItem.segmentationCode ?? editedItem.SegmentationCode ?? '';
        const name = editedItem.segmentationName ?? editedItem.SegmentationName ?? '';
        const label =
            `${code} — ${name}`.replace(/^ — | — $/g, '').trim() || String(assignedProjectIdStr);

        return {
            id: String(assignedProjectIdStr),
            label,
            kind: 'project',
        };
    }, [
        assignedProjectIdStr,
        projectAutocompleteOptions,
        editedItem.segmentationCode,
        editedItem.SegmentationCode,
        editedItem.segmentationName,
        editedItem.SegmentationName,
    ]);
    const editedItemRef = useRef(editedItem);
    editedItemRef.current = editedItem;

    const journalSortCodeIsIncome = isJournalEntryDocument && currentSortCodeVatLabel === 'VatTransactions';
    const journalSortCodeIsExpense = isJournalEntryDocument && currentSortCodeVatLabel === 'VatExpenses';
    const [reportingMonthDatePickerKey, setReportingMonthDatePickerKey] = useState(0);
    const {
        isMonthly,
        startDate,
        endDate,
        startYearDate,
        endYearDate,
    } = useSelector((state) => state.reportPeriodSlice);

    const {
        promptReport, fetchReport, closeReport,
        reportOpen, reportLoading, reportProps,
        datePickerOpen, closeDatePicker,
        pickerStartDate, setPickerStartDate,
        pickerEndDate, setPickerEndDate,
        pendingCardName,
    } = useAccountCardReport({
        client, token, accountCardOptions, isMonthly
    });

    const [FCRate, setFCRate] = useState(1);
    const [isLoadingCurrencyRate, setIsLoadingCurrencyRate] = useState(false);

    // Normalize exchangeRateType on the loaded document so all `=== Manual` comparisons
    // throughout the modal (toggle highlight, manual-rate TextField visibility, fetchRate
    // gating, etc.) behave consistently regardless of how the API serialized the field.
    // Handles two real-world cases:
    //  1) API returns "1" (string) instead of 1 (number) - common with .NET enum serializers
    //  2) Document was saved with a manual rate but exchangeRateType came back null/missing
    //     -> infer Manual from the presence of exchangeRate so the modal still treats it
    //        as Manual and does not fetch the representative rate over the saved value.
    useEffect(() => {
        const rt = editedItem.exchangeRateType;
        const hasSavedRate =
            editedItem.exchangeRate !== null &&
            editedItem.exchangeRate !== undefined &&
            editedItem.exchangeRate !== '';

        if (rt != null && rt !== '' && typeof rt !== 'number') {
            const num = Number(rt);
            if (!Number.isNaN(num)) {
                setEditedItem((prev) => ({ ...prev, exchangeRateType: num }));
                return;
            }
        }

        if ((rt == null || rt === '') && hasSavedRate) {
            setEditedItem((prev) => ({
                ...prev,
                exchangeRateType: Constants.ExchangeRateType.Manual,
            }));
        }
    }, [editedItem?.id, editedItem.exchangeRateType, editedItem.exchangeRate]);

    // The API stores the foreign amount as a positive magnitude, so on a negative document
    // it comes back with the opposite sign of the shekel amount. Align it once per opened
    // document (not on every keystroke) so the two fields always read the same way.
    useEffect(() => {
        const amountNum = parseNumeric(editedItem.amount);
        const fcNum = parseNumeric(editedItem.foreignCurrencyAmount);
        if (isNaN(amountNum) || isNaN(fcNum) || fcNum === 0) return;
        if (Math.sign(amountNum) === Math.sign(fcNum)) return;
        setEditedItem((prev) => ({
            ...prev,
            foreignCurrencyAmount: (signOf(prev.amount) * Math.abs(fcNum)).toFixed(2),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedItem?.id]);

    useEffect(() => {
        async function fetchRate() {
            setIsLoadingCurrencyRate(true);
            const documentDate = editedItem.documentDate;
            const docDateKey = documentDate ? toLocalDateKey(documentDate) : '';
            try {
                const { rate, usedDate } = await ForeignCurrencyService.GetIlsRateForDateWithFallback(
                    editedItem.foreignCurrencyCode,
                    documentDate,
                    token
                );
                setFCRate(rate);
                const usedFallback = usedDate !== docDateKey;

                if (usedFallback) {
                    const rateStr = String(rate);
                    setManualRateInput(rateStr);
                    lastManualRateRef.current = rateStr;
                    setEditedItem((prev) => {
                        const amountRaw = prev.amount;
                        const patch = {
                            exchangeRateType: Constants.ExchangeRateType.Manual,
                            exchangeRate: rateStr,
                        };
                        if (
                            rate > 0 &&
                            amountRaw !== null &&
                            amountRaw !== '' &&
                            amountRaw !== undefined
                        ) {
                            const amountNum = parseNumeric(amountRaw);
                            if (!isNaN(amountNum)) {
                                patch.amount = Number(amountNum.toFixed(2));
                                patch.foreignCurrencyAmount = (amountNum / rate).toFixed(2);
                            }
                        }
                        return { ...prev, ...patch };
                    });
                    notify.info(t('CurrencyRateUsedFallbackDate', { date: usedDate }));
                } else {
                    const amountRaw = editedItem.amount;
                    if (
                        rate != null &&
                        rate > 0 &&
                        amountRaw !== null &&
                        amountRaw !== '' &&
                        amountRaw !== undefined
                    ) {
                        const amountNum = parseNumeric(amountRaw);
                        if (!isNaN(amountNum)) {
                            const amountFixed = Number(amountNum.toFixed(2));
                            const foreignAmount = (amountNum / rate).toFixed(2);
                            setEditedItem((prev) => ({
                                ...prev,
                                amount: amountFixed,
                                foreignCurrencyAmount: foreignAmount,
                            }));
                        }
                    }
                }
            } catch (err) {
                console.error(err);
                notify.error(t('CurrencyRateFetchError'));
                setEditedItem((prev) => ({
                    ...prev,
                    exchangeRateType: Constants.ExchangeRateType.Manual,
                    exchangeRate: null,
                }));
                setManualRateInput('');
                userClearedManualRateRef.current = false;
            } finally {
                setIsLoadingCurrencyRate(false);
            }
        }

        // Manual-rate detection must be defensive: the persisted document might come back
        // from the API with exchangeRateType as a number, a numeric string ("1"), or even
        // missing entirely while still carrying a saved exchangeRate. Only infer Manual
        // when exchangeRateType is null/missing — not merely because exchangeRate is set.
        const rawRateType = editedItem.exchangeRateType;
        const hasSavedManualRate =
            editedItem.exchangeRate !== null &&
            editedItem.exchangeRate !== undefined &&
            editedItem.exchangeRate !== '';
        const isManualMode =
            rawRateType === Constants.ExchangeRateType.Manual ||
            Number(rawRateType) === Constants.ExchangeRateType.Manual ||
            ((rawRateType == null || rawRateType === '') && hasSavedManualRate);

        if (
            editedItem.foreignCurrencyCode != null &&
            editedItem.foreignCurrencyCode !== undefined &&
            editedItem.foreignCurrencyCode !== '' &&
            editedItem.documentDate &&
            !isManualMode
        ) {
            fetchRate();
        } else {
            if (editedItem.foreignCurrencyCode === '' || editedItem.foreignCurrencyCode == null) {
                setFCRate(1);
            }
            setIsLoadingCurrencyRate(false);
        }
    }, [editedItem.foreignCurrencyCode, editedItem.documentDate, editedItem.exchangeRateType, editedItem.exchangeRate])

    useEffect(() => {
        // Update direction based on the current language
        if (i18n.language === 'he' || i18n.language === 'ar') {
            setDirection('rtl');
        } else {
            setDirection('ltr');
        }
    }, [i18n.language]);


    const [asset, setAsset] = useState(null);

    const handleNavigateToDashboard = useCallback(
        (cardId) => () => promptReport(cardId, editedItem?.reportingMonthDate),
        [promptReport, editedItem?.reportingMonthDate]
    );
    const handleSelectSortCode = useCallback(
        (selectedCodeId) => () => {
            // bail if no value
            if (!selectedCodeId) return;

            // 👇 pick up the object you need for the report name
            const option = sortCodeIdOptions.find(
                (o) => o.sortCodeId === selectedCodeId
            );
            if (!option) return;

            const sortCodeName = option.sortCodeName ?? '';
            let groupName = '';

            const buildSortCodeReportDateState = () => {
                if (isMonthly) {
                    const endSource = endDate ? new Date(endDate) : new Date();
                    const year = startDate
                        ? new Date(startDate).getUTCFullYear()
                        : endSource.getUTCFullYear();

                    const normalizedStart = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
                    const normalizedEnd = new Date(Date.UTC(
                        endSource.getUTCFullYear(),
                        endSource.getUTCMonth() + 1,
                        0,
                        23, 59, 59, 999
                    ));
                    const yearEndDateValue = new Date(Date.UTC(endSource.getUTCFullYear(), 11, 31, 0, 0, 0, 0));

                    return {
                        startDate: normalizedStart,
                        endDate: normalizedEnd,
                        isMonthly: true,
                        startYearDate: normalizedStart,
                        endYearDate: yearEndDateValue,
                    };
                }

                const startYear = startYearDate
                    ? new Date(startYearDate).getUTCFullYear()
                    : startDate
                        ? new Date(startDate).getUTCFullYear()
                        : new Date().getUTCFullYear();
                const endYear = endYearDate
                    ? new Date(endYearDate).getUTCFullYear()
                    : endDate
                        ? new Date(endDate).getUTCFullYear()
                        : startYear;

                const yearStart = new Date(Date.UTC(startYear, 0, 1, 0, 0, 0, 0));
                const yearEnd = new Date(Date.UTC(endYear, 11, 31, 23, 59, 59, 999));
                const yearEndDateValue = new Date(Date.UTC(endYear, 11, 31, 0, 0, 0, 0));

                return {
                    startDate: yearStart,
                    endDate: yearEnd,
                    isMonthly: false,
                    startYearDate: yearStart,
                    endYearDate: yearEndDateValue,
                };
            };

            const dateState = buildSortCodeReportDateState();

            /* 3️⃣ Navigate */
            navigate('/SortCodeReport', {
                state: {
                    type,
                    client,
                    groupName,
                    sortCodeName,
                    sortCodeId: selectedCodeId,
                    breadcrumbs: ['Reports', 'ProfitAndLossReport'],
                    ...dateState,
                },
            });
        },
        [
            client,
            endDate,
            isMonthly,
            navigate,
            sortCodeIdOptions,
            startDate,
            startYearDate,
            endYearDate,
            type,
        ]
    );

    useEffect(() => {

        if (assets) {

            const assetFound = assets.find(
                (asset) => asset.expenseId === editedItem.id || asset.journalEntryId === editedItem.id
                    || asset.saleJournalEntryId === editedItem.id || asset.saleIncomeId === editedItem.id
            );

            setAsset(assetFound);

        }
    }, [assets]);
    const syncJournalEntryAccountCaseId = useCallback(() => {
        if (!isJournalEntryDocument || !editedItem.sortCodeId) {
            return;
        }

        const sortCode = sortCodeIdOptions?.find((option) => option.sortCodeId === editedItem.sortCodeId);
        if (!sortCode) {
            return;
        }

        const sortCodeVatTypeLabel = Constants.SortCodeVatType?.[sortCode.sortCodeVatType];
        let targetAccountId = null;
        if (sortCodeVatTypeLabel === 'VatTransactions') {
            targetAccountId = editedItem.debitAccount;
        } else if (sortCodeVatTypeLabel === 'VatExpenses') {
            targetAccountId = editedItem.creditAccount;
        } else {
            return;
        }

        if (!targetAccountId) {
            return;
        }

        const targetAccount = accountCardOptions?.find((option) => option.cardId === targetAccountId);
        if (!targetAccount) {
            return;
        }

        const derivedAccountCaseId = targetAccount.accountCaseId ?? '';
        if (!derivedAccountCaseId || derivedAccountCaseId === editedItem.accountCaseId) {
            return;
        }

        handleChange('accountCaseId', derivedAccountCaseId);
    }, [
        accountCardOptions,
        editedItem.accountCaseId,
        editedItem.creditAccount,
        editedItem.debitAccount,
        editedItem.sortCodeId,
        handleChange,
        isJournalEntryDocument,
        sortCodeIdOptions
    ]);
    const [subAccountants, setSubAccountants] = useState([]);
    const [subAccountantNewOptions, setSubAccountantNewOptions] = useState([]);
    const [accountant, setAccountant] = useState({});
    const [currencyError, setCurrencyError] = useState(false);
    const [dateError, setDateError] = useState(false);

    // For tracking validation errors
    const [hasSameAccountError, setHasSameAccountError] = useState(false);

    // For file upload functionality
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    const fetchSubAccountants = async () => {
        if (!client?.accountantId) {
            return;
        }
        const result2 = await SubAccountantService.GetSubAccountantByAccountantId(token, client?.accountantId);
        const accountantResult = await SubAccountantService.GetAccountantById(token, client?.accountantId);
        const enhancedAccountantResult = {
            ...accountantResult,
            subAccountantId: accountantResult.accountantId, // Add subAccountantId as same as accountantId
        };
        setAccountant(enhancedAccountantResult);

        if (result2) {
            const combinedSubAccountants = [enhancedAccountantResult, ...result2];

            // If client exists, add it to the combined list
            if (client) {
                combinedSubAccountants.push({
                    subAccountantId: client.caseId,
                    name: client.name,
                });
            }

            setSubAccountants(combinedSubAccountants);

            const combinedOptions = combinedSubAccountants.map((acc) => ({
                value: acc.subAccountantId,
                label: acc.name,
            }));

            setSubAccountantNewOptions(combinedOptions);
        } else {
            notify.error(t('fetchSubAccountantsDataError'));
        }
    }


    useEffect(() => {
        // Update direction based on the current language
        if (!subAccountantOptions) {
            fetchSubAccountants();

        } else {
            setSubAccountantNewOptions(subAccountantOptions);
        }
    }, [subAccountantOptions]);

    const handleStatusRadioChange = useCallback(async (e, options = {}) => {
        if (!canProcessStatus) return;
        const value = e.target.value?.toString();
        if (value === 'done') {
            const isValid = await validateDocumentBeforeProcessing(
                editedItem,
                t,
                client,
                options.forceProcess === true,
                sortCodeIdOptions
            );
            if (!isValid) return;
        }
        await handleRadioChange(e, { ...options, validationAlreadyDone: true });
    }, [canProcessStatus, editedItem, t, client, handleRadioChange, sortCodeIdOptions]);

    const [selectedAccount, setSelectedAccount] = useState(() => {
        if (!editedItem?.accountCodeName) return '';

        const selectedOption = accountCardOptions.find(option => option.cardId === editedItem.accountCodeId);
        return selectedOption ? selectedOption : null;
    });

    const [oldSelectedAccountCardItem, setOldSelectedAccountCardItem] = useState({
        cardId: "",
        caseId: "",
        accountCaseId: "",
        name: "",
        palCode: '',
        client: null
    });
    const [oldCreditDebitSelectedAccountCardItem, setOldCreditDebitSelectedAccountCardItem] = useState({
        creditAccount: "",
        debitAccount: "",
    });
    const [showAccountCardInputsEdit, setShowAccountCardInputsEdit] = useState(false);
    const [isAddAccountCard, setIsAddAccountCard] = useState(false);
    const [accountCardIdToDelete, setAccountCardIdToDelete] = useState('');
    const [palCode, setPalCode] = useState('');
    const [newCardId, setNewCardId] = useState(null);
    // Disabled auto-sync for journal entries to allow free editing
    // useEffect(() => {
    //     syncJournalEntryAccountCaseId();
    // }, [syncJournalEntryAccountCaseId]);
    const handleChangeAfterAdd = useCallback((cardId, type) => {
        setNewCardId({ cardId, type });
    }, []);
    // Set by SplitDocumentModal so a card created from one of its rows lands back in that row.
    const applySplitAccountCardRef = useRef(null);
    const [index, setIndex] = useState(currentIndex);
    const [item, setItem] = useState({ ...editedItem });
    const [addAccountCardType, setAddAccountCardType] = useState('main');
    const [dupOpen, setDupOpen] = useState(false);          // ⟨⟨ duplicate
    const openDuplicate = () => setDupOpen(true);           // ⟨⟨ duplicate
    const closeDuplicate = () => setDupOpen(false);          // ⟨⟨ duplicate
    const [splitOpen, setSplitOpen] = useState(false);
    const [dupPaymentOpen, setDupPaymentOpen] = useState(false);  // ⟨⟨ duplicate payment
    const openDuplicatePayment = () => setDupPaymentOpen(true);   // ⟨⟨ duplicate payment
    const closeDuplicatePayment = () => setDupPaymentOpen(false); // ⟨⟨ duplicate payment
    const effectiveDocType =
        editedItem?.docType || (type === 'AccountCardDashboard' ? undefined : type);
    const canSplitDocument = effectiveDocType === 'Expenses' || effectiveDocType === 'Incomes';
    const canCreateFromDocument = canSplitDocument || effectiveDocType === 'JournalEntries';

    // Incomes / expenses / journal entries keep VAT editable when locked.
    const isVatDocument = ['Incomes', 'Expenses', 'JournalEntries'].includes(effectiveDocType);

    /* ── OCR field boxes on the document preview ── */
    const fieldBoxEntityType = effectiveDocType === 'Expenses' ? 'Expense' : effectiveDocType === 'Incomes' ? 'Income' : null;
    const fieldPositions = useFieldPositions({
        caseId: client?.caseId,
        entityType: fieldBoxEntityType,
        documentId: editedItem?.id,
        token,
        enabled: showIframe && showPdfIframe,
        refreshKey: editedItem?.documentUrl?.split('?')[0],
    });
    const [activeOcrField, setActiveOcrField] = useState(null);
    useEffect(() => setActiveOcrField(null), [editedItem?.id]);
    const [fieldBoxesShown, setFieldBoxesShown] = useState(false);

    // Only values the item actually carries take part; a box disappears as soon as its field is edited.
    const fieldBoxValues = useMemo(() => {
        if (!editedItem) return {};
        const values = {};
        const add = (field, raw, toCanonical) => {
            if (raw !== undefined) values[field] = toCanonical(raw);
        };
        add('reference', editedItem.reference, canonical.text);
        add('confirmationNumber', editedItem.confirmationNumber, canonical.digits);
        add('amount', editedItem.amount, canonical.number);
        add('documentDate', editedItem.documentDate, canonical.date);
        add('accountCodeName', editedItem.accountCodeName, canonical.text);
        add('accountCaseId', editedItem.accountCaseId, canonical.digits);
        add('description', editedItem.description, canonical.text);
        return values;
    }, [editedItem]);
    const visibleFieldBoxes = useMemo(
        () => visibleFieldSet(fieldPositions, fieldBoxValues),
        [fieldPositions, fieldBoxValues]
    );
    const fieldBoxLabels = useMemo(() => ({
        reference: t('Reference'),
        confirmationNumber: t('ConfirmationNumber'),
        amount: t('TotalAmountIncludingVAT'),
        documentDate: t('Document Date'),
        accountCodeName: effectiveDocType === 'Incomes' ? t('Debit Account') : t('Credit Account'),
        accountCaseId: t('CompanyId'),
        description: t('Description'),
    }), [t, effectiveDocType]);
    const fieldBoxOrder = useMemo(() => Object.keys(fieldBoxLabels), [fieldBoxLabels]);

    const handleOcrFieldFocus = useCallback((event) => {
        const row = event.target?.closest?.('[data-ocr-field]');
        setActiveOcrField(row ? row.getAttribute('data-ocr-field') : null);
    }, []);
    const handleOcrFieldBlur = useCallback((event) => {
        const from = event.target?.closest?.('[data-ocr-field]');
        if (from && !from.contains(event.relatedTarget)) setActiveOcrField(null);
    }, []);
    const handleFieldBoxClick = useCallback((field) => {
        const row = formPaneRef.current?.querySelector(`[data-ocr-field="${field}"]`);
        if (!row) return;
        row.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        const input = row.querySelector('input:not([type="hidden"]), textarea');
        if (input && !input.disabled) input.focus();
        setActiveOcrField(field);
    }, []);

    // Load the current "שיוך הוצאות" AccountCard assignment for Expenses (prefill; isolated read).
    useEffect(() => {
        let cancelled = false;
        const loadAssignedAccountCard = async () => {
            if (effectiveDocType !== 'Expenses' || !client?.caseId || !token || !editedItem?.id) {
                setSelectedAssignedAccountCardId(null);
                initialAssignedAccountCardIdRef.current = null;
                return;
            }
            try {
                const res = await ClientExpenseAssignmentService.getByExpenseId(
                    client.caseId,
                    editedItem.id,
                    token
                );
                if (!res.ok) return;
                const data = await res.json();
                const cardId =
                    data && data.accountCardId != null && data.accountCardId !== ''
                        ? String(data.accountCardId)
                        : null;
                if (!cancelled) {
                    setSelectedAssignedAccountCardId(cardId);
                    initialAssignedAccountCardIdRef.current = cardId;
                }
            } catch (error) {
                console.error('Error loading expense AccountCard assignment:', error);
            }
        };
        loadAssignedAccountCard();
        return () => {
            cancelled = true;
        };
    }, [effectiveDocType, client?.caseId, token, editedItem?.id]);

    // Persist the AccountCard assignment (only when changed) via the isolated endpoint on Save.
    const persistAssignedAccountCardIfNeeded = useCallback(async () => {
        if (effectiveDocType !== 'Expenses' || !client?.caseId || !token || !editedItem?.id) {
            return;
        }
        const current = selectedAssignedAccountCardId ? String(selectedAssignedAccountCardId) : null;
        const initial = initialAssignedAccountCardIdRef.current
            ? String(initialAssignedAccountCardIdRef.current)
            : null;
        if (current === initial) return;
        try {
            const res = await ClientExpenseAssignmentService.updateAssignment(
                { caseId: client.caseId, expenseId: editedItem.id, accountCardId: current },
                token
            );
            if (!res.ok) {
                let msg = '';
                try {
                    msg = (await res.text())?.trim();
                } catch {
                    /* ignore */
                }
                console.error('ClientExpenseAssignment update failed', res.status, msg);
                notify.error(msg || t('ErrorAssigningExpenseAccountCard'));
                return;
            }
            initialAssignedAccountCardIdRef.current = current;
        } catch (error) {
            console.error('ClientExpenseAssignment update error', error);
            notify.error(t('ErrorAssigningExpenseAccountCard'));
        }
    }, [effectiveDocType, client?.caseId, token, editedItem?.id, selectedAssignedAccountCardId, t]);

    const [clarificationConversation, setClarificationConversation] = useState([]);
    const [clarificationConversationLoading, setClarificationConversationLoading] = useState(false);
    const [clarificationViewerId, setClarificationViewerId] = useState('');
    const [clarificationAttachModalOpen, setClarificationAttachModalOpen] = useState(false);
    const [deletingClarificationConversation, setDeletingClarificationConversation] = useState(false);
    const canUseClarificationConversation = ['Incomes', 'Expenses', 'JournalEntries', 'PaymentAcceptances'].includes(effectiveDocType);

    // DocumentTransferDialog keys the allowed target types off `docType`; callers that only set
    // `documentType` on the item (VAT report, transaction search) would otherwise offer none.
    const transferItem = useMemo(
        () => (editedItem ? { ...editedItem, docType: effectiveDocType } : editedItem),
        [editedItem, effectiveDocType]
    );

    const mapClarificationConversationMessage = useCallback((msg) => {
        const messageType = msg?.messageType ?? 0;
        return {
            id: msg.id,
            position: msg.senderId === clarificationViewerId ? 'right' : 'left',
            type: messageType === 1 ? 'photo' : messageType === 2 ? 'file' : messageType === 3 ? 'audio' : 'text',
            text: msg.messageText || '',
            date: msg.messageDate ? new Date(msg.messageDate) : null,
            dateString: (() => {
                if (!msg?.messageDate) return '';
                const dt = new Date(msg.messageDate);
                if (Number.isNaN(dt.getTime())) return '';
                return `${`${dt.getDate()}`.padStart(2, '0')}/${`${dt.getMonth() + 1}`.padStart(2, '0')} ${`${dt.getHours()}`.padStart(2, '0')}:${`${dt.getMinutes()}`.padStart(2, '0')}`;
            })(),
            title: msg.senderName || msg.senderId,
            senderId: msg.senderId,
            senderName: msg.senderName,
            receiverId: msg.receiverId,
            receiverName: msg.receiverName,
            messageType,
            fileUrls: msg.fileUrls || [],
            data: {
                uris: messageType === 1 ? (msg.fileUrls || []) : [],
                fileURLs: messageType === 2 ? (msg.fileUrls || []) : [],
                audioURLs: messageType === 3 ? (msg.fileUrls || []) : [],
            },
            replyTo: msg.replyToChatMessageId
                ? {
                    id: msg.replyToChatMessageId,
                    text: msg.replyPreview || t('MediaMessage'),
                }
                : null,
        };
    }, [clarificationViewerId, t]);

    const loadClarificationConversation = useCallback(async () => {
        if (!token || !client?.caseId || !editedItem?.id || !effectiveDocType) {
            setClarificationConversation([]);
            return;
        }
        if (!['Incomes', 'Expenses', 'JournalEntries', 'PaymentAcceptances'].includes(effectiveDocType)) {
            setClarificationConversation([]);
            return;
        }

        setClarificationConversationLoading(true);
        try {
            const response = await ClarificationDocumentService.GetClarificationMessages(
                effectiveDocType,
                editedItem.id,
                client.caseId,
                token
            );
            if (!response.ok) {
                throw new Error(await response.text());
            }
            const data = await response.json();
            setClarificationConversation((data || []).map(mapClarificationConversationMessage));
        } catch (error) {
            console.error('Failed to load clarification conversation for edit modal', error);
            setClarificationConversation([]);
        } finally {
            setClarificationConversationLoading(false);
        }
    }, [token, client?.caseId, editedItem?.id, effectiveDocType, mapClarificationConversationMessage]);

    const handleDeleteClarificationConversation = useCallback(async () => {
        if (!token || !client?.caseId || !editedItem?.id || !effectiveDocType) return;
        if (!canUseClarificationConversation) return;
        if (!clarificationConversation.length) return;

        const confirm = await Swal.fire({
            title: t('ConfirmDeleteClarificationConversation') || t('Delete'),
            text: t('ConfirmDeleteClarificationConversationText') || t('AreYouSure'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#6B7280',
            confirmButtonText: t('Delete'),
            cancelButtonText: t('Cancel'),
        });
        if (!confirm.isConfirmed) return;

        setDeletingClarificationConversation(true);
        try {
            const response = await ClarificationDocumentService.DeleteClarificationMessages(
                effectiveDocType,
                editedItem.id,
                client.caseId,
                token
            );
            if (!response.ok) {
                throw new Error(await response.text());
            }
            setClarificationConversation([]);
            notify.success(t('ClarificationConversationDeleted') || t('DeletedSuccessfully') || 'Deleted');
        } catch (error) {
            console.error('Failed to delete clarification conversation', error);
            notify.error(error?.message || t('DeleteClarificationConversationFailed') || t('DeleteFailed'));
        } finally {
            setDeletingClarificationConversation(false);
        }
    }, [
        token,
        client?.caseId,
        editedItem?.id,
        effectiveDocType,
        canUseClarificationConversation,
        clarificationConversation.length,
        t,
    ]);

    useEffect(() => {
        AuthService.getCurrentUserTypeId()
            .then((value) => setClarificationViewerId(value || ''))
            .catch(() => setClarificationViewerId(''));
    }, []);

    useEffect(() => {
        if (!editedItem?.id || !effectiveDocType || !token || !client?.caseId) return;
        loadClarificationConversation();
    }, [editedItem?.id, effectiveDocType, token, client?.caseId, loadClarificationConversation]);

    // The OCR import stamps the description when the counterparty of the document is the client
    // themselves, which means it was filed on the wrong side (income in expenses or vice versa).
    const misfiledDocument = getMisfiledDocumentFlag(editedItem?.description);
    useEffect(() => {
        if (!misfiledDocument.isMisfiled || !editedItem?.id) return;

        Swal.fire({
            title: t('DocumentUploadedToWrongType'),
            text: t(misfiledDocument.messageKey),
            icon: 'warning',
            confirmButtonText: t('OK'),
            heightAuto: false,
            customClass: {
                container: 'swal-high-zindex'
            }
        });
    }, [editedItem?.id, misfiledDocument.isMisfiled, misfiledDocument.messageKey]);

    // Warn when an income document is coded with an expense sort code, or vice versa.
    // Journal entries may mix both VAT types, so they are excluded.
    useEffect(() => {
        if (!editedItem?.id || !editedItem?.sortCodeId || !currentSortCodeVatLabel) return;

        showSortCodeDocTypeMismatchWarning(
            effectiveDocType,
            currentSortCodeVatLabel,
            t
        );
    }, [
        editedItem?.id,
        editedItem?.sortCodeId,
        currentSortCodeVatLabel,
        effectiveDocType,
        t,
    ]);

    const duplicatePaymentSourceType = effectiveDocType;
    const canDuplicatePaymentAcceptance =
        duplicatePaymentSourceType === 'Expenses' || duplicatePaymentSourceType === 'Incomes';
    const [relatedPaymentsOpen, setRelatedPaymentsOpen] = useState(false);  // ⟨⟨ related payment acceptances
    const [relatedPaymentsCount, setRelatedPaymentsCount] = useState(0);
    const openRelatedPayments = () => setRelatedPaymentsOpen(true);
    const closeRelatedPayments = () => setRelatedPaymentsOpen(false);
    const [dupJournalOpen, setDupJournalOpen] = useState(false);  // ⟨⟨ duplicate journal
    const [journalGroup, setJournalGroup] = useState(null);
    const [groupItems, setGroupItems] = useState([]);
    const [loadingGroup, setLoadingGroup] = useState(false);
    const openDuplicateJournal = () => {
        setDupJournalOpen(true);
    };   // ⟨⟨ duplicate journal
    const closeDuplicateJournal = () => {
        setDupJournalOpen(false);
    }; // ⟨⟨ duplicate journal

    // Fetch journal group for the current item
    const fetchJournalGroup = async () => {
        if (!editedItem?.id || !token) {
            setJournalGroup(null);
            setGroupItems([]);
            return;
        }

        setLoadingGroup(true);
        try {
            const response = await JournalGroupService.GetGroupByItemIdOnly(
                editedItem.id,
                token
            );

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setJournalGroup(data.data);
                    setGroupItems(data.data.items || []);
                } else {
                    setJournalGroup(null);
                    setGroupItems([]);
                }
            } else {
                setJournalGroup(null);
                setGroupItems([]);
            }
        } catch (error) {
            console.error('Error fetching journal group:', error);
            setJournalGroup(null);
            setGroupItems([]);
        } finally {
            setLoadingGroup(false);
        }
    };

    // Fetch group when item ID changes
    useEffect(() => {
        if (editedItem?.id) {
            fetchJournalGroup();
        } else {
            setJournalGroup(null);
            setGroupItems([]);
        }
    }, [editedItem?.id]);

    // Prefetch count of PaymentAcceptances linked to this Income/Expense so we can
    // decide whether to show the "related payments" button.
    useEffect(() => {
        const docType = effectiveDocType;
        if (!editedItem?.id || !token || !client?.caseId ||
            (docType !== 'Incomes' && docType !== 'Expenses')) {
            setRelatedPaymentsCount(0);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const ids = docType === 'Incomes'
                    ? { incomeId: editedItem.id }
                    : { expenseId: editedItem.id };
                const response = await DocumentService.GetPaymentAcceptancesByRelatedSource(
                    client.caseId,
                    ids,
                    token
                );
                if (!cancelled && response.ok) {
                    const data = await response.json();
                    setRelatedPaymentsCount(Array.isArray(data) ? data.length : 0);
                } else if (!cancelled) {
                    setRelatedPaymentsCount(0);
                }
            } catch (error) {
                if (!cancelled) setRelatedPaymentsCount(0);
            }
        })();

        return () => { cancelled = true; };
    }, [editedItem?.id, type, client?.caseId, token]);

    const syncEditedItemProjectFromSegmentationList = useCallback(
        (list, item, defaultDocType = '') => {
            if (!item?.id || !Array.isArray(list) || list.length === 0) return;
            const matched = findSegmentationProjectForItem(list, item, defaultDocType);
            if (!matched?.id) return;

            setEditedItem((prev) => {
                if (!prev?.id || String(prev.id) !== String(item.id)) return prev;
                const prevId = getAssignedProjectIdFromItem(prev);
                const hasLabels =
                    (prev.segmentationCode ?? prev.SegmentationCode) &&
                    (prev.segmentationName ?? prev.SegmentationName);
                if (prevId === String(matched.id) && hasLabels) return prev;
                return { ...prev, ...buildProjectFieldsFromSegmentation(matched) };
            });
        },
        [setEditedItem]
    );

    const fetchSegmentationProjects = useCallback(async () => {
        if (!client?.caseId || !token) {
            setSegmentationProjects([]);
            return;
        }
        try {
            const res = await ProjectsService.getProjects(client.caseId, token);
            if (!res.ok) return;
            const data = await res.json();
            const list = (Array.isArray(data) ? data : [])
                .map(normalizeSegmentationProjectRow)
                .filter((p) => p.id);
            setSegmentationProjects(list);

            const item = editedItemRef.current;
            const docType = item?.docType || type;
            syncEditedItemProjectFromSegmentationList(list, item, docType);
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    }, [client?.caseId, token, type, syncEditedItemProjectFromSegmentationList]);

    useEffect(() => {
        if (!showProjectAssignRow || !client?.caseId || !token) {
            return;
        }
        void fetchSegmentationProjects();
    }, [showProjectAssignRow, client?.caseId, token, fetchSegmentationProjects]);

    useEffect(() => {
        if (!showProjectAssignRow || !segmentationProjects.length || !editedItem?.id) {
            return;
        }
        const docType = editedItem.docType || type;
        syncEditedItemProjectFromSegmentationList(segmentationProjects, editedItem, docType);
    }, [
        showProjectAssignRow,
        segmentationProjects,
        editedItem?.id,
        editedItem?.docType,
        type,
        syncEditedItemProjectFromSegmentationList,
    ]);

    const applyProjectSelection = useCallback(
        async (newSegmentationId, optionalProjectMeta = null) => {
            const item = editedItemRef.current;
            if (!client?.caseId || !token || !item?.id) {
                return;
            }
            const docType = item.docType || type;
            if (!['Incomes', 'Expenses', 'JournalEntries'].includes(docType)) return;

            const newId = newSegmentationId ? String(newSegmentationId) : '';
            const oldId = getAssignedProjectIdFromItem(item);
            if (oldId === newId) return;

            setProjectAssignLoading(true);
            try {
                if (oldId) {
                    let removeRes;
                    if (docType === 'Incomes') {
                        removeRes = await ProjectsService.removeIncomeFromSegmentation(
                            oldId,
                            client.caseId,
                            item.id,
                            token
                        );
                    } else if (docType === 'Expenses') {
                        removeRes = await ProjectsService.removeExpenseFromSegmentation(
                            oldId,
                            client.caseId,
                            item.id,
                            token
                        );
                    } else {
                        removeRes = await ProjectsService.removeJournalEntryFromSegmentation(
                            oldId,
                            client.caseId,
                            item.id,
                            token
                        );
                    }
                    if (!removeRes.ok) {
                        let errText = '';
                        try {
                            errText = await removeRes.text();
                        } catch {
                            /* ignore */
                        }
                        notify.error(errText || t('ErrorUpdatingProject'));
                        return;
                    }
                }
                if (newId) {
                    const payload = { SegmentationId: newId, CaseId: client.caseId };
                    if (docType === 'Incomes') payload.IncomesIds = [item.id];
                    else if (docType === 'Expenses') payload.ExpensesIds = [item.id];
                    else payload.JournalEntryIds = [item.id];

                    const addRes = await ProjectsService.assignDocuments(payload, token);
                    if (!addRes.ok) {
                        let errText = '';
                        try {
                            errText = await addRes.text();
                        } catch {
                            /* ignore */
                        }
                        notify.error(errText || t('ErrorUpdatingProject'));
                        return;
                    }
                }

                const meta =
                    optionalProjectMeta ||
                    segmentationProjects.find((p) => String(p.id) === newId);
                setEditedItem((prev) => ({
                    ...prev,
                    projects: newId
                        ? [
                              {
                                  projectId: newId,
                                  code: meta?.code ?? '',
                                  name: meta?.name ?? '',
                              },
                          ]
                        : [],
                    segmentationId: newId || null,
                    segmentationCode: newId ? meta?.code ?? '' : null,
                    segmentationName: newId ? meta?.name ?? '' : null,
                }));
            } catch (e) {
                console.error(e);
                notify.error(t('ErrorUpdatingProject'));
            } finally {
                setProjectAssignLoading(false);
            }
        },
        [client?.caseId, token, type, t, setEditedItem, segmentationProjects]
    );

    const handleProjectAutocompleteChange = (_event, newValue) => {
        if (newValue?.kind === 'add' || newValue?.id === '__ADD_NEW__') {
            setProjectModalOpen(true);
            return;
        }
        if (!newValue || newValue.kind === 'none' || newValue.id === '') {
            void applyProjectSelection('');
            return;
        }
        void applyProjectSelection(String(newValue.id));
    };

    const handleProjectModalClose = () => {
        setProjectModalOpen(false);
    };

    const handleProjectModalSave = async (projectInput) => {
        if (!client?.caseId || !token) return false;
        const drafts = Array.isArray(projectInput) ? projectInput : [projectInput];
        const normalized = drafts.map((d) => ({
            code: String(d?.code ?? '').trim(),
            name: String(d?.name ?? '').trim(),
        }));
        if (normalized.some((d) => !d.code || !d.name)) return false;

        setProjectCreateLoading(true);
        try {
            let lastCreated = null;
            for (const draft of normalized) {
                const res = await ProjectsService.createProject(
                    { CaseId: client.caseId, Code: draft.code, Name: draft.name },
                    token
                );
                if (!res.ok) {
                    const msg = await res.text();
                    notify.error(msg || t('ErrorCreatingProject'));
                    await fetchSegmentationProjects();
                    return false;
                }
                lastCreated = await res.json();
            }
            notify.success(`${t('Add')} ${t('Projects')}`);
            await fetchSegmentationProjects();
            setProjectModalOpen(false);
            if (lastCreated?.id && editedItemRef.current?.id) {
                await applyProjectSelection(String(lastCreated.id), {
                    code: lastCreated.code,
                    name: lastCreated.name,
                });
            }
            return true;
        } catch (e) {
            console.error(e);
            notify.error(t('ErrorCreatingProject'));
            return false;
        } finally {
            setProjectCreateLoading(false);
        }
    };

    // Chat functionality
    const handleSendToChat = async () => {
        try {
            // First, open chat with the client
            await selectUserAndOpenChat(client.caseId, client.name || 'Client', {
                caseId: client.caseId,
                documentId: editedItem?.id,
                documentType: effectiveDocType,
            });

            // If there's a document URL, send the file to chat
            if (editedItem?.documentUrl) {
                // Create a file from the document URL
                const response = await fetch(editedItem.documentUrl);
                const blob = await response.blob();
                const file = new File([blob], `${editedItem.title || 'document'}.pdf`, { type: 'application/pdf' });

                // Send the file to chat
                await handleSendMessageToChat(file, client.caseId, client.name);
            } else {
                // Just open the chat without sending a file
                notify.success(t('ChatOpenedWithClient'));
            }
        } catch (error) {
            console.error('Error opening chat or sending document:', error);
            notify.error(t('ErrorSendingToChat'));
        }
    };

    // Check Confirmation Number functionality
    const handleCheckConfirmationNumber = async () => {
        try {
            // Check if we have the required data
            if (!client?.accountantId) {
                notify.error(t('MissingAccountantId'));
                return;
            }

            if (!editedItem?.amount) {
                notify.error(t('MissingAmount'));
                return;
            }

            if (!editedItem?.documentDate) {
                notify.error(t('MissingDocumentDate'));
                return;
            }

            if (!editedItem?.reference) {
                notify.error(t('MissingReferenceNumber'));
                return;
            }

            // Prepare the request
            let vatNumber = 0;

            // If it's an expense, get the accountCaseId from the credit account
            // If it's an income, get the accountCaseId from the debit account
            if (type === 'Expenses' && editedItem.creditAccount) {
                const creditAccountCard = accountCardOptions.find(card => card.cardId === editedItem.creditAccount);
                if (creditAccountCard && creditAccountCard.accountCaseId) {
                    vatNumber = parseInt(creditAccountCard.accountCaseId) || 0;
                }
            } else if (type === 'Incomes' && editedItem.debitAccount) {
                //debug

                const debitAccountCard = accountCardOptions.find(card => card.cardId === editedItem.debitAccount);
                if (debitAccountCard && debitAccountCard.accountCaseId) {
                    vatNumber = parseInt(debitAccountCard.accountCaseId) || 0;
                }
            } else {
                // For other types, use supplier number
                vatNumber = parseInt(editedItem.supplierNumber) || 0;
            }

            const request = {
                accountantId: client.accountantId,
                customerVATNumber: parseInt(editedItem.caseId),
                vatNumber: vatNumber,
                paymentAmount: parseFloat(editedItem.amount) || 0,
                vatAmount: parseFloat(editedItem.amount - (editedItem.amount / 1.17)) || 0,
                invoiceDate: editedItem.documentDate,
                invoiceReferenceNumber: editedItem.reference,
                vatPercentage: editedItem.vat ?? 100 // Use VAT from editedItem, default to 100 only when null/undefined (preserve explicit 0)
            };

            // Call the API
            const response = await DocumentService.checkConfirmationNumber(request, token);

            if (response.authUrl) {
                // Check if user previously closed the auth window
                if (authWindowClosedByUser) {
                    notify.info(t('AuthenticationCancelledByUser'));
                    return;
                }

                // Check if auth window is already open
                if (isAuthWindowOpen) {
                    notify.info(t('AuthenticationWindowAlreadyOpen'));
                    return;
                }

                // Set state to indicate auth window is opening
                setIsAuthWindowOpen(true);
                setAuthWindowClosedByUser(false);

                // Create unique window name to prevent reuse
                const windowName = `taxAuthorityAuth_${Date.now()}`;

                // Open authentication window
                const authWindow = window.open(
                    response.authUrl,
                    windowName,
                    'width=600,height=700,scrollbars=yes,resizable=yes'
                );

                // Check if window was blocked or failed to open
                if (!authWindow || authWindow.closed) {
                    setIsAuthWindowOpen(false);
                    notify.error(t('AuthenticationWindowBlocked'));
                    return;
                }

                // Declare variables for cleanup
                let checkClosed, timeoutId;

                // Also listen for window focus events to detect if user switched back
                const handleWindowFocus = () => {
                    if (authWindow && authWindow.closed) {
                        clearInterval(checkClosed);
                        clearTimeout(timeoutId);
                        window.removeEventListener('focus', handleWindowFocus);
                        setIsAuthWindowOpen(false);
                        setAuthWindowClosedByUser(true);
                        notify.info(t('AuthenticationWindowClosed'));
                    }
                };

                window.addEventListener('focus', handleWindowFocus);

                // Listen for the window to close or receive a message
                checkClosed = setInterval(() => {
                    if (authWindow.closed) {
                        clearInterval(checkClosed);
                        clearTimeout(timeoutId);
                        window.removeEventListener('focus', handleWindowFocus);
                        setIsAuthWindowOpen(false);
                        setAuthWindowClosedByUser(true);

                        // Don't automatically retry - let user decide
                        notify.info(t('AuthenticationWindowClosed'));
                    }
                }, 1000);

                // Set a timeout to automatically reset state after 5 minutes
                timeoutId = setTimeout(() => {
                    clearInterval(checkClosed);
                    window.removeEventListener('focus', handleWindowFocus);
                    setIsAuthWindowOpen(false);
                    setAuthWindowClosedByUser(false);
                }, 5 * 60 * 1000); // 5 minutes

                notify.info(t('PleaseLoginToTaxAuthority'));
            } else if (response.status === 200 && response.confirmationNumber) {
                // Update the confirmation number in the form
                handleChange('confirmationNumber', response.confirmationNumber);
                notify.success(`${t('ConfirmationNumberReceived')}: ${response.confirmationNumber}`);
                // Reset auth states on success
                setIsAuthWindowOpen(false);
                setAuthWindowClosedByUser(false);
            } else {
                notify.error(response.message || t('ErrorGettingConfirmationNumber'));
            }
        } catch (error) {
            console.error('Error checking confirmation number:', error);
            notify.error(t('ErrorCheckingConfirmationNumber'));
            // Reset auth states on error
            setIsAuthWindowOpen(false);
            setAuthWindowClosedByUser(false);
        }
    };

    const [cardIdOption, setCardIdOption] = useState(-1);
    const [isAuthWindowOpen, setIsAuthWindowOpen] = useState(false);
    const [authWindowClosedByUser, setAuthWindowClosedByUser] = useState(false);

    // Function to reset authentication state and allow retry
    const resetAuthState = () => {
        setIsAuthWindowOpen(false);
        setAuthWindowClosedByUser(false);
    };

    // Cleanup effect to reset auth state when component unmounts
    useEffect(() => {
        return () => {
            setIsAuthWindowOpen(false);
            setAuthWindowClosedByUser(false);
        };
    }, []);

    useEffect(() => {

        if (accountCardOptions && accountCardOptions.length > 0) {
            const maxCardId = Math.max(...accountCardOptions.slice(1).map(option => parseInt(option.cardId, 10)));
            setCardIdOption(maxCardId + 1);


        }
    }, [accountCardOptions]);

    useEffect(() => {
        setShowPdfIframe(showIframe)
    }, [showIframe]);

    useEffect(() => {
        document.body.appendChild(container);
        return () => {
            try {
                document.body.removeChild(container);
            } catch (e) {
                // Ignore
            }
        };
    }, [container]);

    useEffect(() => {
        let account = null;

        if (editedItem.accountCodeId) {
            account = accountCardOptions.find(account => account.cardId === editedItem.accountCodeId);
        }
        if (!account && editedItem.accountCodeName) {
            account = accountCardOptions.find(account => account.name === editedItem.accountCodeName);
        }
        // if (!account && editedItem.accountCaseId) {
        //     account = accountCardOptions.find(account => account.caseId === editedItem.accountCaseId);
        // }


        if (editedItem.docType === 'Incomes') {
            setPalCode("לקוחות");
        }
        else if (editedItem.docType === 'Expenses') {
            setPalCode("ספקים");

        }
        else {
            setPalCode("");
        }
        setSelectedAccount(account);
    }, [editedItem.uploadNumber]);


    useEffect(() => {
        if (!newCardId) return;
        // A split row asked for the card: it owns the field, so hand the id straight back
        // without waiting for the options list to refresh.
        if (newCardId.type === 'split') {
            applySplitAccountCardRef.current?.(newCardId.cardId);
            setNewCardId(null);
            return;
        }
        if (!accountCardOptions?.length) return;
        const selectedOption = accountCardOptions.find(
            (option) => String(option.cardId) === String(newCardId.cardId)
        );
        if (!selectedOption) return; // New card not in list yet (e.g. fetchEnums still in progress); will re-run when options update
        if (newCardId.type === 'main') {
            handleMuiChange(null, selectedOption);
        } else {
            const shouldSyncAccountCaseId =
                (journalSortCodeIsExpense && newCardId.type === 'creditAccount') ||
                (journalSortCodeIsIncome && newCardId.type === 'debitAccount');
            if (shouldSyncAccountCaseId && typeof setEditedItem === 'function') {
                // Atomic update (avoids parent stale-state overwriting when handleChange is called twice)
                setEditedItem((prev) => ({
                    ...prev,
                    [newCardId.type]: newCardId.cardId,
                    accountCaseId: selectedOption.accountCaseId ?? '',
                }));
            } else {
                // Default/legacy behavior
                handleChange(newCardId.type, newCardId.cardId);
            }
        }
        setNewCardId(null);
    }, [
        accountCardOptions,
        newCardId,
        editedItem.docType,
        journalSortCodeIsExpense,
        journalSortCodeIsIncome,
        syncJournalEntryAccountCaseId,
        setEditedItem
    ]);



    useEffect(() => {
        if (editedItem.vat === 16 || editedItem.vat === 17) {
            setCurrentVat(getVatValue(editedItem.documentDate, false, true));
        } else {
            setCurrentVat(getVatValue(editedItem.documentDate, false, false));
        }
    }, [editedItem.vat, editedItem.documentDate]);

    useEffect(() => {
        lastAmountEditRef.current = 'gross';
        netFocusedRef.current = false;
        vatOverrideRef.current = null;
    }, [editedItem?.id]);

    useEffect(() => {
        netAmountInputRef.current = netAmountInput;
    }, [netAmountInput]);

    const getVatCalcDate = () => editedItem.documentDate || '2025-01-01';

    useEffect(() => {
        if (lastAmountEditRef.current === 'net') return;
        const computedNet =
            (Number(editedItem?.amount) || 0) -
            calculateVatAmount(editedItem?.amount, editedItem.vat, getVatCalcDate());
        SetBeforeVatAmount(computedNet);
        if (!netFocusedRef.current) {
            if (editedItem?.amount === '' || editedItem?.amount == null) {
                setNetAmountInput('');
            } else {
                setNetAmountInput(String(Number(computedNet.toFixed(2))));
            }
        }
    }, [editedItem.amount, editedItem.vat, editedItem.documentDate]);

    useEffect(() => {
        if (lastAmountEditRef.current !== 'net') return;
        const net = parseNumeric(netAmountInputRef.current);
        if (isNaN(net) || netAmountInputRef.current === '') return;
        const gross = Number(calculateGrossFromNet(net, editedItem.vat ?? 0, getVatCalcDate()).toFixed(2));
        const currentGross = parseNumeric(editedItem.amount);
        if (!isNaN(currentGross) && Math.abs(currentGross - gross) < 0.001) return;
        handleChange('amount', gross);
        SetBeforeVatAmount(net);
    }, [editedItem.vat, editedItem.documentDate]);



    const handleMuiChange = (event, newValue) => {
        if (newValue && newValue.value1 === 'AddItem') {
            setAddAccountCardType('main');
            setEditedAddAccountCard(true);
            setOldSelectedAccountCardItem({
                cardId: editedItem.accountCodeId,
                caseId: client.caseId,
                accountCaseId: editedItem.accountCaseId,
                name: editedItem.accountCodeName,
                palCode: '',
                client: null
            });
            setEditedItem({ ...editedItem, accountCodeName: newValue.searchTerm || '', accountCodeId: cardIdOption, accountCaseId: '' });
        } else {
            setSelectedAccount(newValue);
            const selectedOption = accountCardOptions.find(option => option.cardId === (newValue ? newValue.cardId : ''));
            const shouldSyncMainAccountCase =
                !(journalSortCodeIsIncome || journalSortCodeIsExpense);
            setEditedItem({
                ...editedItem,
                accountCodeName: selectedOption ? selectedOption.name : '',
                accountCodeId: selectedOption ? selectedOption.cardId : '',
                accountCaseId: shouldSyncMainAccountCase
                    ? (selectedOption ? (selectedOption.accountCaseId ??  '') : '')
                    : editedItem.accountCaseId,
                ...(editedItem.docType === 'Incomes'
                    ? { debitAccount: selectedOption ? selectedOption.cardId : '' } :
                    editedItem.docType === 'Expenses' && { creditAccount: selectedOption ? selectedOption.cardId : '' })
            });
        }
    };

    const handleToggleIframe = () => {
        setShowPdfIframe((prev) => !prev);
    };
    const handleChangeAccount = (field, value) => {
        setEditedItem({ ...editedItem, [field]: value });
    }
    /** Opens the account card dialog for the split modal; the new card goes to the row that asked. */
    const handleRequestAddAccountCardForSplit = useCallback(() => {
        setAddAccountCardType('split');
        setIsEditAccountCard(false);
        setShowAccountCardInputsEdit(false);
        setNewAccountCard({ accountCodeName: '', accountCodeId: cardIdOption });
        setEditedAddAccountCard(true);
    }, [cardIdOption, setEditedAddAccountCard]);

    const handleCloseItem = async () => {
        const didAddNewCard = !!newCardId;
        setPalCode('');
        setShowAccountCardInputsEdit(false);
        setEditedAddAccountCard(false);
        // Only the document's own account card field has something to restore; a cancelled add
        // started from another field (or from the split rows) must leave it alone.
        if (!didAddNewCard && addAccountCardType === 'main') {
            setSelectedAccount(oldSelectedAccountCardItem);
            handleChangeAccount('accountCodeId', oldSelectedAccountCardItem.cardId?.toString());
            handleChangeAccount('accountCodeName', oldSelectedAccountCardItem.name?.toString());
            handleChangeAccount('accountCaseId', oldSelectedAccountCardItem.accountCaseId?.toString());
        } else {
            // Refetch enums when closing after adding a new account card (like Document.jsx) so accountCardOptions has the new card, then selection useEffect can select it
            if (typeof fetchEnums === 'function') {
                try {
                    await fetchEnums();
                } catch (_) {}
            }
        }
    };

    // Input chrome matches AddJournalEntryModal / AutocompleteSelectFilter (see .edit-style-input)
    const inputPropsStyle = {
        disableUnderline: true,
    };

    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );

    const dateAdapterLocale = useMemo(() => {
        switch (i18n.language) {
            case 'ar':
                return arSA;
            case 'he':
                return he;
            default:
                return enUS;
        }
    }, [i18n.language]);

    const toPickerDate = useCallback((value) => {
        if (value == null || value === '') return null;
        const d = value instanceof Date ? value : new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }, []);

    /** Align RegistrationMonthDate to the month/year of DocumentDate (UTC month start, MM/YYYY). */
    const registrationMonthFromDocumentDate = useCallback((documentDate) => {
        if (!documentDate) return null;
        const d = documentDate instanceof Date ? documentDate : new Date(documentDate);
        if (Number.isNaN(d.getTime())) return null;
        return new Date(Date.UTC(d.getFullYear(), d.getMonth(), 1));
    }, []);

    const applyDocumentDateChange = useCallback((documentDate) => {
        if (!documentDate) return;
        const registrationMonthDate = registrationMonthFromDocumentDate(documentDate);
        if (typeof setEditedItem === 'function') {
            setEditedItem((prev) => ({
                ...prev,
                documentDate,
                ...(registrationMonthDate ? { registrationMonthDate } : {}),
            }));
        } else {
            handleChange('documentDate', documentDate);
            if (registrationMonthDate) {
                handleChange('registrationMonthDate', registrationMonthDate);
            }
        }
        setDateError(false);
    }, [handleChange, registrationMonthFromDocumentDate, setEditedItem]);

    const handleEditModalDateEnter = useCallback((event, fieldName) => {
        if (event.key !== 'Enter' || event.shiftKey) return;
        event.preventDefault();
        const parsed = parseSmartDateInput(event.target?.value);
        if (parsed) {
            if (fieldName === 'documentDate') {
                applyDocumentDateChange(parsed);
            } else {
                handleChange(fieldName, parsed);
            }
        }
    }, [applyDocumentDateChange, handleChange]);

    const vatSelectOptions = useMemo(
        () =>
            Constants.VATPercentage.map((option) => ({
                value: option,
                label: option === 16 ? t('AccountingTax') : `${option}%`,
            })),
        [t]
    );

    const currencySelectOptions = useMemo(
        () => [
            { value: '', label: t('Clear') },
            ...Object.entries(Constants.ForeignCurrencyCode).map(([code, num]) => ({
                value: num,
                label: `${code}  ${Constants.ForeignCurrencySymbol[code] || ''}`.trim(),
            })),
        ],
        [t]
    );

    // Validation and warning logic for currency fields
    useEffect(() => {
        const { foreignCurrencyCode, documentDate, foreignCurrencyAmount } = editedItem;
        setCurrencyError(foreignCurrencyCode === null || foreignCurrencyCode === '' || foreignCurrencyCode === undefined);
        setDateError(!documentDate);
        if (
            (foreignCurrencyCode == null || documentDate == null || foreignCurrencyAmount == null) &&
            editedItem.foreignCurrencyAmount
        ) {
            setCurrencyWarn(true);
        } else {
            setCurrencyWarn(false);
        }
    }, [editedItem.foreignCurrencyCode, editedItem.documentDate, editedItem.foreignCurrencyAmount]);

    // Refs to keep the latest values reachable inside debounced callbacks without stale closures
    const manualRateInputRef = useRef('');
    const FCRateRef = useRef(1);
    useEffect(() => { FCRateRef.current = FCRate; }, [FCRate]);

    // Debounce timers for cross-field calculations triggered by typing in the amount fields.
    // - amountSyncTimeoutRef / fcSyncTimeoutRef: per-field debounce for "derive opposite amount"
    //   (uses a known rate, no inter-dependency between fields).
    // - rateCalcTimeoutRef: SHARED debounce for "derive manual rate from both amounts".
    //   This one is reset whenever the user types in EITHER field, so the rate is only
    //   computed after BOTH fields are stable - otherwise typing a partial value in one
    //   field would compute a wrong rate while the user is still typing in the other.
    const amountSyncTimeoutRef = useRef(null);
    const fcSyncTimeoutRef = useRef(null);
    const rateCalcTimeoutRef = useRef(null);
    const AMOUNT_SYNC_DEBOUNCE_MS = 600;

    useEffect(() => {
        return () => {
            if (amountSyncTimeoutRef.current) clearTimeout(amountSyncTimeoutRef.current);
            if (fcSyncTimeoutRef.current) clearTimeout(fcSyncTimeoutRef.current);
            if (rateCalcTimeoutRef.current) clearTimeout(rateCalcTimeoutRef.current);
        };
    }, []);

    // Derive the manual currency rate from both amounts when:
    //  - we are in Manual exchange-rate mode
    //  - a foreign currency code is selected
    //  - both amounts are present and > 0
    // This runs from a shared debounce so partial values during typing don't write
    // wrong intermediate rates into the Manual Rate field.
    // NOTE: when both amounts are set we recompute the rate even if a rate was already
    // present (Flow 1: editing either amount changes the rate). The single-amount cases
    // (Flow 2/3) are handled in computeAmountSideEffect / computeFcSideEffect.
    const computeManualRateFromAmounts = () => {
        const latest = editedItemRef.current || {};
        if (latest.exchangeRateType !== Constants.ExchangeRateType.Manual) return;

        const hasForeignCode =
            latest.foreignCurrencyCode !== null &&
            latest.foreignCurrencyCode !== '' &&
            latest.foreignCurrencyCode !== undefined;
        if (!hasForeignCode) return;

        const amountAbs = absNumeric(latest.amount);
        const fcAbs = absNumeric(latest.foreignCurrencyAmount);
        if (isNaN(amountAbs) || amountAbs <= 0 || isNaN(fcAbs) || fcAbs <= 0) return;

        const newRate = (amountAbs / fcAbs).toFixed(MANUAL_RATE_DECIMALS);
        setManualRateInput(newRate);
        lastManualRateRef.current = newRate;
        // Use functional updater on parent state to avoid stale-closure overwrites of
        // sibling fields (e.g. the user's just-typed foreignCurrencyAmount). The parent's
        // handleChange does setEditedItem({ ...editedItem, [field]: value }) using a
        // closure-captured editedItem, which can revert concurrent edits.
        setEditedItem((prev) => ({ ...prev, exchangeRate: newRate }));
    };

    const scheduleRateCalc = () => {
        if (rateCalcTimeoutRef.current) clearTimeout(rateCalcTimeoutRef.current);
        rateCalcTimeoutRef.current = setTimeout(() => {
            rateCalcTimeoutRef.current = null;
            computeManualRateFromAmounts();
        }, AMOUNT_SYNC_DEBOUNCE_MS);
    };

    // Cross-field side-effects after the user types in NIS / FC amount fields.
    // Behavior in Manual exchange-rate mode (matches the user's three flows):
    //  - Both amounts filled  -> rate recalculation handled by the shared rate-calc debounce.
    //                            We do NOT touch the OTHER amount here, because the user just
    //                            typed it and we don't want to overwrite it.
    //  - Only one amount filled + a rate is set -> derive the missing amount from rate.
    //      * Flow 3: amount filled, FC empty -> derive FC = amount / rate
    //      * Flow 2: FC filled, amount empty -> derive amount = FC * rate
    //  - Otherwise: no-op.
    const computeAmountSideEffect = (value) => {
        const latest = editedItemRef.current || {};
        const isManual = latest.exchangeRateType === Constants.ExchangeRateType.Manual;
        const hasForeignCode =
            latest.foreignCurrencyCode !== null &&
            latest.foreignCurrencyCode !== '' &&
            latest.foreignCurrencyCode !== undefined;

        if (isManual) {
            if (!hasForeignCode) return;

            const fcAbs = absNumeric(latest.foreignCurrencyAmount);
            const fcSet = !isNaN(fcAbs) && fcAbs > 0;
            // Both amounts filled -> let the shared rate-calc debounce recompute the rate.
            if (fcSet) return;

            // FC empty + rate set -> derive FC from amount / rate (Flow 3).
            const amountAbs = absNumeric(value);
            const manualRateNum = parseNumeric(manualRateInputRef.current);
            if (!isNaN(manualRateNum) && manualRateNum > 0 && !isNaN(amountAbs) && amountAbs > 0) {
                const derived = (signOf(value) * (amountAbs / manualRateNum)).toFixed(2);
                // Functional updater - avoids stale-closure parent handleChange overwriting
                // unrelated fields (e.g. the user's latest amount keystrokes).
                setEditedItem((prev) => ({ ...prev, foreignCurrencyAmount: derived }));
            }
            return;
        }

        const rate = FCRateRef.current;
        const amountAbs = absNumeric(value);
        if (hasForeignCode && latest.documentDate && rate != null && rate > 0 && !isNaN(amountAbs)) {
            const derived = signOf(value) * (amountAbs / rate);
            setEditedItem((prev) => ({ ...prev, foreignCurrencyAmount: derived }));
        }
    };

    const computeFcSideEffect = (value) => {
        const latest = editedItemRef.current || {};
        const isManual = latest.exchangeRateType === Constants.ExchangeRateType.Manual;
        const hasForeignCode =
            latest.foreignCurrencyCode !== null &&
            latest.foreignCurrencyCode !== '' &&
            latest.foreignCurrencyCode !== undefined;

        const fcAbs = absNumeric(value);
        if (value === '' || value == null || isNaN(fcAbs) || fcAbs <= 0) return;

        // Keep whatever sign the document already had so deriving the shekel amount from
        // the foreign amount never turns a negative document into a positive one; when
        // the amount is still empty the foreign amount dictates the sign.
        const amountSign = isNaN(parseNumeric(latest.amount))
            ? signOf(value)
            : signOf(latest.amount);

        if (isManual) {
            if (!hasForeignCode) return;

            const amountAbs = absNumeric(latest.amount);
            const amountSet = !isNaN(amountAbs) && amountAbs > 0;
            // Both amounts filled -> let the shared rate-calc debounce recompute the rate.
            if (amountSet) return;

            // Amount empty + rate set -> derive amount from FC * rate (Flow 2).
            const manualRateNum = parseNumeric(manualRateInputRef.current);
            if (!isNaN(manualRateNum) && manualRateNum > 0) {
                const multiplied = amountSign * fcAbs * manualRateNum;
                const derived = Number(multiplied.toFixed(2));
                // Functional updater - avoids stale-closure parent handleChange overwriting
                // unrelated fields (e.g. the user's latest foreignCurrencyAmount keystrokes).
                setEditedItem((prev) => ({ ...prev, amount: derived }));
            }
            return;
        }

        const rate = FCRateRef.current;
        if (hasForeignCode && latest.documentDate && rate != null && rate > 0) {
            const multiplied = amountSign * fcAbs * Number(rate || 0);
            const derived = Number(multiplied.toFixed(2));
            setEditedItem((prev) => ({ ...prev, amount: derived }));
        }
    };

    // Cancel any pending debounced amount/fc sync and (optionally) run it immediately.
    // Also flushes the shared rate-calc debounce: blur means the user is done with this
    // field, so it's safe to compute the rate now.
    const flushAmountSync = (value) => {
        if (amountSyncTimeoutRef.current) {
            clearTimeout(amountSyncTimeoutRef.current);
            amountSyncTimeoutRef.current = null;
        }
        if (rateCalcTimeoutRef.current) {
            clearTimeout(rateCalcTimeoutRef.current);
            rateCalcTimeoutRef.current = null;
        }
        if (value !== undefined) computeAmountSideEffect(value);
        computeManualRateFromAmounts();
    };

    const flushFcSync = (value) => {
        if (fcSyncTimeoutRef.current) {
            clearTimeout(fcSyncTimeoutRef.current);
            fcSyncTimeoutRef.current = null;
        }
        if (rateCalcTimeoutRef.current) {
            clearTimeout(rateCalcTimeoutRef.current);
            rateCalcTimeoutRef.current = null;
        }
        if (value !== undefined) computeFcSideEffect(value);
        computeManualRateFromAmounts();
    };

    // Handlers for direct user input
    const handleChangeAmount = (value) => {
        if (editedItem.exchangeRateType === Constants.ExchangeRateType.Manual) userClearedManualRateRef.current = false;
        handleChange('amount', value);

        // Reset the shared rate-from-amounts debounce on every keystroke in either field.
        scheduleRateCalc();

        if (amountSyncTimeoutRef.current) clearTimeout(amountSyncTimeoutRef.current);
        amountSyncTimeoutRef.current = setTimeout(() => {
            computeAmountSideEffect(value);
        }, AMOUNT_SYNC_DEBOUNCE_MS);
    };

    const handleChangeNetAmount = (value) => {
        lastAmountEditRef.current = 'net';
        setNetAmountInput(value);
        if (value === '' || isNaN(Number(value))) {
            handleChangeAmount('');
            SetBeforeVatAmount(0);
            return;
        }
        const net = Number(value);
        const gross = Number(calculateGrossFromNet(net, editedItem.vat ?? 0, getVatCalcDate()).toFixed(2));
        SetBeforeVatAmount(net);
        handleChangeAmount(String(gross));
    };

    const handleChangeForeignCurrencyAmount = (value) => {
        if (editedItem.exchangeRateType === Constants.ExchangeRateType.Manual) userClearedManualRateRef.current = false;
        if (
            editedItem.foreignCurrencyCode === null ||
            editedItem.foreignCurrencyCode === '' ||
            editedItem.foreignCurrencyCode === undefined
        ) {
            notify.error(t('PleaseSelectCurrencyCodeFirst'));
            return;
        }
        handleChange('foreignCurrencyAmount', value);

        // Reset the shared rate-from-amounts debounce on every keystroke in either field.
        scheduleRateCalc();

        if (fcSyncTimeoutRef.current) clearTimeout(fcSyncTimeoutRef.current);
        fcSyncTimeoutRef.current = setTimeout(() => {
            computeFcSideEffect(value);
        }, AMOUNT_SYNC_DEBOUNCE_MS);
    };

    // Triggered when the user types in the manual exchange-rate field.
    // When the user types a new rate, the missing amount is derived:
    //   - amount is set     -> derive FC = amount / rate (Flow 3-like; also covers both-amounts-filled)
    //   - amount empty, FC set -> derive amount = FC * rate (Flow 2)
    //   - both empty:  nothing to derive
    const handleManualRateChange = (rawValue) => {
        // The rate is a magnitude; the sign belongs to the amounts, so a typed minus is
        // dropped instead of storing a negative rate.
        const value =
            rawValue === '' || rawValue == null
                ? rawValue
                : String(rawValue).replace(/-/g, '');
        handleChange('exchangeRate', value);
        const rateNum = parseNumeric(value);
        if (value !== '' && !isNaN(rateNum) && rateNum > 0) {
            lastManualRateRef.current = value;
        }
        if (value === '' || isNaN(rateNum) || rateNum <= 0) return;
        if (
            editedItem.foreignCurrencyCode == null ||
            editedItem.foreignCurrencyCode === '' ||
            editedItem.foreignCurrencyCode === undefined
        ) {
            notify.info(t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode'));
            return;
        }

        const amountAbs = absNumeric(editedItem.amount);
        const fcAbs = absNumeric(editedItem.foreignCurrencyAmount);
        const amountValid = !isNaN(amountAbs) && amountAbs > 0;
        const fcValid = !isNaN(fcAbs) && fcAbs > 0;

        if (amountValid) {
            // Amount is the anchor; derive FC from amount / rate.
            handleChange(
                'foreignCurrencyAmount',
                (signOf(editedItem.amount) * (amountAbs / rateNum)).toFixed(2)
            );
        } else if (fcValid) {
            // Flow 2: amount is empty but FC is filled; derive amount from FC * rate.
            handleChange(
                'amount',
                Number((signOf(editedItem.foreignCurrencyAmount) * fcAbs * rateNum).toFixed(2))
            );
        }
    };


    const inputStyle = {
        style: {
            textAlign: direction === 'ltr' ? 'left' : 'right',
        },
    };

    // Add local state for editing values
    const [amountInput, setAmountInput] = useState(editedItem.amount ?? '');
    const [currencyAmountInput, setCurrencyAmountInput] = useState(editedItem.foreignCurrencyAmount ?? '');
    const [manualRateInput, setManualRateInput] = useState(editedItem.exchangeRate ?? '');
    const lastManualRateRef = useRef(null);
    const userClearedManualRateRef = useRef(false);

    // Keep manualRateInputRef in sync so debounced cross-field calculations see the latest value
    useEffect(() => { manualRateInputRef.current = manualRateInput; }, [manualRateInput]);

    // Sync local state with editedItem when it changes externally
    useEffect(() => {
        setAmountInput(editedItem.amount ?? '');
    }, [editedItem.amount]);
    useEffect(() => {
        setCurrencyAmountInput(editedItem.foreignCurrencyAmount ?? '');
    }, [editedItem.foreignCurrencyAmount]);
    // Sync manual rate input when opening modal or when entity has Manual rate
    useEffect(() => {
        if (editedItem.exchangeRateType !== Constants.ExchangeRateType.Manual) {
            setManualRateInput('');
            userClearedManualRateRef.current = false;
            return;
        }
        if (userClearedManualRateRef.current) {
            setManualRateInput('');
            return;
        }
        const rate = editedItem.exchangeRate != null && editedItem.exchangeRate !== ''
            ? String(Number(parseNumeric(editedItem.exchangeRate)))
            : (lastManualRateRef.current ?? '');
        setManualRateInput(rate);
        if (rate) lastManualRateRef.current = rate;
    }, [editedItem?.id, editedItem?.exchangeRateType, editedItem?.exchangeRate]);

    // When in Manual mode with no manual rate but Amount and ForeignCurrencyAmount present, keep lastManualRateRef for display (display value computed in render)
    useEffect(() => {
        if (editedItem.exchangeRateType !== Constants.ExchangeRateType.Manual) return;
        if (editedItem.exchangeRate != null && editedItem.exchangeRate !== '') return;
        const amount = absNumeric(editedItem.amount);
        const fc = absNumeric(editedItem.foreignCurrencyAmount);
        if (!amount || !fc || isNaN(amount) || isNaN(fc) || amount <= 0 || fc <= 0) return;
        const calculated = (amount / fc).toFixed(MANUAL_RATE_DECIMALS);
        lastManualRateRef.current = calculated;
    }, [editedItem?.exchangeRateType, editedItem?.exchangeRate, editedItem?.amount, editedItem?.foreignCurrencyAmount]);

    // File upload handlers (moved before early return to satisfy Rules of Hooks)
    const onDrop = useCallback((acceptedFiles) => {
        setIsDragging(false);
        if (acceptedFiles.length > 0) {
            const newDocuments = [...documents, ...acceptedFiles];
            setDocuments(newDocuments);
        }
    }, [documents]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDragEnter: () => setIsDragging(true),
        onDragLeave: () => setIsDragging(false),
    });

    const formattedAccountCardOptions = useMemo(() => (
        accountCardOptions?.map((option) => ({
            value: option.cardId,
            label: option.accountCaseId
                ? `${option.name} - ${option?.accountCaseId || ''}`
                : `${option.name}`,
            name: option.name,
            accountCaseId: option.accountCaseId || '',
            cardId: option.cardId,
            value1: option.value1,
            palCode: option.palCode,
            isEditable: option.isEditable,
            externalSoftwareCardNumber: option.externalSoftwareCardNumber || ''
        })) ?? []
    ), [accountCardOptions]);

    const filterAccountCardOptions = useCallback((options, { inputValue }) => {
        const input = (inputValue || '').trim();
        if (!input) return options;
        const inputLower = input.toLowerCase();
        const filtered = options.filter((option) => {
            if (option.value1 === 'AddItem') return false;
            const cardIdStr = String(option.cardId ?? option.value ?? '');
            const matchCardId = cardIdStr.toLowerCase().includes(inputLower) || cardIdStr.includes(input);
            const matchLabel = (option.label || '').toLowerCase().includes(inputLower);
            const matchName = (option.name || '').toLowerCase().includes(inputLower);
            const matchAccountCaseId = (option.accountCaseId || '').toLowerCase().includes(inputLower);
            const matchPalCode = option.palCode != null && String(option.palCode).toLowerCase().includes(inputLower);
            const extId = (option.externalSoftwareCardNumber || '').trim();
            const matchExternalSoftwareCardId = extId !== '' && extId.toLowerCase().includes(inputLower);
            return matchCardId || matchLabel || matchName || matchAccountCaseId || matchPalCode || matchExternalSoftwareCardId;
        });
        if (filtered.length === 0) {
            return [{ value1: 'AddItem', searchTerm: input, cardId: '', name: '', label: '', accountCaseId: '' }];
        }
        return filtered;
    }, []);

    // Signal that this modal owns F1–F4 (global nav shortcuts should yield).
    useEffect(() => {
        document.body.setAttribute('data-document-edit-modal-open', 'true');
        return () => {
            document.body.removeAttribute('data-document-edit-modal-open');
        };
    }, []);

    // ViewMode blocks everything. Locked / closed month: most fields stay off, but dates +
    // description remain editable. A locked document can still change registration and
    // reporting month even when the reporting month is closed.
    const isClosedMonthDisabled =
        !isRange ? isClosedReportingMonth : editedItem?.isClosedReportingMonth;
    const isEditModalDisabled =
        ViewMode ||
        editedItem?.isLocked ||
        isClosedMonthDisabled;
    // Save still allowed for locked / closed-month partial metadata edits
    const isSaveDisabledByLockOrView = ViewMode;

    useEffect(() => {
        const normalizeFunctionKey = (e) => {
            if (typeof e?.key === 'string' && /^F\d{1,2}$/.test(e.key)) return e.key;
            if (typeof e?.code === 'string' && /^F\d{1,2}$/.test(e.code)) return e.code;
            return null;
        };

        const hasNestedOverlay = () =>
            uploadModalOpen ||
            datePickerOpen ||
            projectModalOpen ||
            dupOpen ||
            dupPaymentOpen ||
            dupJournalOpen ||
            relatedPaymentsOpen ||
            isModalVisible ||
            !!editedAddAccountCard ||
            reportOpen ||
            Swal.isVisible();

        const handleKeyDown = (event) => {
            const fnKey = normalizeFunctionKey(event);

            if (event.key === 'Escape') {
                if (Swal.isVisible()) return;

                if (uploadModalOpen) {
                    setUploadModalOpen(false);
                    return;
                }
                if (datePickerOpen) {
                    closeDatePicker();
                    return;
                }
                if (projectModalOpen) {
                    handleProjectModalClose();
                    return;
                }
                if (dupOpen) {
                    closeDuplicate();
                    return;
                }
                if (dupPaymentOpen) {
                    closeDuplicatePayment();
                    return;
                }
                if (dupJournalOpen) {
                    closeDuplicateJournal();
                    return;
                }
                if (relatedPaymentsOpen) {
                    closeRelatedPayments();
                    return;
                }
                if (isModalVisible) {
                    setIsModalVisible(false);
                    return;
                }
                if (editedAddAccountCard) {
                    handleCloseItem();
                    return;
                }
                if (reportOpen) {
                    closeReport();
                    return;
                }

                handleCancel();
                return;
            }

            if (!fnKey || !['F1', 'F2', 'F3', 'F4'].includes(fnKey)) return;
            if (hasNestedOverlay()) return;

            event.preventDefault();
            event.stopPropagation();

            if (fnKey === 'F1') {
                if (isEditModalDisabled || !canProcessStatus) return;
                handleStatusRadioChange({ target: { value: 'done' } }, { forceProcess });
                return;
            }
            if (fnKey === 'F2') {
                // Locked docs can still save date/description metadata.
                if (isSaveDisabledByLockOrView) return;
                handleSaveWithInventoryCheck();
                return;
            }
            if (fnKey === 'F3') {
                if (currentIndex == null || currentIndex < 0) return;
                if (currentIndex === filteredAndSortedData.length - 1) return;
                handleNext();
                return;
            }
            if (fnKey === 'F4') {
                if (currentIndex == null || currentIndex < 0) return;
                if (currentIndex === 0) return;
                handlePrevious();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [
        handleCancel,
        uploadModalOpen,
        datePickerOpen,
        closeDatePicker,
        projectModalOpen,
        handleProjectModalClose,
        dupOpen,
        closeDuplicate,
        dupPaymentOpen,
        closeDuplicatePayment,
        dupJournalOpen,
        closeDuplicateJournal,
        relatedPaymentsOpen,
        isModalVisible,
        editedAddAccountCard,
        handleCloseItem,
        reportOpen,
        closeReport,
        isEditModalDisabled,
        isSaveDisabledByLockOrView,
        canProcessStatus,
        handleStatusRadioChange,
        forceProcess,
        handleSaveWithInventoryCheck,
        currentIndex,
        filteredAndSortedData,
        handleNext,
        handlePrevious,
    ]);

    if (!editedItem || Object.keys(editedItem).length === 0) {
        return null;
    }

    const formattedAccountCardOptions2 = accountCardOptions
        ?.map((option) => ({
            value: option.cardId,
            label: option.accountCaseId
                ? `${option.name} - ${option?.accountCaseId || ''}`
                : `${option.name}`,
            name: option.name,
            accountCaseId: option.accountCaseId || '',
            cardId: option.cardId,
            value1: option.value1,
            palCode: option.palCode,
            isEditable: option.isEditable
        }))
        .slice(1); // Removes the first item

    // Most fields stay disabled when locked or closed month.
    // Metadata (dates + description) stays editable. Reporting month stays editable on a locked document.
    const disabledAll =
        ViewMode ||
        editedItem?.isLocked ||
        isClosedMonthDisabled;
    const disabledLockedMetadataFields = ViewMode;
    const disabledReportingMonthDate = ViewMode || (isClosedMonthDisabled && !editedItem?.isLocked);
    const handleNoOptionCreditDebitClick = (searchTerm, type) => {
        const newOption = {
            ...(formattedAccountCardOptions[0] || {}),
            value1: 'AddItem',
            searchTerm: searchTerm || ''
        };
        handleCreditDebitForJournalEntry(newOption, type);
    };
    /* ─────────────────────────────────── */

    const handleCreditDebitForJournalEntry = (card, type) => {


        if (!card) {
            setEditedItem({ ...editedItem, [type]: '', });
            return;
        }

        if (card.value1 === 'AddItem') {
            setEditedAddAccountCard(true);

            // Save the old selected account card item based on the type (credit or debit)
            setOldCreditDebitSelectedAccountCardItem({
                [type]: editedItem[type], // Saves the current credit or debit account ID
            });
            setAddAccountCardType(type);
            setIsEditAccountCard(false);
            // Set the specific account type to an empty string
            setNewAccountCard({ accountCodeName: card.searchTerm || '', accountCodeId: cardIdOption })
            // setEditedItem({ ...editedItem, [type]: '', });
        } else {
            // Validation: Credit and Debit accounts must be different
            if (type === 'creditAccount' && card.cardId && card.cardId === editedItem.debitAccount) {
                setHasSameAccountError(true);
                notify.error(t('CreditAndDebitAccountsMustBeDifferent') || 'Credit and Debit accounts must be different');
                return; // Don't update the state
            }
            if (type === 'debitAccount' && card.cardId && card.cardId === editedItem.creditAccount) {
                setHasSameAccountError(true);
                notify.error(t('CreditAndDebitAccountsMustBeDifferent') || 'Credit and Debit accounts must be different');
                return; // Don't update the state
            }

            // Clear error if accounts are being changed to different values
            if (hasSameAccountError) {
                setHasSameAccountError(false);
            }

            handleChange(type, card.cardId);
        }
    };

    const handleAccountOptionChange = (newValue, accountType) => {
        const isJournalEntry = editedItem.docType === 'JournalEntries';

        if (isJournalEntry) {
            handleCreditDebitForJournalEntry(newValue, accountType);
            // if (newValue?.value1 === 'AddItem') {
            //     return;
            // }
        }

        updateAccountFields(newValue, accountType);
    };

    const handleSwapAccounts = () => {
        if (disabledAll) return;
        
        // Store current values before swapping
        const currentDebitAccount = editedItem.debitAccount;
        const currentCreditAccount = editedItem.creditAccount;
        
        // Find the account card options for both accounts
        const debitAccountOption = formattedAccountCardOptions.find(
            (option) => option.cardId === currentDebitAccount
        ) || null;
        const creditAccountOption = formattedAccountCardOptions.find(
            (option) => option.cardId === currentCreditAccount
        ) || null;
        
        // Swap both values atomically in a single state update
        setEditedItem((prev) => {
            const newDebitId = creditAccountOption ? creditAccountOption.cardId : '';
            const newCreditId = debitAccountOption ? debitAccountOption.cardId : '';
            
            const updatedItem = {
                ...prev,
                debitAccount: newDebitId,
                creditAccount: newCreditId,
            };
            
            // Handle sync logic for Incomes/Expenses
            const isIncomesDebit = prev.docType === 'Incomes';
            const isExpensesCredit = prev.docType === 'Expenses';
            
            if (isIncomesDebit && creditAccountOption) {
                updatedItem.accountCodeId = newDebitId;
                updatedItem.accountCodeName = creditAccountOption.name;
                updatedItem.accountCaseId = creditAccountOption.accountCaseId ?? '';
            } else if (isExpensesCredit && debitAccountOption) {
                updatedItem.accountCodeId = newCreditId;
                updatedItem.accountCodeName = debitAccountOption.name;
                updatedItem.accountCaseId = debitAccountOption.accountCaseId ?? '';
            }
            
            return updatedItem;
        });
        
        // Clear any same account error
        if (hasSameAccountError) {
            setHasSameAccountError(false);
        }
    };

    // Flip amount sign: positive → negative, negative → positive. The foreign amount
    // mirrors the shekel amount, so it flips with it (the payload still sends it as a
    // positive magnitude).
    const handleFlipAmount = () => {
        if (disabledAll) return;
        const raw = editedItem.amount;
        if (raw === '' || raw === null || raw === undefined) {
            notify.error(t('PleaseEnterAmount') || 'Please enter amount');
            return;
        }
        const num = parseNumeric(raw);
        if (isNaN(num)) return;
        const newAmount = (-num).toFixed(2);
        lastAmountEditRef.current = 'gross';
        handleChange('amount', newAmount);
        setAmountInput(newAmount);
        if (
            editedItem.foreignCurrencyCode != null &&
            editedItem.foreignCurrencyCode !== '' &&
            editedItem.foreignCurrencyAmount != null &&
            editedItem.foreignCurrencyAmount !== ''
        ) {
            const fcNum = parseNumeric(editedItem.foreignCurrencyAmount);
            if (!isNaN(fcNum)) {
                const newFc = (-fcNum).toFixed(2);
                handleChange('foreignCurrencyAmount', newFc);
                setCurrencyAmountInput(newFc);
            }
        }
    };

    const updateAccountFields = (newValue, accountType) => {

        
        if (newValue?.value1 === 'AddItem') {
            handleNoOptionCreditDebitClick(newValue?.searchTerm ?? '', accountType);
            return;
        }

        // Validation: Credit and Debit accounts must be different
        if (accountType === 'creditAccount' && newValue?.cardId && newValue.cardId === editedItem.debitAccount) {
            setHasSameAccountError(true);
            notify.error(t('CreditAndDebitAccountsMustBeDifferent') || 'Credit and Debit accounts must be different');
            return; // Don't update the state
        }
        if (accountType === 'debitAccount' && newValue?.cardId && newValue.cardId === editedItem.creditAccount) {
            setHasSameAccountError(true);
            notify.error(t('CreditAndDebitAccountsMustBeDifferent') || 'Credit and Debit accounts must be different');
            return; // Don't update the state
        }

        // Clear error if accounts are being changed to different values
        if (hasSameAccountError) {
            setHasSameAccountError(false);
        }

        const isIncomesDebit = editedItem.docType === 'Incomes' && accountType === 'debitAccount';
        const isExpensesCredit = editedItem.docType === 'Expenses' && accountType === 'creditAccount';
        const shouldSyncPrimaryFields = isIncomesDebit || isExpensesCredit;
        const shouldSyncAccountCaseOnly =
            !shouldSyncPrimaryFields &&
            ((journalSortCodeIsIncome && accountType === 'debitAccount') ||
                (journalSortCodeIsExpense && accountType === 'creditAccount'));

        const nextAccountId = newValue ? newValue.cardId : '';
        const nextAccountName = newValue ? newValue.name : '';
        const nextAccountCaseId = newValue ? (newValue.accountCaseId ??  '') : '';

        // Update the edited item with the new fields
        setEditedItem((prev) => {
            const updatedItem = {
                ...prev,
                [accountType]: nextAccountId,
            };

            if (shouldSyncPrimaryFields) {
                updatedItem.accountCodeId = nextAccountId;
                updatedItem.accountCodeName = nextAccountName;
                updatedItem.accountCaseId = nextAccountCaseId;
            } else if (shouldSyncAccountCaseOnly) {
                updatedItem.accountCaseId = nextAccountCaseId;
            }

            return updatedItem;
        });
    };
    const EditAccountCard = (selectedItem, type) => {

        if (!selectedItem) {
            console.warn("No account card selected for editing.");
            return;
        }
        //fetchCard(client.caseId, selectedItem.cardId, token, setNewAccountCard);


        setPalCode(selectedItem.palCode);
        setNewAccountCard({ accountCodeName: selectedItem.name || '', accountCodeId: selectedItem.cardId, accountCaseId: selectedItem.accountCaseId || '', isEditable: selectedItem.isEditable })
        setIsEditAccountCard(true);
        setEditedAddAccountCard(true);
        setAddAccountCardType(type);


    };
    const saveDuplicate = async (patchOrPatches) => {
        setLoading(true);
        try {
            // Use editedItem.docType directly - backend expects plural forms
            // editedItem.docType should be: "JournalEntries", "Incomes", "Expenses", or "PaymentAcceptances"
            // Backend expects: "JournalEntries", "Incomes", "Expenses", "PaymentAcceptances"
            if (!editedItem.docType) {
                notify.error(t('DocumentTypeNotFound') || 'Document type not found. Cannot duplicate document.');
                setLoading(false);
                closeDuplicate();
                return;
            }

            const docType = editedItem.docType; // Use docType directly as backend expects plural forms
            // "New from this" is a plain shortcut: no duplicate marker in the description and no journal group.
            const duplicateOptions = { skipDuplicateMarker: true, skipJournalGroup: true };

            // Check if patchOrPatches is an array (batch save) or single object
            const isBatchSave = Array.isArray(patchOrPatches);

            if (isBatchSave) {
                // Batch duplicate: create multiple documents
                const result = await DocumentService.BatchDuplicateDocuments(
                    docType,
                    editedItem.id,       // sourceId to duplicate from
                    patchOrPatches,      // array of patches
                    client.caseId,
                    token,
                    duplicateOptions
                );

                if (result.ok) {
                    notify.success(t("batchDuplicateSuccess") || `Successfully duplicated ${result.successCount} entries`);
                    await RefreshPage();
                } else {
                    const errorMsg = result.errors.length > 0 
                        ? `Failed: ${result.errors.length} of ${result.totalCount}` 
                        : 'Batch duplicate failed';
                    notify.error(`${t("batchDuplicateError") || 'Batch duplicate error'}: ${errorMsg}`);
                }
            } else {
                // Single duplicate
                const res = await DocumentService.DuplicateDocument(
                    docType,             // "Expenses" | "Incomes" | "JournalEntries" | "PaymentAcceptances"
                    editedItem.id,       // sourceId
                    patchOrPatches,      // may be {} or null
                    client.caseId,
                    token,
                    duplicateOptions
                );

                if (res.ok) {
                    notify.success(t("documentDuplicateSuccess"));
                    await RefreshPage();
                } else {
                    const msg = await res.text();
                    notify.error(`${t("documentDuplicateError")}: ${msg}`);
                }
            }
        } catch (err) {
            notify.error(`${t("documentDuplicateError")}: ${err.message}`);
        } finally {
            setLoading(false);
            closeDuplicate();
        }
    };

    // Transform Expenses/Incomes to PaymentAcceptance format
    // When opened from PCN (or other flows), sourceItem may not have status; derive from client.documentIds so SkipAddToProcessed is set correctly.
    // Build the duplicate indicator prefix: upload number + the source report month beside it (e.g. "שוכפל מ 44 06/2026")
    const buildDuplicatePrefix = (uploadNumber, reportMonthDate) => {
        const reportMonthLabel = reportMonthDate && dayjs(reportMonthDate).isValid()
            ? dayjs(reportMonthDate).format('MM/YYYY')
            : '';
        return reportMonthLabel
            ? `שוכפל מ ${uploadNumber} ${reportMonthLabel}`
            : `שוכפל מ ${uploadNumber}`;
    };

    const transformToPaymentAcceptance = (sourceItem, docType, clientForStatus) => {
        const sourceUploadNumber = sourceItem.uploadNumber;
        const sourceReportingMonthDate = sourceItem.reportingMonthDate || sourceItem.documentDate || null;
        let description = sourceItem.description || '';
        
        // Check if description already contains duplicate indicator
        const hasDuplicateIndicator = description.includes('שוכפל מ');
        
        if (sourceUploadNumber && !hasDuplicateIndicator) {
            const duplicatePrefix = buildDuplicatePrefix(sourceUploadNumber, sourceReportingMonthDate);
            if (description) {
                description = `${duplicatePrefix} – ${description}`;
            } else {
                description = duplicatePrefix;
            }
        }

        // Determine paymentAcceptanceType based on docType
        const paymentAcceptanceType = docType === 'Incomes' ? 'תקבול' : 'תשלום';

        // Derive status when missing (e.g. when EditModal is opened from PCN): "done" if doc id is NOT in processed list
        let status = sourceItem.status;
        if (status == null && clientForStatus?.documentIds) {
            const docId = sourceItem.id ?? sourceItem.Id;
            if (docType === 'Incomes' && clientForStatus.documentIds.incomes) {
                status = clientForStatus.documentIds.incomes.includes(docId) ? 'notDone' : 'done';
            } else if (docType === 'Expenses' && clientForStatus.documentIds.expenses) {
                status = clientForStatus.documentIds.expenses.includes(docId) ? 'notDone' : 'done';
            }
        }

        return {
            ...sourceItem,
            id: undefined,
            uploadNumber: undefined,
            uploadDate: new Date(),
            description: description,
            sourceUploadNumber: sourceUploadNumber,
            sourceReportingMonthDate: sourceReportingMonthDate,
            // Persist the originating Income/Expense so the created PaymentAcceptance is linked to it.
            sourceDocumentId: sourceItem.id ?? sourceItem.Id,
            sourceDocType: docType,
            paymentAcceptanceType: paymentAcceptanceType,
            date: sourceItem.documentDate || sourceItem.date || new Date(),
            reportingMonthDate: sourceItem.reportingMonthDate || sourceItem.documentDate || new Date(),
            registrationMonthDate: sourceItem.registrationMonthDate || sourceItem.documentDate || new Date(),
            receiptDocumentUrl: sourceItem.documentUrl || null,
            checks: [],
            creditCards: [],
            cash: [],
            bankTransactions: [],
            status: status,
        };
    };

    // Save handler for PaymentAcceptance duplicate
    const handleSavePaymentAcceptanceDuplicate = async (duplicatedItem) => {
        setLoading(true);
        try {
            // Add duplicate indicator to description if not already present
            const sourceUploadNumber = duplicatedItem.sourceUploadNumber || duplicatedItem.uploadNumber;
            const sourceReportingMonthDate = duplicatedItem.sourceReportingMonthDate || duplicatedItem.reportingMonthDate || null;
            let description = duplicatedItem.description || '';
            
            const hasDuplicateIndicator = description.includes('שוכפל מ');
            
            if (sourceUploadNumber && !hasDuplicateIndicator) {
                const duplicatePrefix = buildDuplicatePrefix(sourceUploadNumber, sourceReportingMonthDate);
                if (description) {
                    description = `${duplicatePrefix} – ${description}`;
                } else {
                    description = duplicatePrefix;
                }
            }

            const formData = new FormData();
            formData.append("CaseId", client.caseId);
            formData.append("AccountantId", client.accountantId || '');
            formData.append("DocumentFileType", 'Checks');
            formData.append("PaymentAcceptanceType", duplicatedItem.paymentAcceptanceType);
            formData.append("AccountCodeName", duplicatedItem.accountCodeName || '');
            formData.append("AccountCodeId", duplicatedItem.accountCodeId || '');
            formData.append("AccountCaseId", duplicatedItem.accountCaseId || '');
            formData.append("WithholdingTax", (duplicatedItem.withholdingTax || 0).toString());
            formData.append("Date", duplicatedItem.date ? new Date(duplicatedItem.date).toISOString() : new Date().toISOString());
            formData.append("reportingMonthDate", duplicatedItem.reportingMonthDate ? new Date(duplicatedItem.reportingMonthDate).toISOString() : new Date().toISOString());
            formData.append("registrationMonthDate", duplicatedItem.registrationMonthDate ? new Date(duplicatedItem.registrationMonthDate).toISOString() : new Date().toISOString());
            if (duplicatedItem.foreignCurrencyCode) {
                formData.append("foreignCurrencyCode", duplicatedItem.foreignCurrencyCode);
            }
            formData.append('Reference', duplicatedItem.reference || '');
            formData.append('Reference2', '');
            formData.append('Description', description);

            // Link the new PaymentAcceptance back to its source Income/Expense.
            if (duplicatedItem.sourceDocumentId) {
                if (duplicatedItem.sourceDocType === 'Incomes') {
                    formData.append('RelatedIncomeId', duplicatedItem.sourceDocumentId);
                } else if (duplicatedItem.sourceDocType === 'Expenses') {
                    formData.append('RelatedExpenseId', duplicatedItem.sourceDocumentId);
                }
            }

            // Carry over receipt document URL from source Income/Expense (documentUrl from byDateRange) so the duplicate keeps the same document
            const receiptDocumentUrl = duplicatedItem.receiptDocumentUrl || duplicatedItem.documentUrl;
            if (receiptDocumentUrl) {
                formData.append('ReceiptDocumentUrl', receiptDocumentUrl);
            }

            // A payment/acceptance added from the edit modal is always created as processed,
            // so it is not added to ClientDocumentCount (displays as "done").
            formData.append('SkipAddToProcessed', 'true');

            // Append checks, credit cards, cash, and bank transactions to formData (API requires Checks to be present; use [] when empty)
            const checks = (duplicatedItem.checks && duplicatedItem.checks.length > 0)
                ? duplicatedItem.checks.map(check => ({
                    bankName: check.bankName || '',
                    paymentDate: new Date(check.paymentDate ? check.paymentDate : Date.now()).toISOString(),
                    description: check.description || '',
                    amount: check.amount || 0,
                    reference2: check.reference2 || '',
                    bankCode: check.bankCode || '',
                    bankBranchCode: check.bankBranchCode || '',
                    supplierAccountNumber: check.supplierAccountNumber || '',
                    documentUrl: check.documentUrl || "",
                    creditAccount: check.creditAccount || '',
                    debitAccount: check.debitAccount || '',
                    foreignCurrencyAmount: check.foreignCurrencyAmount || null
                }))
                : [];
            formData.append('Checks', JSON.stringify(checks));

            const creditCards = (duplicatedItem.creditCards && duplicatedItem.creditCards.length > 0)
                ? duplicatedItem.creditCards.map(card => ({
                    paymentDate: new Date(card.paymentDate ? card.paymentDate : Date.now()).toISOString(),
                    description: card.description || '',
                    amount: card.amount || 0,
                    reference2: card.reference2 || '',
                    creditAccount: card.creditAccount || '',
                    debitAccount: card.debitAccount || '',
                    foreignCurrencyAmount: card.foreignCurrencyAmount || null
                }))
                : [];
            formData.append('CreditCards', JSON.stringify(creditCards));

            const cash = (duplicatedItem.cash && duplicatedItem.cash.length > 0)
                ? duplicatedItem.cash.map(cashItem => ({
                    paymentDate: new Date(cashItem.paymentDate ? cashItem.paymentDate : Date.now()).toISOString(),
                    description: cashItem.description || '',
                    amount: cashItem.amount || 0,
                    reference2: cashItem.reference2 || '',
                    creditAccount: cashItem.creditAccount || '',
                    debitAccount: cashItem.debitAccount || '',
                    foreignCurrencyAmount: cashItem.foreignCurrencyAmount || null
                }))
                : [];
            formData.append('Cash', JSON.stringify(cash));

            const bankTransactions = (duplicatedItem.bankTransactions && duplicatedItem.bankTransactions.length > 0)
                ? duplicatedItem.bankTransactions.map(transaction => ({
                    paymentDate: new Date(transaction.paymentDate ? transaction.paymentDate : Date.now()).toISOString(),
                    description: transaction.description || '',
                    amount: transaction.amount || 0,
                    reference2: transaction.reference2 || '',
                    creditAccount: transaction.creditAccount || '',
                    debitAccount: transaction.debitAccount || '',
                    foreignCurrencyAmount: transaction.foreignCurrencyAmount || null
                }))
                : [];
            formData.append('BankTransactions', JSON.stringify(bankTransactions));

            const offsets = (duplicatedItem.offsets && duplicatedItem.offsets.length > 0)
                ? duplicatedItem.offsets.map(offset => ({
                    paymentDate: new Date(offset.paymentDate ? offset.paymentDate : Date.now()).toISOString(),
                    description: offset.description || '',
                    amount: offset.amount || 0,
                    reference2: offset.reference2 || '',
                    creditAccount: offset.creditAccount || '',
                    debitAccount: offset.debitAccount || '',
                    foreignCurrencyAmount: offset.foreignCurrencyAmount || null
                }))
                : [];
            formData.append('Offsets', JSON.stringify(offsets));

            const response = await DocumentService.UploadDocument('PaymentAcceptances', formData, token);

            if (response.ok) {
                notify.success(t('InvoiceReceiptCreated') || 'Payment acceptance created successfully');
                await RefreshPage();
            } else {
                const errorText = await response.text();
                console.error(t('FailedToUploadDocument'), errorText);
                notify.error(t('InvoiceReceiptDidNotCreated') || 'Failed to create payment acceptance', { errorText: errorText });
            }
        } catch (error) {
            console.error(t('InvoiceReceiptDidNotCreated'), error);
            notify.error(t('InvoiceReceiptDidNotCreated') || 'Failed to create payment acceptance', { errorText: error.message });
        } finally {
            setLoading(false);
            closeDuplicatePayment();
        }
    };

    const handleUploadDocuments = async () => {
        
        if (documents.length === 0) {
            notify.warning(t('PleaseUploadAtLeastOneDocument'));
            return;
        }

        try {
            setLoading(true);

            // For JournalEntries, Incomes, and Expenses, use the new upload/merge endpoint
            if (effectiveDocType === 'JournalEntries' || effectiveDocType === 'Incomes' || effectiveDocType === 'Expenses') {
                const formData = new FormData();

                // Append all document fields
                formData.append('Id', editedItem.id);
                formData.append('CaseId', editedItem.caseId);
                formData.append('DocumentType', effectiveDocType === 'JournalEntries' ? 'JournalEntry' : effectiveDocType === 'Incomes' ? 'Income' : 'Expense');
                formData.append('SortCodeId', editedItem.sortCodeId || '');
                formData.append('Description', editedItem.description || '');
                formData.append('Reference', editedItem.reference || '');
                formData.append('Reference2', editedItem.reference2 || '');
                formData.append('Amount', editedItem.amount || 0);
                // Use ?? so an explicit 0% VAT is preserved (0 || 100 would wrongly become 100).
                formData.append('VAT', editedItem.vat ?? 100);
                formData.append('DocumentDate', new Date(editedItem.documentDate).toISOString());
                formData.append('UploadDate', new Date(editedItem.uploadDate).toISOString());
                formData.append('ReportingMonthDate', new Date(editedItem.reportingMonthDate).toISOString());
                formData.append('RegistrationMonthDate', editedItem.registrationMonthDate ? new Date(editedItem.registrationMonthDate).toISOString() : '');
                formData.append('CreditAccount', editedItem.creditAccount || '');
                formData.append('DebitAccount', editedItem.debitAccount || '');
                formData.append('UploadNumber', editedItem.uploadNumber || 0);
                formData.append('AccountCaseId', editedItem.accountCaseId || '');
                formData.append('ConfirmationNumber', editedItem.confirmationNumber || '');
                formData.append('AccountCodeId', editedItem.accountCodeId || '');
                formData.append('AccountCodeName', editedItem.accountCodeName || '');

                // Foreign currency fields. The amount goes out as a positive magnitude so the
                // server's conversion back to shekels keeps the right sign.
                if (editedItem.foreignCurrencyAmount !== null && editedItem.foreignCurrencyAmount !== undefined) {
                    const fcAbs = absNumeric(editedItem.foreignCurrencyAmount);
                    formData.append('ForeignCurrencyAmount', isNaN(fcAbs) ? editedItem.foreignCurrencyAmount : fcAbs);
                }
                if (editedItem.foreignCurrencyCode !== null && editedItem.foreignCurrencyCode !== undefined) {
                    formData.append('ForeignCurrencyCode', editedItem.foreignCurrencyCode);
                }

                // Append all documents
                documents.forEach((doc, index) => {
                    formData.append('Documents', doc);
                });

                const response = await DocumentService.uploadOrMergeDocument(formData, token);

                if (response.ok) {
                    const result = await response.json();

                    // Update the editedItem with the new documentUrl (presigned URL)
                    // This will cause the iframe to show the new/merged document

                    if (result.data && result.data.presignedUrl) {
                        handleChange('documentUrl', result.data.presignedUrl);
                    }

                    // Close the upload modal
                    setUploadModalOpen(false);
                    setDocuments([]);

                    // Show success toast
                    notify.success(t('documentUploadedSuccess'));

                    // Note: NOT calling RefreshPage() to keep EditModal open
                    // The iframe will automatically reload with the new presigned URL
                } else {
                    const errorText = await response.text();
                    notify.error(t('ErrorUploadingFiles') + ': ' + errorText);
                }
            } else {
                notify.error(t('ErrorUploadingFiles'));
            }
        } catch (error) {
            console.error('Error uploading documents:', error);
            notify.error(t('ErrorUploadingFiles'));
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveDocument = (index) => {
        setDocuments(documents.filter((_, i) => i !== index));
    };

    const handleReportingMonthDateChange = async (date) => {
        if (date) {
            // Journal entries may be posted into a closed month (unlike incomes/expenses).
            // Locked documents can still move reporting month.
            const isJournalEntry =
                editedItem?.docType === 'JournalEntries' ||
                effectiveDocType === 'JournalEntries';
            if (!isJournalEntry && !editedItem?.isLocked) {
                const newDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 10));
                const response = await ClientService.CheckIfMonthClosed(client.caseId, newDate, token);
                if (response.ok) {
                    const isClosedReportingMonth = await response.json();
                    if (isClosedReportingMonth) {
                        notify.info(t('MonthReportAlreadyClosedTryAgain'));
                        // Force the date picker to re-render with the old value
                        setReportingMonthDatePickerKey(prev => prev + 1);
                        return; // Don't update the date
                    }
                }
            }
            handleChange('reportingMonthDate', date);
        }
    };

    const handleDeleteDocumentUrl = async () => {
        if (disabledAll) return;
        try {
            // Show confirmation dialog
            const result = await Swal.fire({
                title: t('AreYouSure'),
                text: t('DeleteDocumentUrlConfirmation'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('YesDeleteIt'),
                cancelButtonText: t('NoCancel'),
                customClass: {
                    container: 'swal-high-zindex'
                }
            });

            if (result.isConfirmed) {
                if (effectiveDocType !== 'JournalEntries' && effectiveDocType !== 'Incomes' && effectiveDocType !== 'Expenses') {
                    notify.error(t('ErrorDeletingDocumentUrl'));
                    return;
                }

                setLoading(true);

                // Determine document type
                const docType = effectiveDocType === 'JournalEntries' ? 'JournalEntry' : effectiveDocType === 'Incomes' ? 'Income' : 'Expense';

                const response = await DocumentService.deleteDocumentUrl(
                    editedItem.id,
                    editedItem.caseId,
                    docType,
                    token
                );

                if (response.ok) {
                    const result = await response.json();

                    handleChange('documentUrl', null);
                    await RefreshPage();
                    setShowPdfIframe(false);
                    setDocuments([]);


                    notify.success(t('DocumentUrlDeletedSuccessfully'));
                } else {
                    const errorText = await response.text();
                    notify.error(t('ErrorDeletingDocumentUrl') + ': ' + errorText);
                }
            }
        } catch (error) {
            console.error('Error deleting document URL:', error);
            notify.error(t('ErrorDeletingDocumentUrl'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <Portal container={container}>
            <Draggable
                handle=".handle"
                cancel=".edit-modal-no-drag, .edit-modal-close-button, .edit-modal-size-controls, button, .MuiButtonBase-root, .MuiIconButton-root"
                nodeRef={nodeRef}
            >
                <div
                    ref={nodeRef}
                    className={`edit-modal-shell size-${modalSizeMode}${showPdfIframe ? ' with-pdf' : ''}${fitDensity ? ` fit-d${fitDensity}` : ''}`}
                    style={{
                        padding: '8px',
                        flexDirection: 'row',
                        direction: direction,
                        ...(modalSizeMode === 'free'
                            ? {
                                top: freeSize.top,
                                left: freeSize.left,
                                width: freeSize.width,
                                height: freeSize.height,
                            }
                            : {}),
                    }}
                >
                    {/* Form Content */}
                    <div
                        ref={formPaneRef}
                        className="edit-modal-form-pane"
                    >
                        <div className="edit-modal-header">
                            <Tooltip title={`${t('Exit')} (ESC)`}>
                                <button
                                    type="button"
                                    className="edit-modal-close-button edit-modal-fn-key-wrap edit-modal-no-drag"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleCancel();
                                    }}
                                    aria-label="ESC"
                                >
                                    <span className="edit-modal-fn-key-badge">ESC</span>
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                </button>
                            </Tooltip>

                            <div className="handle edit-modal-drag-handle">
                                <div className="edit-title">
                                    {t('Document')}    {editedItem.uploadNumber}
                                </div>
                            </div>

                            <div className="edit-modal-header-actions edit-modal-no-drag">
                                <div className="edit-modal-size-controls">
                                    <Tooltip title={t('Compact') || 'Compact'}>
                                        <button
                                            type="button"
                                            className={`edit-modal-size-btn ${modalSizeMode === 'compact' ? 'active' : ''}`}
                                            onClick={() => applyModalSizeMode('compact')}
                                        >
                                            S
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={t('Medium') || 'Medium'}>
                                        <button
                                            type="button"
                                            className={`edit-modal-size-btn ${modalSizeMode === 'medium' ? 'active' : ''}`}
                                            onClick={() => applyModalSizeMode('medium')}
                                        >
                                            M
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={t('Fullscreen') || 'Full'}>
                                        <button
                                            type="button"
                                            className={`edit-modal-size-btn ${modalSizeMode === 'expanded' ? 'active' : ''}`}
                                            onClick={() => applyModalSizeMode('expanded')}
                                        >
                                            L
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={t('FreeResize') || 'Free resize'}>
                                        <button
                                            type="button"
                                            className={`edit-modal-size-btn ${modalSizeMode === 'free' ? 'active' : ''}`}
                                            onClick={() => applyModalSizeMode('free')}
                                        >
                                            <OpenWithIcon sx={{ fontSize: 14 }} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip title={modalSizeMode === 'expanded' ? (t('Collapse') || 'Collapse') : (t('Expand') || 'Expand')}>
                                        <button
                                            type="button"
                                            className="edit-modal-size-icon-btn"
                                            onClick={handleToggleExpandCollapse}
                                        >
                                            {modalSizeMode === 'expanded'
                                                ? <FullscreenExitIcon sx={{ fontSize: 18 }} />
                                                : <FullscreenIcon sx={{ fontSize: 18 }} />}
                                        </button>
                                    </Tooltip>
                                </div>

                                {editedItem.documentUrl && !disabledAll && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleDeleteDocumentUrl}
                                        sx={{
                                            backgroundColor: '#F44336',
                                            color: 'white',
                                            fontSize: 'var(--xtram-text-xs, 0.75rem)',
                                            fontWeight: 'bold',
                                            padding: '4px 12px',
                                            '&:hover': {
                                                backgroundColor: '#d32f2f'
                                            }
                                        }}
                                    >
                                        {t('DeleteDocument')}
                                    </Button>
                                )}

                                <IconButton onClick={handleToggleIframe}>
                                    {showPdfIframe ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </div>
                        </div>
                        {/* Modal Content */}
                        <FieldBoxDotProvider
                            fields={fieldBoxesShown ? visibleFieldBoxes : null}
                            fieldOrder={fieldBoxOrder}
                            fieldLabels={fieldBoxLabels}
                            activeField={activeOcrField}
                            onFieldClick={handleFieldBoxClick}
                        >
                        <div
                            className="edit-modal-form-scroll"
                            onFocusCapture={fieldPositions ? handleOcrFieldFocus : undefined}
                            onBlurCapture={fieldPositions ? handleOcrFieldBlur : undefined}
                        >
                        <div className={`form-container ${direction}`} style={{ gap: 'var(--edit-row-gap, 4px)' }}>
                            {/* Row 1 */}
                            <div className="form-row">
                                {/* Sort Code ID */}
                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title">
                                        <div className="edit-form-row-item-title" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {t('Code ID')}
                                            <span className="required-asterisk">*</span>

                                            {/* Ledger icon → Sort-Code report */}
                                            {editedItem.sortCodeId && (
                                                <Tooltip title={t('goToSortCodeDetails')}>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0.2 }}
                                                        onClick={handleSelectSortCode(editedItem.sortCodeId)}
                                                    >
                                                        <img src={ledger} alt="Ledger" style={{ width: 18, height: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>

                                        {asset ? (
                                            <Tooltip title={asset ? t('ClickToEditAsset') : ''} arrow>
                                                <div
                                                    className="asset-link"
                                                    onClick={() => {
                                                        if (asset && typeof assetClicked === 'function') {
                                                            assetClicked(asset, editedItem);
                                                        }
                                                    }}
                                                    style={{ color: 'blue', cursor: 'pointer', marginTop: '4px' }}
                                                >
                                                    {`${t('Asset')}: ${asset.name}`}
                                                </div>
                                            </Tooltip>
                                        ) : null}
                                    </div>

                                    {/* Tooltip and red highlighting only if there's an asset */}
                                    <Autocomplete
                                        className={journalFieldStyles.combo}
                                        size="small"
                                        style={
                                            asset
                                                ? { border: '3px solid blue', borderRadius: 10 }
                                                : {}
                                        }
                                        options={sortCodeIdOptions}
                                        getOptionLabel={(option) =>
                                            `${option.sortCodeId} - ${option.sortCodeName}`
                                        }
                                        value={
                                            sortCodeIdOptions.find(
                                                (option) => option.sortCodeId === editedItem.sortCodeId
                                            ) || null
                                        }
                                        onChange={(event, newValue) => {
                                            const newSortCodeId = newValue ? newValue.sortCodeId : '';
                                            const manualVat = vatOverrideRef.current;
                                            handleChange('sortCodeId', newSortCodeId);
                                            // Parent copies the sort code VAT onto the transaction (0 for a 0% sort code).
                                            // Keep the rate the user already chose, including 100%.
                                            if (manualVat != null && typeof setEditedItem === 'function') {
                                                setEditedItem((prev) => ({
                                                    ...prev,
                                                    sortCodeId: newSortCodeId,
                                                    vat: manualVat,
                                                }));
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('Code ID')}
                                                variant="standard"
                                                className="edit-style-input"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    ...inputPropsStyle,
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    ...inputStyle,
                                                }}
                                                required
                                            />
                                        )}
                                        disableListWrap
                                        ListboxComponent={FixedSizeVirtualizedListbox}
                                        ListboxProps={{ direction, itemHeight: 36, listHeight: 240 }}
                                        PopperComponent={renderComboPopper}
                                        disabled={disabledAll}
                                        fullWidth
                                    />
                                </div>



                                {/* Month Date Fields Combined */}
                                <div className="edit-form-row-item">

                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <div className="edit-form-row-item-title">
                                                {t('MonthReportDate')}
                                            </div>
                                            <div className="edit-date-field">
                                            <CustomMonthDatePicker
                                                key={reportingMonthDatePickerKey}
                                                date={
                                                    editedItem.reportingMonthDate
                                                        ? dayjs(new Date(editedItem.reportingMonthDate))
                                                        : null
                                                }
                                                onDateChange={(date) => {
                                                    if (date) {
                                                        handleReportingMonthDateChange(date);
                                                    }
                                                }}
                                                height="40px"
                                                disabled={disabledReportingMonthDate}
                                            />
                                            </div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div className="edit-form-row-item-title">
                                                {t('RegistrationMonthDate')}
                                            </div>
                                            <div className="edit-date-field">
                                            <CustomMonthDatePicker
                                                date={
                                                    editedItem.registrationMonthDate
                                                        ? dayjs(new Date(editedItem.registrationMonthDate))
                                                        : null
                                                }
                                                onDateChange={(date) => {
                                                    if (date) {
                                                        handleChange('registrationMonthDate', date);
                                                    }
                                                }}
                                                height="40px"
                                                disabled={disabledLockedMetadataFields}
                                            />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Row 2: Debit Account | Document Date + Upload Date */}
                            <div className="form-row">
                                {/* Debit Account */}
                                <div
                                    className="edit-form-row-item"
                                    style={{ flex: 1, minWidth: 0 }}
                                    data-ocr-field={effectiveDocType === 'Incomes' ? 'accountCodeName' : undefined}
                                >
                                    <div
                                        className="edit-form-row-item-title"
                                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                                    >
                                        {t('Debit Account')}
                                        <span className="required-asterisk">*</span>
                                        {effectiveDocType === 'Incomes' && <FieldBoxDot field="accountCodeName" />}

                                        {editedItem.debitAccount && (
                                            <Tooltip title={t('goToAccountCardDetails')}>
                                                <IconButton
                                                    size="small"
                                                    sx={{ p: 0.2 }}
                                                    onClick={handleNavigateToDashboard(editedItem.debitAccount)}
                                                >
                                                    <img src={ledger} alt="Ledger" style={{ width: 18, height: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                    <Autocomplete
                                        className={journalFieldStyles.combo}
                                        size="small"
                                        key={`debit-${editedItem.id}`}
                                        options={formattedAccountCardOptions}
                                        getOptionLabel={(option) => {
                                            if (option?.value1 === 'AddItem') {
                                                return `${t('AddAccountCard')} "${option.searchTerm || ''}"`;
                                            }
                                            if (option?.accountCaseId) {
                                                return `${option.cardId} - ${option.name} - ${option.accountCaseId}`;
                                            }
                                            return `${option.cardId} - ${option.name}`;
                                        }}
                                        isOptionEqualToValue={(option, value) => String(option.cardId) === String(value.cardId)}
                                        filterOptions={filterAccountCardOptions}
                                        value={
                                            formattedAccountCardOptions.find((option) => String(option.cardId) === String(editedItem.debitAccount)) || null
                                        }
                                        onChange={(event, newValue) => {
                                            handleAccountOptionChange(newValue, 'debitAccount');
                                        }}
                                        onInputChange={(event, inputValue) => {
                                            setCreditDebitSearchTerm(inputValue);
                                        }}
                                        noOptionsText={
                                            <button
                                                type="button"
                                                className="edit-add-account-empty"
                                                onClick={() =>
                                                    handleNoOptionCreditDebitClick(creditDebitSearchTerm, 'debitAccount')
                                                }
                                            >
                                                <AddAccountCardOptionContent
                                                    label={t('AddAccountCard')}
                                                    searchTerm={creditDebitSearchTerm}
                                                />
                                            </button>
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('Debit Account')}
                                                variant="standard"
                                                className={`edit-style-input${hasSameAccountError ? ' edit-style-input-required' : ''}`}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    ...inputPropsStyle,
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    ...inputStyle,
                                                }}
                                                required
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            if (option?.value1 === 'AddItem') {
                                                const { key, className, ...optionProps } = props;
                                                return (
                                                    <li
                                                        key={key}
                                                        {...optionProps}
                                                        className={`${className || ''} edit-add-account-option`}
                                                    >
                                                        <AddAccountCardOptionContent
                                                            label={t('AddAccountCard')}
                                                            searchTerm={option.searchTerm}
                                                        />
                                                    </li>
                                                );
                                            }
                                            return (
                                                <Tooltip
                                                    key={props.key}
                                                    title={option.isEditable ? t("RightClickToEdit") : t("NotEditable")}
                                                    placement="top"
                                                >
                                                    <li
                                                        {...props}
                                                        onContextMenu={(event) => {
                                                            event.preventDefault();
                                                            EditAccountCard(option, 'debitAccount');
                                                        }}
                                                    >
                                                        {option.accountCaseId
                                                            ? `${option?.cardId ?? ''} - ${option.name} - ${option.accountCaseId}`
                                                            : `${option?.cardId ?? ''} - ${option.name}`
                                                        }
                                                    </li>
                                                </Tooltip>
                                            );
                                        }}
                                        disableListWrap
                                        ListboxComponent={FixedSizeVirtualizedListbox}
                                        ListboxProps={{ direction, itemHeight: 40, listHeight: 240 }}
                                        PopperComponent={renderComboPopper}
                                        disabled={disabledAll || editedItem.sortCodeId === caseSystemSettings.sortCodeCredit}
                                        fullWidth
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                backgroundColor: editedItem.sortCodeId === caseSystemSettings.sortCodeCredit ? '#a9a9a9' : '#f5f5f6',
                                                cursor: editedItem.sortCodeId === caseSystemSettings.sortCodeCredit ? 'not-allowed' : 'pointer',
                                            },
                                            '& .Mui-disabled': {
                                                color: '#A9A9A9',
                                            },
                                        }}
                                    />
                                </div>

                                <div className="edit-form-row-item" style={{ flex: 1, minWidth: 0 }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateAdapterLocale}>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <div style={{ flex: 1, minWidth: 0 }} data-ocr-field="documentDate">
                                                <div className="edit-form-row-item-title">
                                                    {t('Document Date')}
                                                    <span className="required-asterisk">*</span>
                                                    <FieldBoxDot field="documentDate" />
                                                </div>
                                                <DatePicker
                                                    value={toPickerDate(editedItem.documentDate)}
                                                    onChange={(d) => {
                                                        if (d) {
                                                            applyDocumentDateChange(d);
                                                        }
                                                    }}
                                                    format="dd/MM/yyyy"
                                                    disabled={disabledLockedMetadataFields}
                                                    slotProps={{
                                                        textField: {
                                                            variant: 'standard',
                                                            fullWidth: true,
                                                            className: `edit-style-input${dateError ? ' edit-style-input-required' : ''}`,
                                                            InputProps: inputPropsStyle,
                                                            required: true,
                                                            error: dateError,
                                                            onKeyDown: (e) => handleEditModalDateEnter(e, 'documentDate'),
                                                        },
                                                        popper: {
                                                            sx: { zIndex: 1700 },
                                                        },
                                                    }}
                                                />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="edit-form-row-item-title">
                                                    {t('Upload Date')}
                                                    <span className="required-asterisk">*</span>
                                                </div>
                                                <DatePicker
                                                    value={toPickerDate(editedItem.uploadDate)}
                                                    onChange={(d) => {
                                                        if (d) {
                                                            handleChange('uploadDate', d);
                                                        }
                                                    }}
                                                    format="dd/MM/yyyy"
                                                    disabled={disabledLockedMetadataFields}
                                                    slotProps={{
                                                        textField: {
                                                            variant: 'standard',
                                                            fullWidth: true,
                                                            className: 'edit-style-input',
                                                            InputProps: inputPropsStyle,
                                                            required: true,
                                                            onKeyDown: (e) => handleEditModalDateEnter(e, 'uploadDate'),
                                                        },
                                                        popper: {
                                                            sx: { zIndex: 1700 },
                                                        },
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </LocalizationProvider>
                                </div>

                            </div>

                            {/* Row 3: Credit Account + Projects (when applicable) */}
                            <div className="form-row">
                                <div
                                    className="edit-form-row-item"
                                    style={{
                                        flex: showProjectAssignRow ? '1 1 0' : '1 1 100%',
                                        minWidth: 0,
                                    }}
                                    data-ocr-field={effectiveDocType === 'Expenses' ? 'accountCodeName' : undefined}
                                >
                                    <div
                                        className="edit-form-row-item-title"
                                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                                    >
                                        {t('Credit Account')}
                                        <span className="required-asterisk">*</span>
                                        {effectiveDocType === 'Expenses' && <FieldBoxDot field="accountCodeName" />}

                                        {editedItem.creditAccount && (
                                            <Tooltip title={t('goToAccountCardDetails')}>
                                                <IconButton
                                                    size="small"
                                                    sx={{ p: 0.2 }}
                                                    onClick={handleNavigateToDashboard(editedItem.creditAccount)}
                                                >
                                                    <img src={ledger} alt="Ledger" style={{ width: 18, height: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={t('SwapAccounts') || 'Swap Debit and Credit Accounts'}>
                                            <IconButton
                                                size="small"
                                                sx={{ p: 0.2 }}
                                                onClick={handleSwapAccounts}
                                                disabled={disabledAll}
                                            >
                                                <SwapVertIcon sx={{ fontSize: 18, color: '#304FFF' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                    <Autocomplete
                                        className={journalFieldStyles.combo}
                                        size="small"
                                        key={`credit-${editedItem.id}`}
                                        options={formattedAccountCardOptions}
                                        getOptionLabel={(option) => {
                                            if (option?.value1 === 'AddItem') {
                                                return `${t('AddAccountCard')} "${option.searchTerm || ''}"`;
                                            }
                                            if (option?.accountCaseId) {
                                                return `${option.cardId} - ${option.name} - ${option.accountCaseId}`;
                                            }
                                            return `${option.cardId} - ${option.name}`;
                                        }}
                                        isOptionEqualToValue={(option, value) => String(option.cardId) === String(value.cardId)}
                                        filterOptions={filterAccountCardOptions}
                                        value={
                                            formattedAccountCardOptions.find((option) => String(option.cardId) === String(editedItem.creditAccount)) || null
                                        }
                                        onChange={(event, newValue) => {
                                            handleAccountOptionChange(newValue, 'creditAccount');
                                        }}
                                        onInputChange={(event, inputValue) => {
                                            setCreditDebitSearchTerm(inputValue);
                                        }}
                                        noOptionsText={
                                            <button
                                                type="button"
                                                className="edit-add-account-empty"
                                                onClick={() =>
                                                    handleNoOptionCreditDebitClick(creditDebitSearchTerm, 'creditAccount')
                                                }
                                            >
                                                <AddAccountCardOptionContent
                                                    label={t('AddAccountCard')}
                                                    searchTerm={creditDebitSearchTerm}
                                                />
                                            </button>
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('Credit Account')}
                                                variant="standard"
                                                className={`edit-style-input${hasSameAccountError ? ' edit-style-input-required' : ''}`}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    ...inputPropsStyle,
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    ...inputStyle,
                                                }}
                                                required
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            if (option?.value1 === 'AddItem') {
                                                const { key, className, ...optionProps } = props;
                                                return (
                                                    <li
                                                        key={key}
                                                        {...optionProps}
                                                        className={`${className || ''} edit-add-account-option`}
                                                    >
                                                        <AddAccountCardOptionContent
                                                            label={t('AddAccountCard')}
                                                            searchTerm={option.searchTerm}
                                                        />
                                                    </li>
                                                );
                                            }
                                            return (
                                                <Tooltip
                                                    key={props.key}
                                                    title={option.isEditable ? t("RightClickToEdit") : t("NotEditable")}
                                                    placement="top"
                                                >
                                                    <li
                                                        {...props}
                                                        onContextMenu={(event) => {
                                                            event.preventDefault();
                                                            EditAccountCard(option, 'creditAccount');
                                                        }}
                                                    >
                                                        {option.accountCaseId
                                                            ? `${option?.cardId ?? ''} - ${option.name} - ${option.accountCaseId}`
                                                            : `${option?.cardId ?? ''} - ${option.name}`
                                                        }
                                                    </li>
                                                </Tooltip>
                                            );
                                        }}
                                        disableListWrap
                                        ListboxComponent={FixedSizeVirtualizedListbox}
                                        ListboxProps={{ direction, itemHeight: 40, listHeight: 240 }}
                                        PopperComponent={renderComboPopper}
                                        disabled={disabledAll || editedItem.sortCodeId === caseSystemSettings.sortCodeDebit}
                                        fullWidth
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                backgroundColor: editedItem.sortCodeId === caseSystemSettings.sortCodeDebit ? '#a9a9a9' : '#f5f5f6',
                                                cursor: editedItem.sortCodeId === caseSystemSettings.sortCodeDebit ? 'not-allowed' : 'pointer',
                                            },
                                            '& .Mui-disabled': {
                                                color: '#A9A9A9',
                                            },
                                        }}
                                    />
                                </div>

                                {showProjectAssignRow && (
                                    <div className="edit-form-row-item" style={{ flex: '1 1 0', minWidth: 0 }}>
                                        <div className="edit-form-row-item-title">{t('Projects')}</div>
                                        <Autocomplete
                                            className={journalFieldStyles.combo}
                                            size="small"
                                            value={selectedProjectAutocompleteValue}
                                            onChange={handleProjectAutocompleteChange}
                                            options={projectAutocompleteOptions}
                                            getOptionLabel={(option) => option?.label ?? ''}
                                            isOptionEqualToValue={(a, b) => {
                                                if (!a && !b) return true;
                                                if (!a || !b) return false;
                                                return a.kind === b.kind && String(a.id) === String(b.id);
                                            }}
                                            filterOptions={(options, state) => {
                                                const q = state.inputValue.trim().toLowerCase();
                                                if (!q) return options;
                                                return options.filter(
                                                    (o) =>
                                                        o.kind === 'add' ||
                                                        o.label.toLowerCase().includes(q)
                                                );
                                            }}
                                            disabled={disabledAll || projectAssignLoading || projectCreateLoading}
                                            disableListWrap
                                            ListboxComponent={FixedSizeVirtualizedListbox}
                                            ListboxProps={{ direction, itemHeight: 36, listHeight: 240 }}
                                            PopperComponent={renderComboPopper}
                                            fullWidth
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder={t('Projects')}
                                                    variant="standard"
                                                    className="edit-style-input"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        ...inputPropsStyle,
                                                    }}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        ...inputStyle,
                                                    }}
                                                />
                                            )}
                                            renderOption={(props, option) => {
                                                if (option?.kind === 'add') {
                                                    return (
                                                        <li
                                                            {...props}
                                                            key={props.key}
                                                            className={`${props.className || ''} edit-add-project-option ${journalFieldStyles.addOption}`}
                                                            style={{
                                                                ...(props.style || {}),
                                                                color: '#304FFF',
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {option.label}
                                                        </li>
                                                    );
                                                }
                                                return (
                                                    <li {...props} key={props.key}>
                                                        {option.label}
                                                    </li>
                                                );
                                            }}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    backgroundColor: '#f5f5f6',
                                                },
                                            }}
                                        />
                                    </div>
                                )}


                            </div>

                            {/* Row 4 */}
                            <div className="form-row">
                                {/* Company ID */}
                                <div className="edit-form-row-item" data-ocr-field="accountCaseId">
                                    <div className="edit-form-row-item-title">
                                        {t('CompanyId')}
                                        <span className="required-asterisk">*</span>
                                        <FieldBoxDot field="accountCaseId" />
                                    </div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('CompanyId')}
                                        value={editedItem.accountCaseId || ''}
                                        onChange={(e) => handleChange('accountCaseId', e.target.value)}
                                        variant="standard"
                                        className="edit-style-input"
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                        disabled={disabledAll}
                                    />
                                </div>
                                {/* VAT Percentage */}
                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title">
                                        {t('VATPercentage')}
                                        <span className="required-asterisk">*</span>
                                    </div>
                                    {(() => {
                                        // Transaction VAT is independent of the sort code. A sort code
                                        // whose VAT is 0 can still be saved at 100% (or any other rate).
                                        const currentVatValue = editedItem.vat;
                                        const selectedVat =
                                            vatSelectOptions.find((o) => String(o.value) === String(currentVatValue)) ||
                                            (currentVatValue != null && currentVatValue !== ''
                                                ? {
                                                    value: currentVatValue,
                                                    label: Number(currentVatValue) === 16 ? t('AccountingTax') : `${currentVatValue}%`,
                                                }
                                                : null);
                                        return (
                                            <AutocompleteSelectFilter
                                                options={vatSelectOptions}
                                                selectedValues={selectedVat}
                                                onChange={(_, option) => {
                                                    if (option == null || option.value == null || option.value === '') return;
                                                    const nextVat = option.value;
                                                    vatOverrideRef.current = nextVat;
                                                    if (typeof setEditedItem === 'function') {
                                                        setEditedItem((prev) => ({ ...prev, vat: nextVat }));
                                                    } else {
                                                        handleChange('vat', nextVat);
                                                    }
                                                }}
                                                placeholder={t('VATPercentage')}
                                                isMultiple={false}
                                                direction={direction}
                                                isDisabled={isVatDocument && editedItem?.isLocked ? ViewMode : disabledAll}
                                                portalMenu
                                            />
                                        );
                                    })()}
                                </div>

                            </div>

                            {/* Row 5 */}
                            <div className="form-row">


                                {/* Reference */}
                                <div className="edit-form-row-item" data-ocr-field="reference">
                                    <div className="edit-form-row-item-title">
                                        {t('Reference')}
                                        <span className="required-asterisk">*</span>
                                        <FieldBoxDot field="reference" />
                                    </div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('Reference')}
                                        value={editedItem.reference || ''}
                                        onChange={(e) => handleChange('reference', e.target.value)}
                                        variant="standard"
                                        className="edit-style-input"
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                        required
                                        disabled={disabledAll}
                                    />
                                </div>


                                {/* Amount Before VAT */}
                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title">
                                        {t('AmountBeforeVAT')}
                                    </div>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        placeholder={t('AmountBeforeVAT')}
                                        value={netAmountInput}
                                        onFocus={() => {
                                            netFocusedRef.current = true;
                                            setNetAmountInput(
                                                netAmountInput !== '' && !isNaN(Number(netAmountInput))
                                                    ? String(Number(netAmountInput))
                                                    : (beforeVatAmount ? String(Number(beforeVatAmount)) : '')
                                            );
                                        }}
                                        onBlur={(e) => {
                                            netFocusedRef.current = false;
                                            const val = e.target.value;
                                            if (val !== '' && !isNaN(val)) {
                                                const formatted = Number(val).toFixed(2);
                                                setNetAmountInput(formatted);
                                                handleChangeNetAmount(formatted);
                                            }
                                        }}
                                        onChange={(e) => {
                                            handleChangeNetAmount(e.target.value);
                                        }}
                                        variant="standard"
                                        className="edit-style-input"
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                        disabled={disabledAll}
                                    />
                                </div>



                            </div>

                            {/* Row 6 */}
                            <div className="form-row">
                                {/* Reference2 */}
                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title">
                                        {t('Reference2')}

                                    </div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('Reference2')}
                                        value={editedItem.reference2 || ''}
                                        onChange={(e) => handleChange('reference2', e.target.value)}
                                        variant="standard"
                                        className="edit-style-input"
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                        required
                                        disabled={disabledAll}
                                    />
                                </div>

                                {/* Vat */}
                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title">
                                        {t('VAT')} {currentVat * 100}%
                                    </div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('VAT')}
                                        value={beautifyNumber(editedItem.amount - beforeVatAmount)}
                                        variant="standard"
                                        className="edit-style-input"
                                        disabled
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                    />
                                </div>
                            </div>

                            {/* Row 7 */}
                            <div className="form-row">

                                {/* Confirmation Number */}
                                <div className="edit-form-row-item" data-ocr-field="confirmationNumber">

                                    <div className="edit-form-row-item-title">
                                        {t('ConfirmationNumber')}
                                        <FieldBoxDot field="confirmationNumber" />

                                        {!authWindowClosedByUser && !isAuthWindowOpen && (
                                            <span
                                                className="check-confirmation-number-btn"
                                                onClick={handleCheckConfirmationNumber}
                                                style={{
                                                    marginLeft: '10px',
                                                    color: '#304FFF',
                                                    textDecoration: 'underline',
                                                    fontWeight: 'bold',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                (בדיקת מספר אישור)
                                            </span>
                                        )}
                                        {isAuthWindowOpen && (
                                            <span
                                                style={{
                                                    marginLeft: '10px',
                                                    color: '#FF9800',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                (חלון אימות פתוח)
                                            </span>
                                        )}
                                        {authWindowClosedByUser && (
                                            <span
                                                className="retry-auth-btn"
                                                onClick={() => {
                                                    resetAuthState();
                                                    handleCheckConfirmationNumber();
                                                }}
                                                style={{
                                                    marginLeft: '10px',
                                                    color: '#F44336',
                                                    textDecoration: 'underline',
                                                    fontWeight: 'bold',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                (נסה שוב)
                                            </span>
                                        )}
                                    </div>
                                    <TextField
                                        fullWidth
                                        type='number'
                                        placeholder={t('ConfirmationNumber')}
                                        value={editedItem.confirmationNumber || ''}
                                        onChange={(e) =>
                                            handleChange('confirmationNumber', e.target.value)
                                        }
                                        variant="standard"
                                        className="edit-style-input"
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                        required
                                        disabled={disabledAll}
                                    />
                                </div>

                                {/* Amount */}
                                <div className="edit-form-row-item" data-ocr-field="amount">
                                    <div className="edit-form-row-item-title" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {t('TotalAmountIncludingVAT')}
                                        <span className="required-asterisk">*</span>
                                        <FieldBoxDot field="amount" />
                                        <Tooltip title={t('MakeAmountOpposite') || 'Make amount opposite (positive ↔ negative)'}>
                                            <IconButton
                                                size="small"
                                                sx={{ p: 0.2 }}
                                                onClick={handleFlipAmount}
                                                disabled={disabledAll}
                                            >
                                                <SwapHorizIcon sx={{ fontSize: 18, color: '#304FFF' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('Amount')}
                                        value={amountInput}
                                        onFocus={e => {
                                            setAmountInput(
                                                editedItem.amount !== undefined && editedItem.amount !== null
                                                    ? String(Number(parseNumeric(editedItem.amount)))
                                                    : ''
                                            );
                                        }}
                                        onBlur={e => {
                                            const val = e.target.value;
                                            if (val !== '' && !isNaN(val)) {
                                                const formatted = Number(val).toFixed(2);
                                                setAmountInput(formatted);
                                                handleChange('amount', formatted);
                                                flushAmountSync(formatted);
                                            } else {
                                                flushAmountSync();
                                            }
                                        }}
                                        onChange={e => {
                                            lastAmountEditRef.current = 'gross';
                                            setAmountInput(e.target.value);
                                            handleChangeAmount(e.target.value);
                                        }}
                                        variant="standard"
                                        type="number"
                                        className={`edit-style-input${(!amountInput || amountInput === '' || isNaN(Number(amountInput)) || Number(amountInput) === 0) ? ' edit-style-input-required' : ''}`}
                                        InputProps={inputPropsStyle}
                                        inputProps={inputStyle}
                                        required
                                        disabled={disabledAll}
                                    />
                                </div>

                            </div>

                            {/* Row 8 */}
                            <div className="form-row">


                                {/* Description */}
                                <div className="edit-form-row-item" data-ocr-field="description">
                                    <div className="edit-form-row-item-title">
                                        {t('Description')}
                                        <span className="required-asterisk">*</span>
                                        <FieldBoxDot field="description" />
                                    </div>
                                    {(() => {
                                        const descriptionText = editedItem.description || '';
                                        const cellClass = getDescriptionCellClass(descriptionText);
                                        return (
                                            <div className={cellClass ? `description-cell ${cellClass}` : ''}>
                                                <TextField
                                                    fullWidth
                                                    placeholder={t('Description')}
                                                    value={descriptionText}
                                                    onChange={(e) => handleChange('description', e.target.value)}
                                                    variant="standard"
                                                    className={cellClass ? undefined : 'edit-style-input'}
                                                    InputProps={inputPropsStyle}
                                                    inputProps={inputStyle}
                                                    required
                                                    disabled={disabledLockedMetadataFields}
                                                />
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Currency Code + Exchange Rate Type Toggle (compact, beside label) */}
                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {t('Currency')}
                                        <div
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'stretch',
                                                gap: '1px',
                                                height: '24px',
                                                padding: '1px',
                                                borderRadius: '4px',
                                                background: '#F8F9FA',
                                                border: '1px solid #E9ECEF',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            <div
                                                onClick={() => {
                                                    if (!disabledAll) {
                                                        const r = parseNumeric(manualRateInput);
                                                        if (manualRateInput !== '' && !isNaN(r) && r > 0) {
                                                            lastManualRateRef.current = manualRateInput;
                                                        }
                                                        setEditedItem((prev) => ({
                                                            ...prev,
                                                            exchangeRateType: Constants.ExchangeRateType.Representative,
                                                            exchangeRate: null,
                                                        }));
                                                        setManualRateInput('');
                                                    }
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    padding: '0 6px',
                                                    borderRadius: '3px',
                                                    cursor: disabledAll ? 'not-allowed' : 'pointer',
                                                    backgroundColor: editedItem.exchangeRateType !== Constants.ExchangeRateType.Manual ? '#304FFF' : 'transparent',
                                                    color: editedItem.exchangeRateType !== Constants.ExchangeRateType.Manual ? '#FFFFFF' : '#6C757D',
                                                    fontWeight: editedItem.exchangeRateType !== Constants.ExchangeRateType.Manual ? '600' : '500',
                                                    fontSize: 'var(--xtram-text-xs, 0.75rem)',
                                                    lineHeight: 1,
                                                    boxSizing: 'border-box',
                                                    whiteSpace: 'nowrap',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                {t('RepresentativeRate')}
                                            </div>
                                            <div
                                                onClick={() => {
                                                    if (!disabledAll) {
                                                        let restored = editedItem.exchangeRate != null && editedItem.exchangeRate !== ''
                                                            ? String(Number(parseNumeric(editedItem.exchangeRate)))
                                                            : (lastManualRateRef.current ?? '');
                                                        if (!restored) {
                                                            const amount = parseNumeric(editedItem.amount);
                                                            const fc = parseNumeric(editedItem.foreignCurrencyAmount);
                                                            if (amount > 0 && fc > 0 && !isNaN(amount) && !isNaN(fc)) {
                                                                restored = (amount / fc).toFixed(MANUAL_RATE_DECIMALS);
                                                            }
                                                        }
                                                        setManualRateInput(restored);
                                                        setEditedItem((prev) => ({
                                                            ...prev,
                                                            exchangeRateType: Constants.ExchangeRateType.Manual,
                                                            exchangeRate: restored || null,
                                                        }));
                                                    }
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    height: '100%',
                                                    padding: '0 6px',
                                                    borderRadius: '3px',
                                                    cursor: disabledAll ? 'not-allowed' : 'pointer',
                                                    backgroundColor: editedItem.exchangeRateType === Constants.ExchangeRateType.Manual ? '#304FFF' : 'transparent',
                                                    color: editedItem.exchangeRateType === Constants.ExchangeRateType.Manual ? '#FFFFFF' : '#6C757D',
                                                    fontWeight: editedItem.exchangeRateType === Constants.ExchangeRateType.Manual ? '600' : '500',
                                                    fontSize: 'var(--xtram-text-xs, 0.75rem)',
                                                    lineHeight: 1,
                                                    boxSizing: 'border-box',
                                                    whiteSpace: 'nowrap',
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                {t('ManualRate')}
                                            </div>
                                        </div>
                                    </div>
                                    {(() => {
                                        const hasCurrency =
                                            editedItem.foreignCurrencyCode != null &&
                                            editedItem.foreignCurrencyCode !== '';
                                        const selectedCurrency = hasCurrency
                                            ? (
                                                currencySelectOptions.find((o) => o.value === editedItem.foreignCurrencyCode) ||
                                                { value: editedItem.foreignCurrencyCode, label: String(editedItem.foreignCurrencyCode) }
                                            )
                                            : null;
                                        const currencyRequiredError =
                                            editedItem.exchangeRateType === Constants.ExchangeRateType.Manual &&
                                            !hasCurrency;
                                        return (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    borderRadius: 8,
                                                    boxShadow: currencyRequiredError ? '0 0 0 2px #c62828' : undefined,
                                                }}
                                            >
                                                <AutocompleteSelectFilter
                                                    options={currencySelectOptions}
                                                    selectedValues={selectedCurrency}
                                                    onChange={(_, option) => {
                                                        const next = option?.value;
                                                        if (next === '' || next == null) {
                                                            setEditedItem(prev => ({
                                                                ...prev,
                                                                foreignCurrencyCode: '',
                                                                foreignCurrencyAmount: '',
                                                                amount: '',
                                                                exchangeRate: '',
                                                            }));
                                                            setManualRateInput('');
                                                            userClearedManualRateRef.current = true;
                                                            lastManualRateRef.current = null;
                                                        } else {
                                                            setEditedItem((prev) => ({
                                                                ...prev,
                                                                foreignCurrencyCode: Number(next),
                                                                exchangeRateType:
                                                                    prev.exchangeRateType ?? Constants.ExchangeRateType.Representative,
                                                            }));
                                                        }
                                                    }}
                                                    placeholder={t('Currency')}
                                                    isMultiple={false}
                                                    direction={direction}
                                                    isDisabled={disabledAll}
                                                    portalMenu
                                                />
                                            </div>
                                        );
                                    })()}
                                    {/* Manual Exchange Rate — under Currency, reduced width, left-aligned */}
                                    {editedItem.exchangeRateType === Constants.ExchangeRateType.Manual && (() => {
                                        const hasForeignCode = editedItem.foreignCurrencyCode != null && editedItem.foreignCurrencyCode !== '';
                                        const amountNum = parseNumeric(editedItem.amount);
                                        const fcNum = parseNumeric(editedItem.foreignCurrencyAmount);
                                        const hasValidAmountAndFc = hasForeignCode && amountNum > 0 && fcNum > 0 && !isNaN(amountNum) && !isNaN(fcNum);
                                        const calculatedManualRate = hasValidAmountAndFc ? (amountNum / fcNum).toFixed(MANUAL_RATE_DECIMALS) : null;
                                        const manualRateDisplayValue = (manualRateInput !== '' && manualRateInput != null)
                                            ? manualRateInput
                                            : (userClearedManualRateRef.current ? '' : (calculatedManualRate ?? ''));
                                        const hasValidDisplayRate = manualRateDisplayValue !== '' && !isNaN(Number(manualRateDisplayValue)) && Number(manualRateDisplayValue) > 0;
                                        return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, alignSelf: 'flex-start' }}>
                                            <div className="edit-form-row-item-title">{t('ManualExchangeRate')}</div>
                                            <TextField
                                                fullWidth
                                                placeholder={t('ManualExchangeRate')}
                                                value={manualRateDisplayValue}
                                                onFocus={() => {
                                                    if (userClearedManualRateRef.current) {
                                                        setManualRateInput('');
                                                        return;
                                                    }
                                                    if (editedItem.exchangeRate != null && editedItem.exchangeRate !== '') {
                                                        setManualRateInput(String(Number(parseNumeric(editedItem.exchangeRate))));
                                                    } else if (hasValidAmountAndFc) {
                                                        setManualRateInput(manualRateInput || lastManualRateRef.current || calculatedManualRate || '');
                                                    } else {
                                                        setManualRateInput(manualRateInput || '');
                                                    }
                                                }}
                                                onBlur={e => {
                                                    const val = e.target.value;
                                                    if (val !== '' && !isNaN(val) && Number(val) > 0) {
                                                        const formatted = Number(val).toFixed(MANUAL_RATE_DECIMALS);
                                                        setManualRateInput(formatted);
                                                        handleManualRateChange(formatted);
                                                    }
                                                }}
                                                onChange={e => {
                                                    const v = e.target.value;
                                                    userClearedManualRateRef.current = (v === '' || v == null);
                                                    setManualRateInput(v);
                                                    handleManualRateChange(v);
                                                }}
                                                variant="standard"
                                                type="number"
                                                className={`edit-style-input${(editedItem.exchangeRateType === Constants.ExchangeRateType.Manual && !hasValidDisplayRate) ? ' edit-style-input-required' : ''}`}
                                                InputProps={inputPropsStyle}
                                                inputProps={inputStyle}
                                                disabled={disabledAll}
                                            />
                                        </div>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Row 9 */}
                            <div className="form-row">
                                {/* Status */}
                                {canProcessStatus && (
                                    <div className="edit-form-row-item">
                                        <div className="edit-form-row-item-title" style={{ marginRight: '16px' }}>
                                            {t('status')}
                                            <span className="required-asterisk">*</span>
                                        </div>
                                        <div
                                            className="edit-style-input"
                                            style={{
                                                display: 'flex',
                                                gap: '8px',
                                                height: 'var(--edit-field-h, 40px)',
                                                minHeight: 'var(--edit-field-h, 40px)',
                                                boxSizing: 'border-box',
                                                padding: '2px',
                                                position: 'relative',
                                                overflow: 'visible',
                                            }}
                                        >
                                            {/* Unprocessed Option */}
                                            <div
                                                onClick={() => {
                                                    if (!disabledAll) {
                                                        handleStatusRadioChange({ target: { value: 'notDone' } }, { forceProcess: false });
                                                    }
                                                }}
                                                style={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    cursor: disabledAll ? 'not-allowed' : 'pointer',
                                                    backgroundColor: !statusCheck ? '#304FFF' : 'transparent',
                                                    color: !statusCheck ? '#FFFFFF' : '#6C757D',
                                                    fontWeight: !statusCheck ? '600' : '500',
                                                    fontSize: 'var(--xtram-text-xs, 0.75rem)',
                                                    transition: 'all 0.2s ease',
                                                    opacity: disabledAll ? 0.6 : 1,
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!disabledAll && statusCheck) {
                                                        e.currentTarget.style.backgroundColor = '#E3F2FD';
                                                        e.currentTarget.style.color = '#1976D2';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!disabledAll && statusCheck) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                        e.currentTarget.style.color = '#6C757D';
                                                    }
                                                }}
                                            >
                                                {/* Icon for Unprocessed */}
                                                <ScheduleIcon style={{ fontSize: 16 }} />
                                                <span>{t('unprocessed')}</span>
                                            </div>

                                            {/* Processed Option */}
                                            <div
                                                className="edit-modal-fn-key-wrap"
                                                onClick={() => {
                                                    if (!disabledAll) {
                                                        handleStatusRadioChange({ target: { value: 'done' } }, { forceProcess: forceProcess });
                                                    }
                                                }}
                                                style={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    cursor: disabledAll ? 'not-allowed' : 'pointer',
                                                    backgroundColor: statusCheck ? '#10B981' : 'transparent',
                                                    color: statusCheck ? '#FFFFFF' : '#6C757D',
                                                    fontWeight: statusCheck ? '600' : '500',
                                                    fontSize: 'var(--xtram-text-xs, 0.75rem)',
                                                    transition: 'all 0.2s ease',
                                                    opacity: disabledAll ? 0.6 : 1,
                                                    position: 'relative',
                                                    overflow: 'visible'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!disabledAll && !statusCheck) {
                                                        e.currentTarget.style.backgroundColor = '#D1FAE5';
                                                        e.currentTarget.style.color = '#059669';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!disabledAll && !statusCheck) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                        e.currentTarget.style.color = '#6C757D';
                                                    }
                                                }}
                                            >
                                                <span className="edit-modal-fn-key-badge">F1</span>
                                                {/* Icon for Processed */}
                                                <CheckCircleIcon style={{ fontSize: 16 }} />
                                                <span>{t('processed')}</span>
                                            </div>
                                        </div>
                                        {showForceProcessOption && (
                                            <Box
                                                className={`document-mode-toggle${forceProcess ? ' is-checked' : ''}`}
                                                sx={{ mt: 0.5, width: 'fit-content', maxWidth: '100%' }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={forceProcess}
                                                            onChange={(e) => setForceProcess(e.target.checked)}
                                                            disabled={disabledAll}
                                                            size="small"
                                                            inputProps={{ 'aria-label': t('ForceProcess') }}
                                                        />
                                                    }
                                                    label={t('ForceProcess')}
                                                />
                                            </Box>
                                        )}
                                    </div>
                                )}

                                {/* Currency Amount */}
                                <div className="edit-form-row-item">
                                    <div className="edit-form-row-item-title" style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'center' }}>
                                        {t('CurrencyAmount')}
                                        {currencyWarn && (
                                            <span style={{ color: 'red', fontSize: '0.85em' }}>
                                                {t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode')}
                                            </span>
                                        )}
                                        {/* <span className="required-asterisk">*</span> */}
                                    </div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('CurrencyAmount')}
                                        value={currencyAmountInput}
                                        onFocus={e => {
                                            setCurrencyAmountInput(
                                                editedItem.foreignCurrencyAmount !== undefined && editedItem.foreignCurrencyAmount !== null
                                                    ? String(Number(parseNumeric(editedItem.foreignCurrencyAmount)).toFixed(2))
                                                    : ''
                                            );
                                        }}
                                        onBlur={e => {
                                            const val = e.target.value;
                                            if (val !== '' && !isNaN(val)) {
                                                const formatted = Number(val).toFixed(2);
                                                setCurrencyAmountInput(formatted);
                                                handleChange('foreignCurrencyAmount', formatted);
                                                flushFcSync(formatted);
                                            } else {
                                                flushFcSync();
                                            }
                                        }}
                                        onChange={e => {
                                            setCurrencyAmountInput(e.target.value);
                                            handleChangeForeignCurrencyAmount(e.target.value);
                                        }}
                                        variant="standard"
                                        type="number"
                                        className="edit-style-input"
                                        InputProps={{
                                            ...inputPropsStyle,
                                            endAdornment: isLoadingCurrencyRate ? (
                                                <InputAdornment position="end">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CircularProgress size={22} />
                                                        <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                                            {t('FetchingCurrencyRateByDate')}
                                                        </span>
                                                    </Box>
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                        inputProps={inputStyle}
                                        required
                                        disabled={disabledAll || isLoadingCurrencyRate}
                                    />

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px', marginLeft: '4px' , justifyContent: 'center'}}>
                                        {(isJournalEntryDocument ||
                                            editedItem?.docType === 'Expenses' ||
                                            editedItem?.docType === 'Incomes') &&
                                            /* Only when we know there are duplicates: item in a group with other items (modal will show them; no "no duplicates" empty state) */
                                            (journalGroup && (journalGroup.items?.length ?? groupItems.length) > 1) &&
                                            /* Hide when group contains a PaymentAcceptance (PA→JE duplication handled separately) */
                                            !(groupItems || journalGroup?.items || []).some(gi => gi.paymentAcceptanceId) ? (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={openDuplicateJournal}
                                                disabled={disabledAll}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: 'var(--xtram-text-xs, 0.75rem)',
                                                    padding: '4px 10px',
                                                    borderColor: '#162473',
                                                    color: '#162473',
                                                    '&:hover': {
                                                        borderColor: '#162473',
                                                        backgroundColor: 'rgba(22, 36, 115, 0.08)'
                                                    },
                                                    '&:disabled': {
                                                        borderColor: '#ccc',
                                                        color: '#ccc'
                                                    }
                                                }}
                                            >
                                                {t('DuplicateJournal')}
                                            </Button>
                                        ) : null}
                                        {canDuplicatePaymentAcceptance && relatedPaymentsCount > 0 ? (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={openRelatedPayments}
                                                disabled={disabledAll}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: 'var(--xtram-text-xs, 0.75rem)',
                                                    padding: '4px 10px',
                                                    borderColor: '#162473',
                                                    color: '#162473',
                                                    '&:hover': {
                                                        borderColor: '#162473',
                                                        backgroundColor: 'rgba(22, 36, 115, 0.08)'
                                                    },
                                                    '&:disabled': {
                                                        borderColor: '#ccc',
                                                        color: '#ccc'
                                                    }
                                                }}
                                            >
                                                {t('RelatedPayments') || 'Related Payments'}
                                            </Button>
                                        ) : null}
                                    </div>

                                    {/* שיוך הוצאה — under CurrencyAmount (Expenses only, persists on Save) */}
                                    {effectiveDocType === 'Expenses' && (
                                        <div style={{ marginTop: 8 }}>
                                            <div className="edit-form-row-item-title">{t('AssignExpense')}</div>
                                            <AutocompleteSelectFilter
                                                key={selectedAssignedAccountCardId ?? 'empty'}
                                                options={assignedAccountCardSelectOptions}
                                                selectedValues={selectedAssignedAccountCardOption}
                                                onChange={(e, newValue) =>
                                                    setSelectedAssignedAccountCardId(
                                                        newValue ? newValue.value : null
                                                    )
                                                }
                                                placeholder={t('Client Name')}
                                                isMultiple={false}
                                                direction={direction}
                                                isDisabled={disabledAll}
                                                searchable
                                                clearable
                                                portalMenu
                                            />
                                        </div>
                                    )}
                                </div>


                            </div>

                            {/* Match Numbers Row */}
                            {(() => {
                                const matchEntries = [
                                    { key: 'creditCardMatchNumber', label: t('CardMatching') + ' - ' + t('Credit') },
                                    { key: 'debitCardMatchNumber', label: t('CardMatching') + ' - ' + t('Debit') },
                                    { key: 'bankCreditMatchNumber', label: t('BankMatching') + ' - ' + t('Credit') },
                                    { key: 'bankDebitMatchNumber', label: t('BankMatching') + ' - ' + t('Debit') },
                                ].filter(e => editedItem[e.key] != null && editedItem[e.key] !== -1 && editedItem[e.key] !== 0);

                                if (matchEntries.length === 0) return null;

                                return (
                                    <div className="form-row" style={{ flexWrap: 'wrap', gap: '8px' }}>
                                        {matchEntries.map(entry => (
                                            <div
                                                key={entry.key}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    padding: '4px 12px',
                                                    borderRadius: '16px',
                                                    backgroundColor: '#EEF2FF',
                                                    border: '1px solid #C7D2FE',
                                                    fontSize: 'var(--xtram-text-xs, 0.75rem)',
                                                    color: '#304FFF',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                <span>{entry.label}:</span>
                                                <span style={{ fontWeight: '700' }}>{editedItem[entry.key]}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            {/* Row 10 - Actions */}
                            <div className="form-row edit-modal-actions-row">


                                {/* Navigation and Save Buttons */}
                                <div className="edit-form-row-item edit-modal-nav-col" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <div className="edit-navigation-container">
                                        {/* Next Button */}
                                        {currentIndex !== null && currentIndex > -1 && currentIndex !== filteredAndSortedData.length - 1 && (
                                            <div className="edit-nav-button-wrapper">
                                                <div
                                                    className={`edit-nav-button edit-modal-fn-key-wrap ${currentIndex === filteredAndSortedData.length - 1 ? 'disabled' : ''}`}
                                                    onClick={handleNext}
                                                >
                                                    <span className="edit-modal-fn-key-badge">F3</span>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M9.00047 15.875L12.8805 11.995L9.00047 8.11498C8.61047 7.72498 8.61047 7.09498 9.00047 6.70498C9.39047 6.31498 10.0205 6.31498 10.4105 6.70498L15.0005 11.295C15.3905 11.685 15.3905 12.315 15.0005 12.705L10.4105 17.295C10.0205 17.685 9.39047 17.685 9.00047 17.295C8.62047 16.905 8.61047 16.265 9.00047 15.875Z"
                                                            fill="black"
                                                            fillOpacity="0.9"
                                                        />
                                                    </svg>
                                                </div>
                                                <span className="edit-button-label">
                                                    {t('Next')}
                                                </span>
                                            </div>
                                        )}

                                        {/* Save Button - Centered */}
                                        <div className="edit-nav-button-wrapper">
                                            <div
                                                className={`edit-modal-save-button edit-modal-fn-key-wrap ${disabledLockedMetadataFields ? 'disabled' : ''}`}
                                                onClick={disabledLockedMetadataFields ? undefined : handleSaveWithInventoryCheck}
                                            >
                                                <span className="edit-modal-fn-key-badge">F2</span>
                                                <span className="edit-modal-save-button-text">
                                                    {t('Save')}
                                                </span>
                                            </div>
                                            <span className="edit-button-label">
                                                {t('Save')}
                                            </span>
                                        </div>

                                        {/* Previous Button */}
                                        {currentIndex !== null && currentIndex > -1 && currentIndex !== 0 && (
                                            <div className="edit-nav-button-wrapper">
                                                <div
                                                    className={`edit-nav-button edit-modal-fn-key-wrap ${currentIndex === 0 ? 'disabled' : ''}`}
                                                    onClick={handlePrevious}
                                                >
                                                    <span className="edit-modal-fn-key-badge">F4</span>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M14.9995 15.875L11.1195 11.995L14.9995 8.11498C15.3895 7.72498 15.3895 7.09498 14.9995 6.70498C14.6095 6.31498 13.9795 6.31498 13.5895 6.70498L8.99953 11.295C8.60953 11.685 8.60953 12.315 8.99953 12.705L13.5895 17.295C13.9795 17.685 14.6095 17.685 14.9995 17.295C15.3795 16.905 15.3895 16.265 14.9995 15.875Z"
                                                            fill="black"
                                                            fillOpacity="0.9"
                                                        />
                                                    </svg>
                                                </div>
                                                <span className="edit-button-label">
                                                    {t('Previous')}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Document Processed By Information */}
                                    {editedItem?.processedBy && subAccountantNewOptions.length > 0 && (
                                        <div
                                            style={{
                                                marginTop: '5px',
                                                padding: '4px 6px',
                                                backgroundColor: '#304FFF',
                                                borderRadius: '6px',
                                                border: '1px solid #304FFF',
                                                fontSize: 'var(--xtram-text-sm, 0.875rem)',
                                                color: '#ffffff',
                                                textAlign: 'center',
                                                width: '100%',
                                                boxSizing: 'border-box !important',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {t('DocumentProcessedBy')}: {(() => {
                                                const foundOption = subAccountantNewOptions.find(option => option.value === editedItem.processedBy);
                                                if (foundOption?.label) {
                                                    return foundOption.label;
                                                } else if (editedItem.processedBy === "-100") {
                                                    return t('ProcessedBySystem');
                                                } else {
                                                    return editedItem.processedBy;
                                                }
                                            })()}
                                        </div>
                                    )}
                                    {/* Document Created By Information */}
                                    {(editedItem?.createdByUserId || editedItem?.createdByUserType) && (
                                        <div
                                            style={{
                                                marginTop: '5px',
                                                padding: '4px 6px',
                                                backgroundColor: '#2E7D32',
                                                borderRadius: '6px',
                                                border: '1px solid #2E7D32',
                                                fontSize: 'var(--xtram-text-sm, 0.875rem)',
                                                color: '#ffffff',
                                                textAlign: 'center',
                                                width: '100%',
                                                boxSizing: 'border-box !important',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {t('DocumentCreatedBy')}: {(() => {
                                                if (subAccountantNewOptions?.length > 0 && editedItem.createdByUserId) {
                                                    const foundOption = subAccountantNewOptions.find(option => option.value === editedItem.createdByUserId);
                                                    if (foundOption?.label) return foundOption.label;
                                                }
                                                if (editedItem.createdByUserType === 'Client') return t('Client');
                                                if (editedItem.createdByUserId && editedItem.createdByUserType) return `${editedItem.createdByUserId} (${editedItem.createdByUserType})`;
                                                return editedItem.createdByUserId || editedItem.createdByUserType || '—';
                                            })()}
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="edit-form-row-item edit-modal-actions-col">
                                    <div className="edit-form-row-item-title">
                                        {t('Actions')}
                                    </div>
                                    <div
                                        className="edit-modal-actions-bar"
                                        style={{
                                            opacity: disabledAll ? 0.45 : 1,
                                            pointerEvents: disabledAll ? 'none' : 'auto',
                                        }}
                                    >
                                        {
                                            (userTypeContext?.toString() !== Constants.UserType.Client || permissionsState.Premium) &&
                                            <>

                                                <Tooltip title={t('SendToChat')}>
                                                    <IconButton
                                                        disabled={disabledAll}
                                                        onClick={handleSendToChat}
                                                    >
                                                        <span style={{ display: 'flex', alignItems: 'center', color: '#4CAF50' }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4CAF50">
                                                                <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16ZM7 9H17V11H7V9ZM7 12H14V14H7V12Z" />
                                                            </svg>
                                                        </span>
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title={t('AttachDocument')}>
                                                    <IconButton
                                                        disabled={disabledAll}
                                                        onClick={() => setUploadModalOpen(true)}
                                                    >
                                                        <AttachFileIcon sx={{ color: '#2196F3' }} />
                                                    </IconButton>
                                                </Tooltip>

                                                {canSplitDocument && (
                                                    <Tooltip title={client?.isActive === false ? (t('ClientInactiveCannotCreateDocuments') || t('Split')) : t('Split')}>
                                                        <span>
                                                            <IconButton
                                                                disabled={disabledAll || client?.isActive === false}
                                                                onClick={() => setSplitOpen(true)}>
                                                                <CallSplitIcon sx={{ color: '#ffa000' }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}

                                                {canCreateFromDocument && (
                                                    <Tooltip title={client?.isActive === false ? (t('ClientInactiveCannotCreateDocuments') || t('NewFromThis')) : t('NewFromThis')}>
                                                        <span>
                                                            <IconButton
                                                                disabled={disabledAll || client?.isActive === false}
                                                                onClick={openDuplicate}>
                                                                <ContentCopyIcon sx={{ color: '#304fff' }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}

                                                {canDuplicatePaymentAcceptance && (
                                                    <Tooltip title={duplicatePaymentSourceType === 'Incomes' ? t('AddReceipt') : t('AddPayment')}>
                                                        <IconButton disabled={disabledAll} onClick={openDuplicatePayment}>
                                                            <ReceiptLongIcon sx={{ color: '#304fff' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </>
                                        }

                                        {
                                            (userTypeContext?.toString() !== Constants.UserType.Client || permissionsState.Premium) &&
                                            <>
                                                <Tooltip title={t('Delete')}>
                                                    <IconButton
                                                        disabled={disabledAll}
                                                        onClick={() => handleDeleteClick(editedItem.id)}
                                                    >
                                                        <DeleteIcon style={{ color: '#fa0a1e' }} />
                                                    </IconButton>
                                                </Tooltip>

                                                <DocumentTransferDialog
                                                    editedItem={transferItem}
                                                    token={token}
                                                    setLoading={setLoading}
                                                    RefreshPage={RefreshPage}
                                                    onTransferComplete={handleCancel}
                                                    t={t}
                                                    client={client}
                                                    clarificationRestoreMode={!!editedItem?.clarificationId}
                                                />

                                                {editedItem?.isLocked ? (
                                                    <Tooltip title={t('Locked')}>
                                                        <LockIcon style={{ color: '#6c757d', cursor: 'default' }} />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title={t('LockDocument')}>
                                                        <IconButton
                                                            onClick={async () => {
                                                                if (!statusCheck) {
                                                                    notify.warning(t('MarkAsProcessedBeforeLocking'), { autoClose: 3000 });
                                                                    return;
                                                                }
                                                                const result = await Swal.fire({
                                                                    title: t('AreYouSure'),
                                                                    text: t('OnceLockedYouCannotEditOrDelete'),
                                                                    icon: 'warning',
                                                                    showCancelButton: true,
                                                                    confirmButtonColor: '#3085d6',
                                                                    cancelButtonColor: '#d33',
                                                                    confirmButtonText: t('YesLockIt'),
                                                                    cancelButtonText: t('NoCancel'),
                                                                    customClass: {
                                                                        container: 'swal-high-zindex'
                                                                    }
                                                                });

                                                                if (result.isConfirmed) {
                                                                    try {
                                                                        setLoading(true);
                                                                        const res = await DocumentService.LockDocument(editedItem.id, client.caseId, token);
                                                                        if (res.ok) {
                                                                            notify.success(t('DocumentLocked'));
                                                                            RefreshPage();
                                                                        } else {
                                                                            const errorText = await res.text();
                                                                            notify.error(t('LockFailed', { errorText }));
                                                                        }
                                                                    } catch (err) {
                                                                        notify.error(t('LockFailed', { errorText: err.message }));
                                                                    } finally {
                                                                        setLoading(false);
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <LockIcon style={{ color: '#d32f2f' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                        </div>
                        </FieldBoxDotProvider>
                    </div>
                    {showPdfIframe  && (
                        <DocumentConversationSideTabs
                            documentUrl={editedItem.documentUrl}
                            documentId={editedItem.id}
                            fieldPositions={fieldPositions}
                            visibleFields={visibleFieldBoxes}
                            activeField={activeOcrField}
                            fieldLabels={fieldBoxLabels}
                            fieldOrder={fieldBoxOrder}
                            onFieldClick={handleFieldBoxClick}
                            onBoxesShownChange={setFieldBoxesShown}
                            documentTitle={editedItem.title}
                            messages={clarificationConversation}
                            loading={clarificationConversationLoading}
                            refreshMessages={loadClarificationConversation}
                            initialTab={clarificationConversation.length > 0 ? 'conversation' : 'document'}
                            headerAction={canUseClarificationConversation ? (
                                <>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!token || disabledAll}
                                        startIcon={<LinkIcon sx={{ fontSize: 18 }} />}
                                        onClick={() => setClarificationAttachModalOpen(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            color: '#304FFF',
                                            borderColor: '#BFC8FF',
                                            whiteSpace: 'nowrap',
                                            '&:hover': { backgroundColor: '#F5F7FF', borderColor: '#304FFF' },
                                            '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
                                        }}
                                    >
                                        {t('AttachConversation')}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        disabled={
                                            !token
                                            || disabledAll
                                            || deletingClarificationConversation
                                            || clarificationConversation.length === 0
                                        }
                                        startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                                        onClick={handleDeleteClarificationConversation}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
                                        }}
                                    >
                                        {t('DeleteClarificationConversation')}
                                    </Button>
                                </>
                            ) : null}
                        />
                    )}
                    <ClarificationConversationPickerModal
                        open={clarificationAttachModalOpen}
                        onClose={() => setClarificationAttachModalOpen(false)}
                        caseId={client?.caseId}
                        documentId={editedItem?.id}
                        documentType={effectiveDocType}
                        token={token}
                        onAttached={() => {
                            setClarificationAttachModalOpen(false);
                            loadClarificationConversation();
                        }}
                    />
                    {/* Free resize handles */}
                    <div className="edit-modal-resize-handle edge-e" onMouseDown={beginResize('e')} />
                    <div className="edit-modal-resize-handle edge-s" onMouseDown={beginResize('s')} />
                    <div className="edit-modal-resize-handle edge-se" onMouseDown={beginResize('se')} />
                    <div className="edit-modal-resize-handle edge-sw" onMouseDown={beginResize('sw')} />
                    <div className="edit-modal-resize-handle edge-w" onMouseDown={beginResize('w')} />
                </div>
            </Draggable>
            <CustomDeleteConfirmationModal
                isVisible={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                onDelete={() => { }}
                confirmMessage={t('confirmDeleteMessage')}
                titleMessage={t('confirmDeleteTitle')}
            />
            <AddAccountCardModal
                accountCardOptions={accountCardOptions}
                open={editedAddAccountCard}
                onClose={handleCloseItem}
                editedItem={newAccountCard}
                isEdit={isEditAccountCard}
                isRange={isRange}
                isClosedReportingMonth={isClosedReportingMonth}
                palCodes={palCodes}
                propPalCode={palCode}
                setPalCode={setPalCode}
                direction={direction} // Pass direction for styling
                isAdd={!showAccountCardInputsEdit}
                client={client}
                token={token}
                setLoading={setLoading}
                handleChangeAfterAdd={handleChangeAfterAdd}
                fetchEnums={fetchEnums}
                type={addAccountCardType}
                accountCards={accountCardOptions}
            />

            {dupOpen && (
                <BulkEditModal
                    duplicateMode           // flag picked up inside BulkEditModal
                    titleKey="NewFromThis"
                    open={dupOpen}
                    ids={[editedItem.id]}   // single source id
                    sourceTransaction={editedItem}  // pass the source transaction data
                    onClose={closeDuplicate}
                    onSavePatch={saveDuplicate}
                    formattedAccountCardOptions={formattedAccountCardOptions2}
                    sortCodeIdOptions={sortCodeIdOptions}
                    client={client}
                    accountCardOptions={accountCardOptions}
                    type={type}
                    token={token}
                />
            )}

            {splitOpen && canSplitDocument && (
                <SplitDocumentModal
                    open={splitOpen}
                    onClose={() => setSplitOpen(false)}
                    onSplit={async (result) => {
                        if (result && typeof setEditedItem === 'function') {
                            setEditedItem((prev) => ({
                                ...prev,
                                amount: Number(result.remaining).toFixed(2),
                                ...(result.foreignCurrencyAmount != null
                                    ? { foreignCurrencyAmount: Number(result.foreignCurrencyAmount).toFixed(2) }
                                    : {}),
                            }));
                        }
                        await RefreshPage();
                    }}
                    document={editedItem}
                    docType={effectiveDocType}
                    formattedAccountCardOptions={formattedAccountCardOptions2}
                    sortCodeIdOptions={sortCodeIdOptions}
                    onRequestAddAccountCard={handleRequestAddAccountCardForSplit}
                    applyAccountCardRef={applySplitAccountCardRef}
                    client={client}
                    token={token}
                />
            )}

            {/* Duplicate Payment Acceptance Modal for Expenses/Incomes (including AccountCardDashboard rows) */}
            {dupPaymentOpen && canDuplicatePaymentAcceptance && editedItem && (
                <PaymentAcceptanceDuplicateModal
                    open={dupPaymentOpen}
                    onClose={closeDuplicatePayment}
                    sourceItem={transformToPaymentAcceptance(editedItem, duplicatePaymentSourceType, client)}
                    onSave={handleSavePaymentAcceptanceDuplicate}
                    t={t}
                    accountCardOptions={accountCardOptions}
                    client={client}
                    setLoading={setLoading}
                    token={token}
                    direction={direction}
                    sortDate={editedItem.documentDate || editedItem.date || new Date()}
                    user={null} // user object not available in EditModal props
                    isClosedReportingMonth={isClosedReportingMonth}
                    isRange={isRange}
                    fetchEnums={fetchEnums}
                />
            )}

            {/* Duplicate Journal Entries List Modal */}
            <DuplicateJournalEntriesModal
                open={dupJournalOpen}
                onClose={closeDuplicateJournal}
                direction={direction}
                filteredAndSortedData={filteredAndSortedData}
                handleReopenEdit={handleReopenEdit}
                setEditedItem={setEditedItem}
                currentEditedItem={editedItem}
                client={client}
                token={token}
            />

            {/* Related Payment Acceptances List Modal (payments created from this Income/Expense) */}
            <RelatedPaymentAcceptancesModal
                open={relatedPaymentsOpen}
                onClose={closeRelatedPayments}
                direction={direction}
                currentEditedItem={editedItem}
                client={client}
                token={token}
            />

            <ProjectModal
                open={projectModalOpen}
                mode="create"
                direction={direction}
                formData={{ code: '', name: '' }}
                existingProjects={segmentationProjects}
                onClose={handleProjectModalClose}
                onSave={handleProjectModalSave}
                loading={projectCreateLoading}
            />

            {/* File Upload Modal */}
            {uploadModalOpen && (
                <Portal>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1400,
                        }}
                        onClick={() => {
                            setUploadModalOpen(false);
                            setDocuments([]);
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                padding: '24px',
                                minWidth: '500px',
                                maxWidth: '600px',
                                maxHeight: '80vh',
                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
                                direction: direction,
                            }}
                        >
                            {/* Header */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '20px'
                            }}>
                                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                                    {t('AttachDocument')}
                                </h2>
                                <IconButton
                                    onClick={() => {
                                        setUploadModalOpen(false);
                                        setDocuments([]);
                                    }}
                                    size="small"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </div>

                            {/* File Upload Section */}
                            <div
                                className="newDocument-file-upload-container input-field_Container_required-asterisk edit-attach-dropzone"
                                {...getRootProps()}
                            >
                                <DropZoneContainer isDragActive={isDragActive} style={{ width: '100%', minHeight: '120px' }}>
                                    <input {...getInputProps()} />

                                    {/* Show message when dragging */}
                                    {isDragging && (
                                        <DragOverlayText style={{ display: 'block' }}>
                                            {t('DropFilesHere')}
                                        </DragOverlayText>
                                    )}

                                    {!isDragging && (
                                        <label className="newDocument-upload-label">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="upload-icon"
                                                viewBox="0 0 58 58"
                                                fill="none"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M29 38.0625C30.001 38.0625 30.8125 37.251 30.8125 36.25V9.73298L34.874 14.4712C35.5253 15.2313 36.6696 15.3193 37.4296 14.6678C38.1896 14.0164 38.2776 12.8721 37.6261 12.1121L30.3761 3.65378C30.0319 3.25204 29.529 3.02084 29 3.02084C28.471 3.02084 27.9681 3.25204 27.624 3.65378L20.3739 12.1121C19.7224 12.8721 19.8104 14.0164 20.5705 14.6678C21.3305 15.3193 22.4747 15.2313 23.1262 14.4712L27.1875 9.73298V36.25C27.1875 37.251 27.999 38.0625 29 38.0625Z"
                                                    fill="#F9AA2A"
                                                />
                                            </svg>
                                            <span className="upload-text">
                                                {documents?.length > 0
                                                    ? documents.map((doc) => doc.name).join(', ')
                                                    : t('DragDropFilesOrClick')}
                                            </span>
                                        </label>
                                    )}
                                </DropZoneContainer>
                            </div>

                            {/* Documents List */}
                            {documents.length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{
                                        fontSize: 'var(--xtram-text-sm, 0.875rem)',
                                        fontWeight: 'bold',
                                        marginBottom: '10px',
                                        color: '#333'
                                    }}>
                                        {t('SelectedFiles')} ({documents.length})
                                    </div>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {documents.map((doc, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '8px 12px',
                                                    marginBottom: '8px',
                                                    backgroundColor: '#F5F5F6',
                                                    borderRadius: '6px',
                                                }}
                                            >
                                                <span style={{ fontSize: 'var(--xtram-text-sm, 0.875rem)', color: '#333' }}>
                                                    {doc.name}
                                                </span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveDocument(index)}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div style={{
                                display: 'flex',
                                marginTop: '20px',
                                gap: '12px',
                                justifyContent: 'flex-end'
                            }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setUploadModalOpen(false);
                                        setDocuments([]);
                                    }}
                                    style={{
                                        borderColor: '#ccc',
                                        color: '#666',
                                    }}
                                >
                                    {t('cancel')}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleUploadDocuments}
                                    disabled={documents.length === 0}
                                    style={{
                                        backgroundColor: documents.length === 0 ? '#ccc' : '#304FFF',
                                        color: 'white',
                                    }}
                                >
                                    {t('Upload')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Portal>
            )}
        </Portal >

            <Dialog
                open={datePickerOpen}
                onClose={closeDatePicker}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        minWidth: 340,
                        direction: i18n.dir(),
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: '1.1rem' }}>
                    {t('AccountCardReport')} – {pendingCardName}
                </DialogTitle>

                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <CustomMonthDatePicker
                        date={pickerStartDate}
                        onDateChange={(d) => setPickerStartDate(d)}
                        label={t('StartDate')}
                        height="40px"
                        width="100%"
                    />
                    <CustomMonthDatePicker
                        date={pickerEndDate}
                        onDateChange={(d) => setPickerEndDate(d)}
                        label={t('EndDate')}
                        height="40px"
                        width="100%"
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDatePicker} sx={{ color: '#666' }}>
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!pickerStartDate || !pickerEndDate}
                        onClick={() => fetchReport(pickerStartDate, pickerEndDate)}
                        sx={{ bgcolor: '#304FFF', '&:hover': { bgcolor: '#1a3bcc' } }}
                    >
                        {t('ViewReport')}
                    </Button>
                </DialogActions>
            </Dialog>

            {reportOpen && (
                <>
                    {reportLoading && (
                        <Portal>
                            <Box sx={{
                                position: 'fixed', inset: 0, zIndex: 1400,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: 'rgba(0,0,0,0.3)',
                            }}>
                                <CircularProgress size={48} />
                            </Box>
                        </Portal>
                    )}
                    <AccountCardReportHTML {...reportProps} />
                </>
            )}
        </>
    );
}

export default EditModal;
